import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  Paper,
  Chip,
  Popover,
  Collapse,
  FormControlLabel,
  Checkbox,
  Switch,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { useNotification } from '../contexts/NotificationContext';
import { useRequestLeave } from '../contexts/UnsavedSessionContext';
import ChartWidget, { LINE_INTERPOLATION_OPTIONS } from './ChartWidget';
import TablePlusView, { resolveTablePlusInstantColumns } from './TablePlusView';
import ChartBuilderCollectionSidebar from './chartBuilder/ChartBuilderCollectionSidebar';
import ReferencePreviewPanel from './chartBuilder/ReferencePreviewPanel';
import LiveDatasetTable, {
  fieldAlias,
  getColumnMeta,
  headerLabel,
  technicalLabel,
  DEFAULT_OBJECT_KV_FIELDS,
  resolvePlainColumnAliases,
} from './chartBuilder/LiveDatasetTable';
import ImageDensitySettingsFields from './chartBuilder/ImageDensitySettingsFields';
import DateDisplayFormatControl from './chartBuilder/DateDisplayFormatControl';
import EpisodeAnalysisDialog, {
  DEFAULT_EPISODE_CONFIG,
  episodeOutputFields,
} from './chartBuilder/EpisodeAnalysisDialog';
import EpisodeResultsTable from './chartBuilder/EpisodeResultsTable';
import ChartFeedBar from './chartBuilder/ChartFeedBar';
import SpatialZoneAnalysisDialog, {
  DEFAULT_SPATIAL_ZONE_CONFIG,
} from './chartBuilder/SpatialZoneAnalysisDialog';
import SpatialZoneResultsTable from './chartBuilder/SpatialZoneResultsTable';
import { parseSeriesTimestamp } from '../utils/episodeDetection';
import {
  episodeOutputDescription,
  resolveEpisodeGroupCols,
  resolveEpisodeGroupColsFromOutputFields,
  applyEpisodePreserveGroupColumns,
  resolveEpisodeSeriesColumnsFromOutputFields,
  buildEpisodeColumnOptionsFromOutputFields,
  resolveEpisodeSeriesLabel,
  resolveEpisodeDurationPresentation,
  episodeConfigReady,
  resolveEpisodeSeriesColumns,
  episodeColumnAlias,
} from '../utils/episodeOutputMeta';
import {
  hasUnnestDatasetColumn,
  resolveSpatialGroupCols,
  spatialZoneOutputDescription,
  spatialZoneOutputFields,
} from '../utils/spatialZoneOutputMeta';
import {
  DEFAULT_POST_PROCESS_ORDER,
  getEpisodeInputSource,
  migratePostProcessOrder,
  normalizePostProcessOrder,
  orderedPipelineSteps,
  resolveChartFeedFromPipeline,
} from '../utils/chartFeedMeta';
import {
  buildChartFeedDatetimeFieldOptions,
  guessSeriesTimeRangeSourceField,
} from '../utils/seriesTimeRangeMeta';
import { resolveExampleWizard, applyWizardState } from './chartBuilder/resolveExampleWizard';
import { isLibraryReference, originalToWizardLoad } from '../utils/chartLibrary';
import {
  DEFAULT_PIE_LEGEND_FONT_SIZE,
  DEFAULT_PIE_LABEL_POSITION,
  PIE_LEGEND_FONT_SIZE_OPTIONS,
  PIE_LABEL_POSITION_OPTIONS,
  normalizePieLegendFontSize,
  normalizePieLabelPosition,
  pieLabelsInLegend as isPieLegendLabelMode,
} from '../utils/pieChartOptions';
import { specForPreviewApi } from '../utils/specForApiRequest';
import {
  applyCalculatedColumns,
  isCalculatedColumnKey,
  normalizeCalculatedColumns,
  sumCalculatedColumn,
} from '../utils/calculatedColumns';
import { rowLimitInputToSpecLimit, specLimitToRowLimitInput } from '../utils/chartRowLimit';
import {
  buildImageDensityConfigFromViz,
  coalesceManualPositions,
  imageDensityVizFieldsFromConfig,
  isNonEmptyManualPositions,
} from '../utils/imageDensityConfig';
import { temporalMapsFromColumns } from '../utils/tooltipDetail';
import { isQuerylessChartType, isSafeButtonUrl, QUERYLESS_CHART_TYPES } from '../utils/querylessChartTypes';
import WeatherLocationPicker from './WeatherLocationPicker';
import CalendarEventsEditor from './CalendarEventsEditor';
import {
  attachHeatmapPositionColumns,
  heatmapOverlayColumns,
  inferPositionKeyColumn,
  withHeatmapPositionFieldOptions,
} from '../utils/imageDensityPoints';
import GeoHeatmapSettingsFields from './chartBuilder/GeoHeatmapSettingsFields';
import {
  GEO_HEATMAP_AUTO_LAT,
  GEO_HEATMAP_AUTO_LNG,
  buildGeoHeatmapConfigFromViz,
  geoHeatmapVizFieldsFromConfig,
  geoHeatmapAxisForSpec,
} from '../utils/geoHeatmapConfig';
import { isDurationFieldBase, resolveFieldDurationFormat } from '../utils/durationFormat';
import { copyTextToClipboard } from '../utils/clipboard';
import { supportsFillTimeAxisGaps } from '../utils/timeAxisGaps';
import { isValidColorThresholds } from '../utils/colorThresholds';
import { PIVOTABLE_DATE_BUCKETS } from '../utils/pivotDateBucketTable';
import {
  buildNarrativePlaceholders,
  renderNarrativeSegments,
  slugifyPlaceholderKey,
  isInternalIdColumn,
  dedupePlaceholderKey,
  hasNarrativePlaceholder,
} from '../utils/narrativeTemplate';
import {
  DEFAULT_IMAGE_CAROUSEL,
  carouselColumnSelectValue,
  normalizeCarouselColumnFromStorage,
} from '../utils/imageCarouselConfig';

const isPointDatasetChartType = (t) => t === 'imageDensity' || t === 'geoHeatmap';
/** Tipos que consumen filas crudas de la tabla (sin agregación SQL por ejes). */
const isRawRowsChartType = (t) => isPointDatasetChartType(t) || t === 'intervals' || t === 'imageCarousel';
const TIME_RANGE_ALL_VALUE = '__all__';
const SERIES_TIME_RANGE_PREFIX = '__trset__:';
const IMAGE_DENSITY_AXIS_AUTO_X = '__id_auto_x__';
const IMAGE_DENSITY_AXIS_AUTO_Y = '__id_auto_y__';

const COMPARE_PERIOD_ALERT_LABEL = {
  week: 'la semana anterior',
  month: 'el mes anterior',
  year: 'el año anterior',
};

function withCalculatedFieldOptions(options, defs) {
  const next = [...(options || [])];
  const seen = new Set(next.map((o) => o.value));
  normalizeCalculatedColumns(defs).forEach((def) => {
    if (!def?.key || seen.has(def.key)) return;
    seen.add(def.key);
    const labelTaken = next.some((o) => o.label === def.label);
    next.push({
      value: def.key,
      label: labelTaken ? `${def.label} (calculada)` : def.label,
    });
  });
  return next;
}

function previewResultFieldNames(preview) {
  return (preview?.fieldKeys || preview?.columns || [])
    .map((c) => (typeof c === 'string' ? c : c.name || c.field))
    .filter(Boolean);
}

function effectiveImageDensityAxis(field, defaultName, autoSentinel) {
  if (!field || field === autoSentinel) return defaultName;
  return field;
}

function imageDensityAxisForSpec(field, autoSentinel) {
  if (!field || field === autoSentinel) return undefined;
  return field;
}

function normalizeImageDensityAxisSelectValue(stored, axis, optionValues) {
  const auto =
    axis === 'x'
      ? [IMAGE_DENSITY_AXIS_AUTO_X, GEO_HEATMAP_AUTO_LAT]
      : [IMAGE_DENSITY_AXIS_AUTO_Y, GEO_HEATMAP_AUTO_LNG];
  if (auto.includes(stored)) return stored;
  if (!stored) return auto[0];
  if (optionValues.has(stored)) return stored;
  if (stored === 'x' || stored === 'y' || stored === 'lat' || stored === 'lng') return auto[0];
  return stored;
}

function imageDensityAxisSelectLabel(selectValue, axis, labelByValue) {
  const auto = axis === 'x' ? IMAGE_DENSITY_AXIS_AUTO_X : IMAGE_DENSITY_AXIS_AUTO_Y;
  const defaultName = axis === 'x' ? 'x' : 'y';
  if (selectValue === auto || !selectValue) {
    return `Automático (columna «${defaultName}»)`;
  }
  return labelByValue.get(selectValue) || selectValue;
}

function pieShowModeFromLabels(showValue, showPercent) {
  const sv = showValue !== false;
  const sp = showPercent !== false;
  if (!sv && !sp) return 'none';
  if (sv && sp) return 'both';
  if (sv) return 'value';
  if (sp) return 'percent';
  return 'both';
}

function pieShowLabelsFromMode(mode) {
  switch (mode) {
    case 'value':
      return { showValue: true, showPercent: false };
    case 'percent':
      return { showValue: false, showPercent: true };
    case 'none':
      return { showValue: false, showPercent: false };
    default:
      return { showValue: true, showPercent: true };
  }
}

function seriesTimeRangeSetIdFromField(value) {
  if (!value?.startsWith(SERIES_TIME_RANGE_PREFIX)) return '';
  return value.slice(SERIES_TIME_RANGE_PREFIX.length);
}

function guessDatetimeFieldAlias(columns, context) {
  const aliases = resolvePlainColumnAliases(columns);
  for (let i = 0; i < columns.length; i += 1) {
    const c = columns[i];
    if (!c?.tableId || !c?.column) continue;
    const meta = getColumnMeta(context, c);
    if (meta?.logicalType === 'datetime' || meta?.logicalType === 'date') {
      return aliases[i];
    }
  }
  return '';
}

function guessTimeRangeSourceField(columns, filterList, setId, context) {
  const trFilter = (filterList || []).find(
    (f) => f.operator === 'inTimeRange' && f.timeRangeSetId === setId && f.column
  );
  if (trFilter) {
    const idx = columns.findIndex(
      (c) =>
        c.tableId === trFilter.tableId &&
        c.column === trFilter.column &&
        (c.jsonPath || '') === (trFilter.jsonPath || '') &&
        (c.joinAlias || '') === (trFilter.joinAlias || '')
    );
    if (idx >= 0) {
      return resolvePlainColumnAliases(columns)[idx];
    }
  }
  return guessDatetimeFieldAlias(columns, context);
}

function emptyCol(overrides = {}) {
  return {
    id: `col-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    tableId: '',
    column: '',
    jsonPath: '',
    role: 'row',
    aggregation: 'none',
    dateBucket: 'raw',
    ...overrides,
  };
}

function newFilterClientId() {
  return `f-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function ensureFilterClientId(f) {
  if (f?.clientId) return f;
  return { ...f, clientId: newFilterClientId() };
}

function normalizeFiltersClientIds(list) {
  return (list || []).map((f) => ensureFilterClientId(f));
}

function emptyFilter(overrides = {}) {
  return {
    tableId: '',
    column: '',
    jsonPath: '',
    layer: 'universe',
    dynamic: false,
    operator: 'eq',
    value: '',
    paramName: '',
    previewValue: '',
    clientId: newFilterClientId(),
    ...overrides,
  };
}

/** Nombres de parametro sugeridos para un filtro de rango de fecha dinamico (Desde/Hasta). */
function suggestDateRangeParamNames(column, via) {
  const base = suggestParamName(column, via);
  return { start: `${base}Desde`, end: `${base}Hasta` };
}

function suggestParamName(column, via) {
  const raw = via?.fromColumn ? `${via.fromColumn}_${column}` : String(column || 'Param');
  const base = raw.replace(/[^a-zA-Z0-9_]/g, '');
  return base ? base.slice(0, 40) : 'Param';
}

function columnFollowFields(col) {
  return {
    joinAlias: col.joinAlias || undefined,
    via: col.via || undefined,
    viaPath: col.viaPath?.length ? col.viaPath : undefined,
  };
}

// 'scalar' (columna plana, sin extraccion JSON) es equivalente a no tener jsonMode
// seteado: los filtros armados via "Filtrar esta columna" lo dejan en 'scalar' por
// defecto, pero las columnas planas en si mismas suelen quedar con jsonMode vacio,
// asi que sin esta normalizacion nunca emparejan.
function normalizeJsonModeForCompare(mode) {
  const m = mode || '';
  return m === 'scalar' ? '' : m;
}

/** Sitio (name + regexExtract) y Zona (name crudo) comparten tableId/column: sin esto se
 * confundirian como la misma columna al detectar/ordenar filtros. */
function transformKeyForCompare(transform) {
  if (!transform?.type) return '';
  return `${transform.type}|${transform.pattern || ''}|${transform.replacement || ''}`;
}

function sameFilterColumn(f, col) {
  return (
    f.tableId === col.tableId &&
    f.column === col.column &&
    (f.jsonPath || '') === (col.jsonPath || '') &&
    (f.joinAlias || '') === (col.joinAlias || '') &&
    normalizeJsonModeForCompare(f.jsonMode) === normalizeJsonModeForCompare(col.jsonMode) &&
    (f.objectKvPart || '') === (col.objectKvPart || '') &&
    transformKeyForCompare(f.transform) === transformKeyForCompare(col.transform)
  );
}

/**
 * Identidad de un filtro ya existente (misma columna Y mismo operador). Sin el
 * operador, dos filtros con distinto operador sobre la misma columna (ej. uno
 * "Desde" y otro "Antes de") se confunden como el mismo filtro y el segundo
 * termina reemplazando al primero en vez de agregarse.
 */
function sameFilterEntry(f, ref) {
  return sameFilterColumn(f, ref) && (f.operator || 'eq') === (ref.operator || 'eq');
}

function isValueColumn(col) {
  return (
    col?.role === 'value' ||
    col?.jsonMode === 'arrayCount' ||
    col?.jsonMode === 'arraySum' ||
    col?.jsonMode === 'arrayUnnest' ||
    col?.jsonMode === 'objectKv' ||
    col?.jsonMode === 'jsonDiff' ||
    col?.objectKvPart === 'value' ||
    (col?.aggregation && col.aggregation !== 'none')
  );
}

function resolveFilterLogicalTypeForFilter(context, ref) {
  if (ref?.jsonMode === 'arrayCount' || ref?.jsonMode === 'arraySum') return 'number';
  if (ref?.jsonMode === 'objectKv') return ref?.objectKvPart === 'key' ? 'string' : 'number';
  if (ref?.objectKvPart === 'value') return 'number';
  if (ref?.objectKvPart === 'key') return 'string';
  if (ref?.filterScope === 'result') return 'number';
  const meta = getColumnMeta(context, ref);
  if (ref?.jsonPath && meta?.jsonPaths) {
    const entry = meta.jsonPaths.find((p) => p.path === ref.jsonPath);
    if (entry?.selectable && entry.types?.includes('number') && !entry.types?.includes('array')) {
      return 'number';
    }
  }
  return meta?.logicalType || 'string';
}

function operatorsForFilter(context, ref) {
  const lt = resolveFilterLogicalTypeForFilter(context, ref);
  const map = context?.filterOperators || {};
  return map[lt] || map.string || [{ value: 'eq', label: 'Igual a' }];
}

function operatorNeedsValue(op) {
  return (
    op !== 'isNull' && op !== 'isNotNull' && op !== 'inLast' && op !== 'inTimeRange' && op !== 'between'
  );
}

/**
 * Si este filtro realmente va a filtrar algo (mismo criterio que usa buildSpec para decidir
 * si lo incluye): columna elegida y, según el operador, el valor que ese operador necesita.
 * Se usa para detectar un filtro configurado que en los hechos no hace nada, como en un
 * narrativo de texto fijo que no lee ningún dato de la consulta.
 */
function isFilterActive(f) {
  if (!f?.column) return false;
  const layer = f.layer || (f.dynamic ? 'selection' : 'universe');
  if (f.operator === 'inTimeRange') {
    if (layer === 'selection' || f.dynamic) return Boolean(f.paramName && f.timeRangeSetId);
    return Boolean(f.timeRangeSetId && f.timeRangeId);
  }
  if (f.operator === 'inLast') {
    if (layer === 'selection' || f.dynamic) return Boolean(f.paramName);
    if ((f.relativeMode || 'rolling') === 'calendar') return Boolean(f.relativePeriod);
    const n = parseInt(f.relativeAmount, 10);
    return Number.isFinite(n) && n > 0 && Boolean(f.relativeUnit);
  }
  if (f.operator === 'between') {
    if (layer === 'selection' || f.dynamic) return Boolean(f.paramNameStart && f.paramNameEnd);
    return Boolean(String(f.rangeStart ?? '').trim()) && Boolean(String(f.rangeEnd ?? '').trim());
  }
  if (layer === 'selection') return Boolean(f.paramName);
  if (!operatorNeedsValue(f.operator)) return true;
  return String(f.value ?? '').trim() !== '';
}

function filterSupportsOrConditions(filter) {
  if (!filter) return false;
  const layer = filter.layer || (filter.dynamic ? 'selection' : 'universe');
  if (layer === 'selection' || filter.dynamic) return false;
  const op = filter.operator || 'eq';
  return op !== 'inLast' && op !== 'inTimeRange' && op !== 'between';
}

const FILTER_OP_SHORT_LABELS = {
  eq: '=',
  ne: '≠',
  contains: 'contiene',
  startsWith: 'empieza',
  gt: '>',
  gte: '≥',
  lt: '<',
  lte: '≤',
  isNull: 'vacío',
  isNotNull: 'no vacío',
};

function formatFilterConditionLabel(operator, value) {
  const op = operator || 'eq';
  if (op === 'isNull') return 'vacío';
  if (op === 'isNotNull') return 'no vacío';
  const sym = FILTER_OP_SHORT_LABELS[op] || op;
  const v = value != null && String(value).trim() !== '' ? String(value) : '?';
  return `${sym} ${v}`;
}

const RELATIVE_TIME_UNIT_OPTIONS = [
  { value: 'minutes', label: 'minutos' },
  { value: 'hours', label: 'horas' },
  { value: 'days', label: 'días' },
];

const CALENDAR_PERIOD_OPTIONS = [
  { value: 'day', label: 'Ayer' },
  { value: 'week', label: 'Semana pasada' },
  { value: 'month', label: 'Mes pasado' },
  { value: 'quarter', label: 'Trimestre pasado' },
  { value: 'year', label: 'Año pasado' },
];

const ROLLING_TIME_PRESETS = [
  { mode: 'rolling', amount: 15, unit: 'minutes', label: '15 min' },
  { mode: 'rolling', amount: 1, unit: 'hours', label: '1 h' },
  { mode: 'rolling', amount: 24, unit: 'hours', label: '24 h' },
  { mode: 'rolling', amount: 7, unit: 'days', label: 'Últimos 7 días' },
  { mode: 'rolling', amount: 30, unit: 'days', label: 'Últimos 30 días' },
];

const CALENDAR_TIME_PRESETS = [
  { mode: 'calendar', period: 'day', label: 'Ayer' },
  { mode: 'calendar', period: 'week', label: 'Semana pasada' },
  { mode: 'calendar', period: 'month', label: 'Mes pasado' },
  { mode: 'calendar', period: 'quarter', label: 'Trimestre pasado' },
  { mode: 'calendar', period: 'year', label: 'Año pasado' },
];

function relativeTimeLabel(filter) {
  const mode = filter.relativeMode || 'rolling';
  if (mode === 'calendar') {
    const opt = CALENDAR_PERIOD_OPTIONS.find((o) => o.value === filter.relativePeriod);
    return opt?.label?.toLowerCase() || 'periodo calendario';
  }
  const opt = RELATIVE_TIME_UNIT_OPTIONS.find((o) => o.value === filter.relativeUnit);
  const n = Math.max(1, parseInt(filter.relativeAmount, 10) || 1);
  return `últimos ${n} ${opt?.label || filter.relativeUnit || 'días'}`;
}

function isSameRollingPreset(filter, preset) {
  return (
    (filter.relativeMode || 'rolling') === 'rolling' &&
    Number(filter.relativeAmount) === preset.amount &&
    (filter.relativeUnit || 'days') === preset.unit
  );
}

function isSameCalendarPreset(filter, preset) {
  return (
    (filter.relativeMode || 'rolling') === 'calendar' &&
    (filter.relativePeriod || 'week') === preset.period
  );
}

function isValidPopoverAnchor(el) {
  if (!el || typeof el !== 'object') return false;
  if (typeof el.isConnected === 'boolean') return el.isConnected;
  return typeof document !== 'undefined' && document.body.contains(el);
}

function isModelTimeRangeFilter(f) {
  return f?.operator === 'inTimeRange';
}

function isDataDynamicFilter(f) {
  const layer = f?.layer || (f?.dynamic ? 'selection' : 'universe');
  return (layer === 'selection' || f?.dynamic) && !isModelTimeRangeFilter(f);
}

function filterChipProps(f) {
  if (isModelTimeRangeFilter(f)) {
    if (f.layer === 'selection' || f.dynamic) {
      return { color: 'info', variant: 'filled' };
    }
    return { color: 'info', variant: 'outlined' };
  }
  if (isDataDynamicFilter(f)) {
    return { color: 'secondary', variant: 'filled' };
  }
  return { color: 'default', variant: 'filled' };
}

function suggestTimeRangeParamName(setName, column, via) {
  const fromSet = String(setName || '')
    .replace(/[^a-zA-Z0-9_]/g, '')
    .slice(0, 30);
  if (fromSet) return fromSet;
  return suggestParamName(column, via);
}

function suggestRelativeTimeParamName(column, via) {
  const raw = via?.fromColumn ? `${via.fromColumn}_ventana` : `${column || 'time'}_ventana`;
  const base = raw.replace(/[^a-zA-Z0-9_]/g, '');
  return base ? base.slice(0, 40) : 'Ventana';
}

const RELATIVE_TIME_DASHBOARD_PRESETS = [
  { id: 'rolling:15:minutes', label: 'Últimos 15 min' },
  { id: 'rolling:1:hours', label: 'Última hora' },
  { id: 'rolling:24:hours', label: 'Últimas 24 h' },
  { id: 'rolling:7:days', label: 'Últimos 7 días' },
  { id: 'rolling:30:days', label: 'Últimos 30 días' },
  { id: 'calendar:day', label: 'Ayer' },
  { id: 'calendar:week', label: 'Semana pasada' },
  { id: 'calendar:month', label: 'Mes pasado' },
  { id: 'calendar:quarter', label: 'Trimestre pasado' },
  { id: 'calendar:year', label: 'Año pasado' },
];

function encodeRelativeTimePresetId(filter) {
  const mode = filter?.relativeMode || 'rolling';
  if (mode === 'calendar') {
    return `calendar:${filter?.relativePeriod || 'week'}`;
  }
  const amount = Math.max(1, parseInt(filter?.relativeAmount, 10) || 1);
  const unit = filter?.relativeUnit || 'days';
  return `rolling:${amount}:${unit}`;
}

/** Preset por defecto del filtro inLast dinámico. previewValue vacío equivale a no configurado. */
function resolveRelativeTimePreviewId(filter) {
  const raw = filter?.previewValue;
  if (raw != null && String(raw).trim() !== '') return String(raw);
  return encodeRelativeTimePresetId(filter);
}

function relativeTimeDashboardOptions(filter) {
  const currentId = encodeRelativeTimePresetId(filter);
  const byId = new Map(RELATIVE_TIME_DASHBOARD_PRESETS.map((p) => [p.id, p]));
  if (!byId.has(currentId)) {
    byId.set(currentId, { id: currentId, label: relativeTimeLabel(filter) });
  }
  return [...byId.values()];
}

function objectKvFieldAlias(field, index) {
  if (field?.alias && String(field.alias).trim()) {
    return String(field.alias).trim().replace(/[^a-zA-Z0-9_]/g, '_').slice(0, 60);
  }
  const base = String(field?.path || `field_${index}`)
    .replace(/[^a-zA-Z0-9_]/g, '_')
    .slice(0, 60);
  return base || `kv_${index}`;
}

/** Opciones de eje/medida/series incluyendo kv_key / kv_value de columnas objectKv. */
function collectResultFieldEntries(columns) {
  const raw = [];
  const plainAliases = resolvePlainColumnAliases(columns);
  (columns || []).forEach((c, i) => {
    if (!c?.tableId || !c?.column) return;
    if (c.jsonMode === 'objectKv') {
      const fields = c.objectKvFields?.length ? c.objectKvFields : DEFAULT_OBJECT_KV_FIELDS;
      fields.forEach((f, fi) => {
        raw.push({
          value: objectKvFieldAlias(f, fi),
          column: c,
          objectKvField: f,
          virtualKind:
            f.path === '$value' || f.path === 'value' ? 'value' : 'row',
        });
      });
      return;
    }
    raw.push({
      value: plainAliases[i],
      column: c,
    });
  });
  return raw;
}

function columnToFieldRef(c) {
  if (!c?.tableId || !c?.column) return null;
  const unnestFields =
    c.jsonMode === 'arrayUnnest'
      ? c.unnestFields?.length
        ? c.unnestFields
        : [
            { path: 'frame.x', alias: 'x' },
            { path: 'frame.y', alias: 'y' },
          ]
      : undefined;
  const objectKvFields =
    c.jsonMode === 'objectKv'
      ? c.objectKvFields?.length
        ? c.objectKvFields
        : DEFAULT_OBJECT_KV_FIELDS
      : undefined;
  const jsonExtra = {
    jsonPath: c.jsonPath || undefined,
    jsonMode: c.jsonMode && c.jsonMode !== 'scalar' ? c.jsonMode : undefined,
    arrayFilters: c.arrayFilters?.length ? c.arrayFilters : undefined,
    arrayValueField: c.arrayValueField || undefined,
    unnestFields,
    objectValueAgg: c.objectValueAgg || undefined,
    objectKvFields,
    objectKvPart: c.objectKvPart || undefined,
    joinAlias: c.joinAlias || undefined,
    via: c.via || undefined,
    viaPath: c.viaPath?.length ? c.viaPath : undefined,
    adhocJoin: c.adhocJoin || undefined,
    alias: c.alias || undefined,
    decimalPlaces:
      c.decimalPlaces != null && c.decimalPlaces !== '' ? Number(c.decimalPlaces) : undefined,
    colorThresholds: isValidColorThresholds(c.colorThresholds) ? c.colorThresholds : undefined,
    diffSubtractPath: c.jsonMode === 'jsonDiff' ? c.diffSubtractPath || undefined : undefined,
    transform: c.transform || undefined,
    coalesceParent: c.coalesceParent || undefined,
  };
  const isValue =
    c.role === 'value' ||
    c.jsonMode === 'arrayCount' ||
    c.jsonMode === 'arraySum' ||
    c.jsonMode === 'arrayUnnest' ||
    c.jsonMode === 'objectKv';
  if (isValue) {
    return {
      tableId: c.tableId,
      column: c.column,
      ...jsonExtra,
      role: 'value',
      aggregation:
        c.jsonMode === 'arrayUnnest' || c.jsonMode === 'objectKv'
          ? 'none'
          : c.aggregation && c.aggregation !== 'none'
            ? c.aggregation
            : 'sum',
      displayAsImage: c.displayAsImage === true ? true : undefined,
    };
  }
  return {
    tableId: c.tableId,
    column: c.column,
    jsonPath: c.jsonPath || undefined,
    dateBucket: c.dateBucket || 'raw',
    dateDisplayFormat:
      c.dateDisplayFormat && c.dateDisplayFormat !== 'auto' ? c.dateDisplayFormat : undefined,
    aggregation: 'none',
    // Sin este chequeo, una columna "Solo filtro" se guardaba siempre como "Fila",
    // perdiendo el rol apenas se guardaba el grafico (no solo al reabrirlo).
    role: c.role === 'filter' ? 'filter' : 'row',
    joinAlias: c.joinAlias || undefined,
    via: c.via || undefined,
    viaPath: c.viaPath?.length ? c.viaPath : undefined,
    adhocJoin: c.adhocJoin || undefined,
    alias: c.alias || undefined,
    jsonMode: c.jsonMode === 'arrayUnnestGroup' ? c.jsonMode : undefined,
    arrayElemField: c.jsonMode === 'arrayUnnestGroup' ? c.arrayElemField || undefined : undefined,
    lookup: c.jsonMode === 'arrayUnnestGroup' ? c.lookup || undefined : undefined,
    transform: c.transform || undefined,
    coalesceParent: c.coalesceParent || undefined,
    // Pivotea esta columna (debe tener un dateBucket con columnas fijas
    // conocidas: hora del día, día de la semana, día del mes o mes) a una
    // columna por valor del bucket en la tabla, en vez de una fila por valor.
    // Solo tiene sentido en "Fila".
    pivotHour: PIVOTABLE_DATE_BUCKETS.includes(c.dateBucket) && c.pivotHour === true ? true : undefined,
    displayAsImage: c.displayAsImage === true ? true : undefined,
  };
}

function columnsToRowsValues(columns) {
  const rows = [];
  const values = [];
  const tableColumns = [];
  for (const c of columns || []) {
    const field = columnToFieldRef(c);
    if (!field) continue;
    tableColumns.push(field);
    if (field.role === 'value' || field.jsonMode === 'arrayCount' || field.jsonMode === 'arraySum' || field.jsonMode === 'arrayUnnest' || field.jsonMode === 'objectKv') {
      values.push(field);
    } else if (field.role !== 'filter') {
      // Solo filtro: va en tableColumns (para el WHERE) pero no debe colarse aqui, o
      // el servidor la vuelve a usar como Fila cuando no hay ninguna otra (bar/kpi
      // sin columna Fila real caen de vuelta a esta lista, no a tableColumns).
      rows.push(field);
    }
  }
  return { rows, values, tableColumns };
}

function rowsValuesToColumns(rows, values) {
  const cols = [];
  for (const r of rows || []) {
    if (!r?.tableId && !r?.column) continue;
    cols.push(
      emptyCol({
        ...r,
        role: 'row',
        aggregation: 'none',
        dateBucket: r.dateBucket || 'raw',
      })
    );
  }
  for (const v of values || []) {
    if (!v?.tableId && !v?.column) continue;
    cols.push(
      emptyCol({
        ...v,
        role: 'value',
        aggregation: v.aggregation || 'sum',
      })
    );
  }
  return cols;
}

function FilterEditorPopover({
  open,
  anchorEl,
  onClose,
  filter,
  onChange,
  onRemove,
  context,
  connectionId,
  database,
  getAuthHeaders,
}) {
  const meta = getColumnMeta(context, filter || {});
  const logicalType = resolveFilterLogicalTypeForFilter(context, filter || {});
  const timeRangeSets = context?.timeRangeSets || [];
  const operators = operatorsForFilter(context, filter || {}).filter(
    (o) => o.value !== 'inTimeRange' || logicalType === 'datetime'
  );
  const op = filter?.operator || 'eq';
  const needsValue = operatorNeedsValue(op);
  const [samples, setSamples] = useState([]);
  const [sampleLoading, setSampleLoading] = useState(false);
  const columnSampleMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/column-sample', body, { headers }),
  });

  const layer = filter?.layer || (filter?.dynamic ? 'selection' : 'universe');
  const isSelection = layer === 'selection';
  const isRelativeTime = op === 'inLast';
  const isCustomTimeRange = op === 'inTimeRange';
  const isFixedDateRange = op === 'between';
  const isTimeFilter = isRelativeTime || isCustomTimeRange;
  const relativeMode = filter?.relativeMode || 'rolling';
  const selectedTimeSet =
    timeRangeSets.find((s) => s.id === filter?.timeRangeSetId) || timeRangeSets[0] || null;
  const selectedTimeRanges = selectedTimeSet?.ranges || [];
  const currentValue = isSelection ? filter?.previewValue ?? filter?.value ?? '' : filter?.value ?? '';

  useEffect(() => {
    if (!open || !filter?.tableId || !filter?.column || !connectionId || !database) {
      setSamples([]);
      return undefined;
    }
    const headers = getAuthHeaders?.();
    if (!headers) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setSampleLoading(true);
      try {
        const res = await columnSampleMutation.mutateAsync({
          body: {
            connectionId,
            database,
            tableId: filter.tableId,
            column: filter.column,
            jsonPath: filter.jsonPath || undefined,
            joinAlias: filter.joinAlias || undefined,
            via: filter.via || undefined,
            viaPath: filter.viaPath?.length ? filter.viaPath : undefined,
            jsonMode: filter.jsonMode || undefined,
            objectKvPart: filter.objectKvPart || undefined,
            limit: 12,
          },
          headers,
        });
        if (!cancelled && res.data.success) setSamples(res.data.values || []);
      } catch {
        if (!cancelled) setSamples([]);
      } finally {
        if (!cancelled) setSampleLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    open,
    filter?.tableId,
    filter?.column,
    filter?.jsonPath,
    filter?.joinAlias,
    filter?.via,
    filter?.viaPath,
    filter?.jsonMode,
    filter?.objectKvPart,
    connectionId,
    database,
    getAuthHeaders,
  ]);

  if (!filter) return null;

  const validAnchorEl = isValidPopoverAnchor(anchorEl) ? anchorEl : null;
  const popoverOpen = open && Boolean(validAnchorEl);
  const orSupported = filterSupportsOrConditions(filter);
  const orConditions = filter.orConditions || [];
  const comparableOperators = operators.filter(
    (o) => o.value !== 'inLast' && o.value !== 'inTimeRange'
  );
  const valueInputType =
    logicalType === 'number'
      ? 'number'
      : logicalType === 'date'
        ? 'date'
        : logicalType === 'datetime'
          ? 'datetime-local'
          : 'text';
  const nativeDateInput = valueInputType === 'date' || valueInputType === 'datetime-local';
  const nativeDateInputLabelProps = nativeDateInput ? { shrink: true } : undefined;

  const patchOrConditions = (next) => {
    onChange({
      ...filter,
      orConditions: next.length ? next : undefined,
    });
  };

  const applyLayer = (nextLayer) => {
    if (nextLayer === 'selection') {
      if (isCustomTimeRange) {
        onChange({
          ...filter,
          layer: 'selection',
          dynamic: true,
          orConditions: undefined,
          paramName:
            filter.paramName ||
            suggestTimeRangeParamName(selectedTimeSet?.name, filter.column, filter.via),
          previewValue: filter.previewValue ?? TIME_RANGE_ALL_VALUE,
        });
      } else if (isRelativeTime) {
        onChange({
          ...filter,
          layer: 'selection',
          dynamic: true,
          orConditions: undefined,
          paramName: filter.paramName || suggestRelativeTimeParamName(filter.column, filter.via),
          previewValue: resolveRelativeTimePreviewId(filter),
        });
      } else if (isFixedDateRange) {
        const names = suggestDateRangeParamNames(filter.column, filter.via);
        onChange({
          ...filter,
          layer: 'selection',
          dynamic: true,
          orConditions: undefined,
          paramNameStart: filter.paramNameStart || names.start,
          paramNameEnd: filter.paramNameEnd || names.end,
          previewValueStart: filter.previewValueStart ?? filter.rangeStart,
          previewValueEnd: filter.previewValueEnd ?? filter.rangeEnd,
        });
      } else {
        onChange({
          ...filter,
          layer: 'selection',
          dynamic: true,
          orConditions: undefined,
          paramName: filter.paramName || suggestParamName(filter.column, filter.via),
        });
      }
    } else {
      onChange({
        ...filter,
        layer: 'universe',
        dynamic: false,
        paramName: '',
        paramNameStart: '',
        paramNameEnd: '',
        orConditions: undefined,
      });
    }
  };

  return (
    <Popover
      open={popoverOpen}
      anchorEl={validAnchorEl}
      onClose={onClose}
      disablePortal
      disableScrollLock
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Box sx={{ p: 2, width: 400, maxWidth: 'calc(100vw - 24px)', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography variant="subtitle2">
          Filtro: {technicalLabel(filter, context)}
          {filter.filterScope === 'result' ? ' · resultado' : ''}
        </Typography>
        {filter.filterScope === 'result' && (
          <Typography variant="caption" color="text.secondary">
            Compara el valor calculado de la columna (conteo JSON, SUM, etc.). Si hay agrupación, se aplica
            después del GROUP BY.
          </Typography>
        )}
        <FormControl fullWidth size="small">
          <InputLabel>Capa</InputLabel>
          <Select
            label="Capa"
            value={layer}
            onChange={(e) => applyLayer(e.target.value)}
          >
            <MenuItem value="universe">Universo (fijo)</MenuItem>
            <MenuItem value="selection">Selección (dinámico)</MenuItem>
          </Select>
        </FormControl>
        {isTimeFilter && (
          <Typography variant="caption" color="text.secondary">
            {isCustomTimeRange
              ? isSelection
                ? 'El usuario elige un rango del modelo en el gráfico del dashboard.'
                : 'Fijo: aplica un rango del modelo a la hora de la columna.'
              : isSelection
                ? 'El usuario elige la ventana (7 días, 30 días, etc.) en el gráfico del dashboard.'
                : 'Fijo: ventana respecto al momento de ejecutar (zona horaria del dashboard).'}
          </Typography>
        )}
        {isFixedDateRange && (
          <Typography variant="caption" color="text.secondary">
            {isSelection
              ? 'El usuario elige el rango Desde/Hasta en el gráfico del dashboard.'
              : 'Fijo: aplica un rango de fechas exacto (Desde/Hasta). No es ajustable desde el dashboard.'}
          </Typography>
        )}
        <FormControl fullWidth size="small">
          <InputLabel>Comparación</InputLabel>
          <Select
            label="Comparación"
            value={op}
            onChange={(e) => {
              const nextOp = e.target.value;
              if (nextOp === 'inTimeRange') {
                const firstSet = timeRangeSets[0];
                const firstRange = firstSet?.ranges?.[0];
                onChange({
                  ...filter,
                  operator: nextOp,
                  orConditions: undefined,
                  layer: filter.layer || 'universe',
                  dynamic: filter.layer === 'selection',
                  paramName: filter.layer === 'selection' ? filter.paramName || suggestTimeRangeParamName(firstSet?.name, filter.column, filter.via) : '',
                  timeRangeSetId: firstSet?.id || '',
                  timeRangeId: firstRange?.id || '',
                  previewValue: TIME_RANGE_ALL_VALUE,
                });
              } else if (nextOp === 'inLast') {
                onChange({
                  ...filter,
                  operator: nextOp,
                  layer: 'universe',
                  dynamic: false,
                  paramName: '',
                  orConditions: undefined,
                  // El previewValue del operador anterior no es un preset de ventana válido.
                  previewValue: undefined,
                  relativeMode: filter.relativeMode || 'rolling',
                  relativeAmount: filter.relativeAmount || 7,
                  relativeUnit: filter.relativeUnit || 'days',
                  relativePeriod: filter.relativePeriod || 'week',
                });
              } else if (nextOp === 'between') {
                // Rango de fecha fijo, solo capa fija (v1, sin modo dinamico).
                onChange({
                  ...filter,
                  operator: nextOp,
                  layer: 'universe',
                  dynamic: false,
                  paramName: '',
                  orConditions: undefined,
                });
              } else {
                onChange({ ...filter, operator: nextOp });
              }
            }}
          >
            {operators.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {isRelativeTime && (
          <>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de rango</InputLabel>
              <Select
                label="Tipo de rango"
                value={relativeMode}
                onChange={(e) => {
                  const nextMode = e.target.value;
                  if (nextMode === 'calendar') {
                    onChange({
                      ...filter,
                      relativeMode: 'calendar',
                      relativePeriod: filter.relativePeriod || 'week',
                    });
                  } else {
                    onChange({
                      ...filter,
                      relativeMode: 'rolling',
                      relativeAmount: filter.relativeAmount || 7,
                      relativeUnit: filter.relativeUnit || 'days',
                    });
                  }
                }}
              >
                <MenuItem value="rolling">Últimos X (ventana móvil)</MenuItem>
                <MenuItem value="calendar">Periodo calendario</MenuItem>
              </Select>
            </FormControl>

            {relativeMode === 'rolling' ? (
              <>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    type="number"
                    label="Cantidad"
                    value={filter.relativeAmount ?? 7}
                    onChange={(e) =>
                      onChange({
                        ...filter,
                        relativeMode: 'rolling',
                        relativeAmount: Math.max(1, parseInt(e.target.value, 10) || 1),
                      })
                    }
                    inputProps={{ min: 1, max: 9999 }}
                    sx={{ width: 110 }}
                  />
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Unidad</InputLabel>
                    <Select
                      label="Unidad"
                      value={filter.relativeUnit || 'days'}
                      onChange={(e) =>
                        onChange({ ...filter, relativeMode: 'rolling', relativeUnit: e.target.value })
                      }
                    >
                      {RELATIVE_TIME_UNIT_OPTIONS.map((u) => (
                        <MenuItem key={u.value} value={u.value}>
                          {u.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Ej.: «últimos 7 días» = 168 h hacia atrás desde ahora (no es lunes–domingo).
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {ROLLING_TIME_PRESETS.map((p) => (
                    <Chip
                      key={`r-${p.amount}-${p.unit}`}
                      size="small"
                      label={p.label}
                      variant={isSameRollingPreset(filter, p) ? 'filled' : 'outlined'}
                      onClick={() =>
                        onChange({
                          ...filter,
                          relativeMode: 'rolling',
                          relativeAmount: p.amount,
                          relativeUnit: p.unit,
                        })
                      }
                    />
                  ))}
                </Box>
              </>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Periodo</InputLabel>
                  <Select
                    label="Periodo"
                    value={filter.relativePeriod || 'week'}
                    onChange={(e) =>
                      onChange({ ...filter, relativeMode: 'calendar', relativePeriod: e.target.value })
                    }
                  >
                    {CALENDAR_PERIOD_OPTIONS.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Ej.: «semana pasada» = lunes–domingo completos anteriores (no equivale a 7 días móviles).
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {CALENDAR_TIME_PRESETS.map((p) => (
                    <Chip
                      key={`c-${p.period}`}
                      size="small"
                      label={p.label}
                      variant={isSameCalendarPreset(filter, p) ? 'filled' : 'outlined'}
                      onClick={() =>
                        onChange({
                          ...filter,
                          relativeMode: 'calendar',
                          relativePeriod: p.period,
                        })
                      }
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        )}
        {isCustomTimeRange && (
          <>
            {timeRangeSets.length === 0 ? (
              <Alert severity="warning" sx={{ py: 0.25 }}>
                No hay conjuntos configurados. Define rangos en Configuración → Rangos horarios.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Conjunto</InputLabel>
                  <Select
                    label="Conjunto"
                    value={filter.timeRangeSetId || selectedTimeSet?.id || ''}
                    onChange={(e) => {
                      const set = timeRangeSets.find((s) => s.id === e.target.value);
                      const firstRangeId = set?.ranges?.[0]?.id || '';
                      onChange({
                        ...filter,
                        timeRangeSetId: e.target.value,
                        timeRangeId: firstRangeId,
                        previewValue: isSelection ? firstRangeId : filter.previewValue,
                      });
                    }}
                  >
                    {timeRangeSets.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel>Rango</InputLabel>
                  <Select
                    label="Rango"
                    value={filter.timeRangeId || selectedTimeRanges[0]?.id || ''}
                    onChange={(e) =>
                      onChange({
                        ...filter,
                        timeRangeId: e.target.value,
                        ...(isSelection ? { previewValue: e.target.value } : {}),
                      })
                    }
              >
                <MenuItem value={TIME_RANGE_ALL_VALUE}>Todos</MenuItem>
                {selectedTimeRanges.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.label} ({r.start}–{r.end}
                    {r.overnight ? ', cruza medianoche' : ''})
                  </MenuItem>
                ))}
              </Select>
                </FormControl>
                {selectedTimeSet?.description && (
                  <Typography variant="caption" color="text.secondary">
                    {selectedTimeSet.description}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
        {isCustomTimeRange && isSelection && (
          <>
            <TextField
              size="small"
              fullWidth
              label="Parámetro @"
              value={filter.paramName || ''}
              onChange={(e) =>
                onChange({
                  ...filter,
                  paramName: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                  dynamic: true,
                  layer: 'selection',
                })
              }
            />
            <FormControl fullWidth size="small">
              <InputLabel>Valor de prueba</InputLabel>
              <Select
                label="Valor de prueba"
                value={filter.previewValue ?? filter.timeRangeId ?? TIME_RANGE_ALL_VALUE}
                onChange={(e) =>
                  onChange({
                    ...filter,
                    previewValue: e.target.value,
                    timeRangeId: e.target.value,
                    dynamic: true,
                    layer: 'selection',
                  })
                }
              >
                {selectedTimeRanges.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {isRelativeTime && isSelection && (
          <>
            <TextField
              size="small"
              fullWidth
              label="Parámetro @"
              value={filter.paramName || ''}
              onChange={(e) =>
                onChange({
                  ...filter,
                  paramName: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                  dynamic: true,
                  layer: 'selection',
                })
              }
            />
            <FormControl fullWidth size="small">
              <InputLabel>Valor por defecto (preview)</InputLabel>
              <Select
                label="Valor por defecto (preview)"
                value={resolveRelativeTimePreviewId(filter)}
                onChange={(e) =>
                  onChange({
                    ...filter,
                    previewValue: e.target.value,
                    dynamic: true,
                    layer: 'selection',
                  })
                }
              >
                {relativeTimeDashboardOptions(filter).map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {isSelection && !isRelativeTime && !isCustomTimeRange && !isFixedDateRange && (
          <>
            <TextField
              size="small"
              fullWidth
              label="Parámetro @"
              value={filter.paramName || ''}
              onChange={(e) =>
                onChange({
                  ...filter,
                  paramName: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                  dynamic: true,
                })
              }
            />
            {op === 'eq' && logicalType === 'datetime' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={filter.matchWholeDay === true}
                      onChange={(e) =>
                        onChange({
                          ...filter,
                          matchWholeDay: e.target.checked,
                          dynamic: true,
                          layer: 'selection',
                        })
                      }
                    />
                  }
                  label={<Typography variant="body2">Día completo</Typography>}
                  sx={{ mx: 0 }}
                />
                <Tooltip title="Usa el rango del día calendario completo en vez del instante exacto. Aplica cuando la columna incluye hora.">
                  <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }}>
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filter.allowExecuteWithoutSelection === true}
                    onChange={(e) =>
                      onChange({
                        ...filter,
                        allowExecuteWithoutSelection: e.target.checked,
                        dynamic: true,
                        layer: 'selection',
                      })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    Permitir ejecutar sin selección
                  </Typography>
                }
                sx={{ mx: 0 }}
              />
              <Tooltip title="Sin un valor, equivale a «Cualquiera» y no filtra. Desactivado, hace falta elegir un valor antes de ejecutar.">
                <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={filter.excludeFromWhere === true}
                    onChange={(e) =>
                      onChange({
                        ...filter,
                        excludeFromWhere: e.target.checked,
                        dynamic: true,
                        layer: 'selection',
                      })
                    }
                  />
                }
                label={
                  <Typography variant="body2">
                    No aplicar como filtro
                  </Typography>
                }
                sx={{ mx: 0 }}
              />
              <Tooltip title="El parámetro se sigue enviando, pero no se agrega como condición de filtro; queda disponible para otro mecanismo del gráfico.">
                <IconButton size="small" sx={{ p: 0.25, ml: 0.5 }}>
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
        {isFixedDateRange && !isSelection && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              size="small"
              type={valueInputType}
              label="Desde"
              InputLabelProps={nativeDateInputLabelProps}
              inputProps={nativeDateInput ? { placeholder: '' } : undefined}
              value={filter.rangeStart || ''}
              onChange={(e) => onChange({ ...filter, rangeStart: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type={valueInputType}
              label="Hasta"
              InputLabelProps={nativeDateInputLabelProps}
              inputProps={nativeDateInput ? { placeholder: '' } : undefined}
              value={filter.rangeEnd || ''}
              onChange={(e) => onChange({ ...filter, rangeEnd: e.target.value })}
              sx={{ flex: 1 }}
            />
          </Box>
        )}
        {isFixedDateRange && isSelection && (
          <>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                label="Parámetro @ (desde)"
                value={filter.paramNameStart || ''}
                onChange={(e) =>
                  onChange({
                    ...filter,
                    paramNameStart: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                    dynamic: true,
                  })
                }
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                label="Parámetro @ (hasta)"
                value={filter.paramNameEnd || ''}
                onChange={(e) =>
                  onChange({
                    ...filter,
                    paramNameEnd: e.target.value.replace(/[^a-zA-Z0-9_]/g, ''),
                    dynamic: true,
                  })
                }
                sx={{ flex: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type={valueInputType}
                label="Valor de prueba (desde)"
                InputLabelProps={nativeDateInputLabelProps}
                inputProps={nativeDateInput ? { placeholder: '' } : undefined}
                value={filter.previewValueStart || ''}
                onChange={(e) => onChange({ ...filter, previewValueStart: e.target.value })}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type={valueInputType}
                label="Valor de prueba (hasta)"
                InputLabelProps={nativeDateInputLabelProps}
                inputProps={nativeDateInput ? { placeholder: '' } : undefined}
                value={filter.previewValueEnd || ''}
                onChange={(e) => onChange({ ...filter, previewValueEnd: e.target.value })}
                sx={{ flex: 1 }}
              />
            </Box>
          </>
        )}
        {needsValue && !isRelativeTime && !isCustomTimeRange && !isFixedDateRange && (
          <TextField
            size="small"
            fullWidth
            type={valueInputType}
            label={isSelection ? 'Valor de prueba' : 'Valor'}
            InputLabelProps={nativeDateInputLabelProps}
            inputProps={nativeDateInput ? { placeholder: '' } : undefined}
            value={currentValue}
            onChange={(e) => {
              const v = e.target.value;
              if (isSelection) onChange({ ...filter, previewValue: v, value: v, dynamic: true });
              else onChange({ ...filter, value: v });
            }}
            onBlur={() => {
              // Un espacio al inicio/final en el valor de un filtro de igualdad rompe el match
              // sin dar ningún error visible (ej. "N' 6ad0e...'" nunca calza con la fila real).
              if (typeof currentValue !== 'string') return;
              const trimmed = currentValue.trim();
              if (trimmed === currentValue) return;
              if (isSelection) onChange({ ...filter, previewValue: trimmed, value: trimmed, dynamic: true });
              else onChange({ ...filter, value: trimmed });
            }}
          />
        )}
        {orSupported && (
          <>
            <Divider />
            <Typography variant="caption" color="text.secondary">
              O también (OR) — la fila pasa si cumple la condición principal o cualquiera de estas
            </Typography>
            {orConditions.map((cond, idx) => {
              const condOp = cond.operator || op;
              const condNeedsValue = operatorNeedsValue(condOp);
              return (
                <Box key={`or-${idx}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
                    <InputLabel>Comparación</InputLabel>
                    <Select
                      label="Comparación"
                      value={condOp}
                      onChange={(e) => {
                        const next = [...orConditions];
                        next[idx] = { ...next[idx], operator: e.target.value };
                        patchOrConditions(next);
                      }}
                    >
                      {comparableOperators.map((o) => (
                        <MenuItem key={o.value} value={o.value}>
                          {o.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {condNeedsValue ? (
                    <TextField
                      size="small"
                      fullWidth
                      type={valueInputType}
                      label="Valor"
                      InputLabelProps={nativeDateInputLabelProps}
                      inputProps={nativeDateInput ? { placeholder: '' } : undefined}
                      value={cond.value ?? ''}
                      onChange={(e) => {
                        const next = [...orConditions];
                        next[idx] = { ...next[idx], value: e.target.value };
                        patchOrConditions(next);
                      }}
                      onBlur={() => {
                        if (typeof cond.value !== 'string') return;
                        const trimmed = cond.value.trim();
                        if (trimmed === cond.value) return;
                        const next = [...orConditions];
                        next[idx] = { ...next[idx], value: trimmed };
                        patchOrConditions(next);
                      }}
                    />
                  ) : (
                    <Box sx={{ flex: 1, pt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Sin valor
                      </Typography>
                    </Box>
                  )}
                  <IconButton
                    size="small"
                    aria-label="Quitar condición OR"
                    onClick={() => patchOrConditions(orConditions.filter((_, i) => i !== idx))}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                patchOrConditions([...orConditions, { operator: op, value: '' }])
              }
            >
              Agregar condición OR
            </Button>
          </>
        )}
        {!orSupported && (filter.orConditions || []).length > 0 && (
          <Alert severity="info" sx={{ py: 0.25 }}>
            Las condiciones OR solo están disponibles en filtros de <strong>Universo (fijo)</strong>, sin
            rango de tiempo relativo ni horario personalizado.
          </Alert>
        )}
        {sampleLoading ? (
          <CircularProgress size={18} />
        ) : (
          !isRelativeTime &&
          !isCustomTimeRange &&
          filter.filterScope !== 'result' &&
          samples.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {samples.map((v, i) => (
                <Chip
                  key={`${i}-${v}`}
                  size="small"
                  label={String(v).length > 28 ? `${String(v).slice(0, 26)}…` : String(v)}
                  onClick={() => {
                    const s = String(v);
                    if (isSelection) onChange({ ...filter, previewValue: s, value: s, dynamic: true });
                    else onChange({ ...filter, value: s });
                  }}
                />
              ))}
            </Box>
          )
        )}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between' }}>
          <Button size="small" color="error" onClick={onRemove}>
            Quitar
          </Button>
          <Button size="small" variant="contained" onClick={onClose}>
            Listo
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}

function filterChipLabel(f, timeRangeSets = [], context) {
  const layer = f.layer || (f.dynamic ? 'selection' : 'universe');
  const col = technicalLabel(f, context);
  const op = f.operator || 'eq';
  if (op === 'inLast') {
    if (layer === 'selection' || f.dynamic) {
      const presetId = resolveRelativeTimePreviewId(f);
      const opt = relativeTimeDashboardOptions(f).find((o) => o.id === presetId);
      return `@${f.paramName || 'Ventana'}: ${opt?.label || relativeTimeLabel(f)}`;
    }
    return `${col}: ${relativeTimeLabel(f)}`;
  }
  if (op === 'inTimeRange') {
    const set = timeRangeSets.find((s) => s.id === f.timeRangeSetId);
    const rangeId = layer === 'selection' || f.dynamic ? f.previewValue ?? f.timeRangeId : f.timeRangeId;
    const range = (set?.ranges || []).find((r) => r.id === rangeId);
    if (layer === 'selection' || f.dynamic) {
      if (rangeId === TIME_RANGE_ALL_VALUE) {
        return `@${f.paramName || set?.name || 'Rango'}: Todos`;
      }
      return `@${f.paramName || set?.name || 'Rango'}: ${range?.label || '?'}`;
    }
    return `${col}: ${set?.name || 'Rango'} → ${range?.label || '?'}`;
  }
  if (op === 'between') {
    if (layer === 'selection' || f.dynamic) {
      return `@${f.paramNameStart || 'Desde'} / @${f.paramNameEnd || 'Hasta'}`;
    }
    return `${col}: ${f.rangeStart || '?'} → ${f.rangeEnd || '?'}`;
  }
  if (layer === 'selection') return `@${f.paramName || col}`;
  if (op === 'isNull') return `${col} vacío`;
  if (op === 'isNotNull') return `${col} no vacío`;
  const parts = [formatFilterConditionLabel(op, f.value)];
  for (const c of f.orConditions || []) {
    parts.push(formatFilterConditionLabel(c.operator || op, c.value));
  }
  if (parts.length > 1) return `${col}: ${parts.join(' OR ')}`;
  return `${col} ${op} ${f.value ?? ''}`;
}

const ChartBuilderDialog = ({
  open,
  onClose,
  connectionId,
  database,
  timezone,
  getAuthHeaders,
  onPublish,
  initialChart = null,
  currentDashboardId = null,
  currentDashboardName = '',
  collectionRefreshKey = 0,
  chartLibrary = [],
  onRestoreToGrid,
  onAddOriginalToDashboard,
  onMakeInstanceIndependent,
  gridReferenceRequest = null,
}) => {
  const [publishAsNew, setPublishAsNew] = useState(false);
  // Tras Duplicar, el recuadro abierto deja de ser el destino: Publicar agrega otro gráfico.
  const editing = Boolean(initialChart?.id) && !publishAsNew;
  const { showError, showSuccess } = useNotification();
  const requestLeave = useRequestLeave();
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [title, setTitle] = useState('');
  const [baseTableId, setBaseTableId] = useState('');
  const [columns, setColumns] = useState([]);
  const [filters, setFilters] = useState([]);
  const [rowLimit, setRowLimit] = useState('');
  const [orderByKind, setOrderByKind] = useState('column');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [orderColumnIndex, setOrderColumnIndex] = useState(0);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [chartPreview, setChartPreview] = useState(null);
  const [chartPreviewLoading, setChartPreviewLoading] = useState(false);
  const [chartPreviewError, setChartPreviewError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [loadedExampleId, setLoadedExampleId] = useState(null);
  // Tras «Duplicar», el builder queda ligado al original NUEVO (publishAsNew).
  // Publicar actualiza esa copia y la agrega al dashboard; no pisa el recuadro abierto.
  const [boundOriginalId, setBoundOriginalId] = useState(null);
  const [reuseExistingQuery, setReuseExistingQuery] = useState(true);
  const [referencePreview, setReferencePreview] = useState(null);
  const [makingIndependent, setMakingIndependent] = useState(false);
  const [loadedExampleTitle, setLoadedExampleTitle] = useState('');
  const [loadWarnings, setLoadWarnings] = useState([]);
  const [examplesRefreshKey, setExamplesRefreshKey] = useState(0);
  const [saveExampleOpen, setSaveExampleOpen] = useState(false);
  const [saveExampleTitle, setSaveExampleTitle] = useState('');
  const [saveExampleNote, setSaveExampleNote] = useState('');
  const [savingExample, setSavingExample] = useState(false);
  const [propagateDialogOpen, setPropagateDialogOpen] = useState(false);
  const [propagateUsage, setPropagateUsage] = useState([]);
  const [vizChartType, setVizChartType] = useState('table');
  const [tablePlusApplyTimezone, setTablePlusApplyTimezone] = useState(true);
  const [tablePlusGroupColumns, setTablePlusGroupColumns] = useState([]);
  const [tablePlusVisibleColumns, setTablePlusVisibleColumns] = useState(null);
  const [tableCalculatedColumns, setTableCalculatedColumns] = useState([]);
  const [vizXField, setVizXField] = useState('');
  const [vizYField, setVizYField] = useState('');
  const [vizKpiAggregation, setVizKpiAggregation] = useState('');
  const [vizNarrativeTemplate, setVizNarrativeTemplate] = useState('');
  const [vizKpiValueFontSize, setVizKpiValueFontSize] = useState('large');
  const [vizKpiComparePriorYear, setVizKpiComparePriorYear] = useState(false);
  // '' = sin comparacion; 'week' | 'month' | 'year' generalizan un mecanismo anterior que
  // solo comparaba contra el año previo, con dia de semana exacto por -364 dias.
  const [vizComparePeriod, setVizComparePeriod] = useState('');
  // Diferencia/porcentaje de variacion entre dos valores de "Series/color" en el mismo
  // punto del eje X (ej. una columna "año" con 2025/2026), no entre periodos de fecha
  // como vizComparePeriod. '' en cualquiera de los dos = sin calculo.
  const [vizSeriesMetricA, setVizSeriesMetricA] = useState('');
  const [vizSeriesMetricB, setVizSeriesMetricB] = useState('');
  // Columna medida (no un valor de serie) por la que dividir (A - B), ej. una columna
  // numérica traída por "Seguir referencia" desde una tabla relacionada. '' = sin cálculo.
  const [vizSeriesMetricDivideBy, setVizSeriesMetricDivideBy] = useState('');
  // Suma corrida de la diferencia (A - B) en el orden del eje X, reiniciando cada dia
  // calendario. Solo aplica con un eje X de fecha real (no un bucket numerico).
  const [vizSeriesMetricCumulative, setVizSeriesMetricCumulative] = useState(false);
  // Grafica la diferencia/porcentaje de variacion (de cualquiera de los dos mecanismos de
  // arriba) como una linea mas, en un eje Y a la derecha con su propia escala.
  const [vizShowMetricPercentLine, setVizShowMetricPercentLine] = useState(false);
  // Comparacion por segunda fecha elegida libremente: vizCustomDateCompareBaseStart
  // identifica el filtro de rango base (por su paramNameStart; paramNameEnd se busca en
  // filters con ese mismo paramNameStart al armar el spec), vizCustomDateCompareParam es el
  // paramName del filtro "Fecha Compara" (excludeFromWhere). '' en cualquiera = sin calculo.
  const [vizCustomDateCompareBaseStart, setVizCustomDateCompareBaseStart] = useState('');
  const [vizCustomDateCompareParam, setVizCustomDateCompareParam] = useState('');
  const [vizSeriesField, setVizSeriesField] = useState('');
  // Tipos sin consulta: Boton, Clima, Calendario. No usan
  // columns/filters/SQL en absoluto, solo su propia configuracion minima.
  const [vizButtonUrl, setVizButtonUrl] = useState('');
  const [vizWeatherLat, setVizWeatherLat] = useState('');
  const [vizWeatherLon, setVizWeatherLon] = useState('');
  const [vizCalendarEvents, setVizCalendarEvents] = useState([]);
  const [vizSeriesTimeRangeColumn, setVizSeriesTimeRangeColumn] = useState('');
  const [vizBgImageUrl, setVizBgImageUrl] = useState('');
  const [vizDensityRenderMode, setVizDensityRenderMode] = useState('heatmap');
  const [vizDensityHeatmapStyle, setVizDensityHeatmapStyle] = useState('smooth');
  const [vizDensityIntensity, setVizDensityIntensity] = useState(1);
  const [vizDensityOverlayOpacity, setVizDensityOverlayOpacity] = useState(1);
  const [vizDensityBlur, setVizDensityBlur] = useState(7);
  const [vizGeoHeatmapRadius, setVizGeoHeatmapRadius] = useState(25);
  const [vizGeoHeatmapBlur, setVizGeoHeatmapBlur] = useState(15);
  const [vizDensityPointSize, setVizDensityPointSize] = useState(1);
  const [vizDensitySeparation, setVizDensitySeparation] = useState(0.55);
  const [vizDensityBackgroundDim, setVizDensityBackgroundDim] = useState(0.72);
  const [vizDensityGroupBy, setVizDensityGroupBy] = useState('');
  const [vizDensityWeightCol, setVizDensityWeightCol] = useState('');
  const [vizDensityShowLegend, setVizDensityShowLegend] = useState(true);
  const [vizDensityPointRadius, setVizDensityPointRadius] = useState(14);
  const [vizDensitySelectedGroups, setVizDensitySelectedGroups] = useState(null);
  const [vizDensityClusterEnabled, setVizDensityClusterEnabled] = useState(false);
  const [vizDensityClusterRadiusPx, setVizDensityClusterRadiusPx] = useState(25);
  const [vizDensityClusterMerge, setVizDensityClusterMerge] = useState('count');
  const [vizDensityClusterShowCount, setVizDensityClusterShowCount] = useState(false);
  const [vizDensityClusterColorByCount, setVizDensityClusterColorByCount] = useState(false);
  const [vizDensityTooltipEnabled, setVizDensityTooltipEnabled] = useState(false);
  const [vizDensityTooltipColumns, setVizDensityTooltipColumns] = useState([]);
  const [vizDensityTooltipDetail, setVizDensityTooltipDetail] = useState(null);
  const [vizIntervalStartField, setVizIntervalStartField] = useState('');
  const [vizIntervalEndField, setVizIntervalEndField] = useState('');
  const [vizIntervalSeriesField, setVizIntervalSeriesField] = useState('');
  const [vizCarouselSensorTypeField, setVizCarouselSensorTypeField] = useState('');
  const [vizCarouselSensorIdField, setVizCarouselSensorIdField] = useState('');
  const [vizCarouselContentField, setVizCarouselContentField] = useState('');
  const [vizCarouselInZoneField, setVizCarouselInZoneField] = useState('');
  const [vizCarouselSingleImageMode, setVizCarouselSingleImageMode] = useState(false);
  const [vizCarouselShowOnlyInZone, setVizCarouselShowOnlyInZone] = useState(false);
  const [vizCarouselCropMode, setVizCarouselCropMode] = useState(false);
  const [vizCarouselCropClassFilter, setVizCarouselCropClassFilter] = useState('');
  const [vizDensityPositionMode, setVizDensityPositionMode] = useState('columns');
  const [vizDensityPositionKeyColumn, setVizDensityPositionKeyColumn] = useState('');
  const [vizDensityManualPositions, setVizDensityManualPositions] = useState({});
  const [vizDensityMetricColumns, setVizDensityMetricColumns] = useState([]);
  const [vizDensityShowMarkerLabels, setVizDensityShowMarkerLabels] = useState(false);
  const [vizDensityMarkerEnumColumn, setVizDensityMarkerEnumColumn] = useState('');
  const [vizDensityMarkerShape, setVizDensityMarkerShape] = useState('circle');
  const [vizDensityMarkerValueColumn, setVizDensityMarkerValueColumn] = useState('');
  const [vizDensityMarkerValueAggregation, setVizDensityMarkerValueAggregation] = useState('first');
  const [vizDensityMarkerLabelColumn, setVizDensityMarkerLabelColumn] = useState('');
  const [vizDensityMarkerFill, setVizDensityMarkerFill] = useState('value');
  const [vizDensityMarkerFit, setVizDensityMarkerFit] = useState('text');
  const [vizDensityMarkerSize, setVizDensityMarkerSize] = useState('medium');
  const [vizDensityMarkerColor, setVizDensityMarkerColor] = useState('#ffffff');
  const [vizDensityImageWidth, setVizDensityImageWidth] = useState(undefined);
  const [vizDensityImageHeight, setVizDensityImageHeight] = useState(undefined);
  const [vizDensityPositionColumnsCommitted, setVizDensityPositionColumnsCommitted] = useState(false);
  const [heatmapPositionDraft, setHeatmapPositionDraft] = useState(null);
  const [vizStacked, setVizStacked] = useState(false);
  const [vizHorizontal, setVizHorizontal] = useState(false);
  const [vizBarShowValueLabels, setVizBarShowValueLabels] = useState(false);
  const [vizLineShowValueLabels, setVizLineShowValueLabels] = useState(false);
  const [vizLineFillMissingAsZero, setVizLineFillMissingAsZero] = useState(true);
  const [vizFillTimeAxisGaps, setVizFillTimeAxisGaps] = useState(false);
  const [vizLineInterpolation, setVizLineInterpolation] = useState('linear');
  const [vizPieShowValueLabel, setVizPieShowValueLabel] = useState(true);
  const [vizPieShowPercentLabel, setVizPieShowPercentLabel] = useState(true);
  const [vizPieLabelPosition, setVizPieLabelPosition] = useState(DEFAULT_PIE_LABEL_POSITION);
  const [vizPieShowNameLabel, setVizPieShowNameLabel] = useState(false);
  const [vizPieLegendFontSize, setVizPieLegendFontSize] = useState(DEFAULT_PIE_LEGEND_FONT_SIZE);
  const [vizLegendPosition, setVizLegendPosition] = useState('left');
  const [vizLegendDisplay, setVizLegendDisplay] = useState(true);
  const [vizXAxisTitle, setVizXAxisTitle] = useState('');
  const [vizYAxisTitle, setVizYAxisTitle] = useState('');
  const [showSql, setShowSql] = useState(false);
  const [sqlFullscreen, setSqlFullscreen] = useState(false);
  const [sqlCopied, setSqlCopied] = useState(false);
  const sqlCopiedTimer = useRef(null);
  const [filterEditIndex, setFilterEditIndex] = useState(null);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const filterBarRef = useRef(null);
  const previewTimer = useRef(null);
  const chartPreviewTimer = useRef(null);
  const buildSpecRef = useRef(null);
  const previewRequestSeq = useRef(0);
  const chartPreviewRequestSeq = useRef(0);
  /** previewDataKey con el que se generó chartPreview (evita mostrar gráfico obsoleto). */
  const chartPreviewDataKeyRef = useRef(null);
  const loadedChartIdRef = useRef(null);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [previewTab, setPreviewTab] = useState('table');
  const [episodeAnalysis, setEpisodeAnalysis] = useState(null);
  const [spatialZoneAnalysis, setSpatialZoneAnalysis] = useState(null);

  const handleCopySql = useCallback(
    async (sqlText) => {
      const copied = await copyTextToClipboard(sqlText);
      if (!copied) {
        showError('No se pudo copiar el SQL. Selecciona el texto y cópialo manualmente.');
        return;
      }
      setSqlCopied(true);
      clearTimeout(sqlCopiedTimer.current);
      sqlCopiedTimer.current = setTimeout(() => setSqlCopied(false), 1500);
    },
    [showError]
  );

  useEffect(() => () => clearTimeout(sqlCopiedTimer.current), []);
  const [postProcessOrder, setPostProcessOrder] = useState(DEFAULT_POST_PROCESS_ORDER);
  const [pipelinePreviews, setPipelinePreviews] = useState({ episodes: null, zones: null });
  const [spatialZoneDialogOpen, setSpatialZoneDialogOpen] = useState(false);
  const [episodeDialogOpen, setEpisodeDialogOpen] = useState(false);

  const mergeLiveLibraryEntry = useCallback(
    (entry) => {
      if (!entry?.id || !chartLibrary?.length) return entry;
      const live = chartLibrary.find((e) => e.id === entry.id);
      return live ? { ...entry, ...live } : entry;
    },
    [chartLibrary]
  );

  const loadContextMutation = useMutation({
    mutationFn: ({ headers, connectionId, database }) =>
      axios.get('/api/chart-builder/context', { headers, params: { connectionId, database } }),
  });

  const loadContext = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers || !connectionId || !database) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await loadContextMutation.mutateAsync({ headers, connectionId, database });
      if (res.data.success) {
        setContext(res.data.context);
        if (res.data.context.tables?.length === 1) {
          setBaseTableId(res.data.context.tables[0].id);
        }
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al cargar modelo');
    } finally {
      setLoading(false);
    }
  }, [connectionId, database, getAuthHeaders, showError]);

  useEffect(() => {
    if (open) {
      setPreview(null);
      // Cada apertura debe poder recargar dataset (editar / ejemplo)
      loadedChartIdRef.current = null;
      if (!initialChart) {
        setLoadedExampleId(null);
        setBoundOriginalId(null);
        setReuseExistingQuery(true);
        setReferencePreview(null);
        setLoadedExampleTitle('');
        setLoadWarnings([]);
        setVizChartType('table');
        setTablePlusApplyTimezone(true);
        setTablePlusGroupColumns([]);
        setTablePlusVisibleColumns(null);
        setTableCalculatedColumns([]);
        setVizButtonUrl('');
        setVizWeatherLat('');
        setVizWeatherLon('');
        setVizCalendarEvents([]);
        setVizStacked(false);
        setVizHorizontal(false);
        setVizBarShowValueLabels(false);
        setVizLineShowValueLabels(false);
        setVizBgImageUrl('');
        setVizDensityRenderMode('heatmap');
        setVizDensityHeatmapStyle('smooth');
        setVizDensityIntensity(1);
        setVizDensityOverlayOpacity(1);
        setVizDensityBlur(7);
        setVizDensityPointSize(1);
        setVizDensitySeparation(0.55);
        setVizDensityBackgroundDim(0.72);
        setVizDensityGroupBy('');
        setVizDensityWeightCol('');
        setVizDensityShowLegend(true);
        setPreviewTab('table');
        setColumns([]);
        setFilters([]);
        setRowLimit('');
        setTitle('');
        setBaseTableId('');
        // Reset de estado visual al crear un grafico nuevo, para no heredar
        // la configuracion (ejes, leyenda, series, torta, lineas, mapa de
        // calor) del ultimo grafico editado en esta sesion.
        setVizXAxisTitle('');
        setVizYAxisTitle('');
        setVizXField('');
        setVizYField('');
        setVizKpiAggregation('');
        setVizNarrativeTemplate('');
        setVizKpiValueFontSize('large');
        setVizKpiComparePriorYear(false);
        setVizComparePeriod('');
        setVizSeriesMetricA('');
        setVizSeriesMetricB('');
        setVizSeriesMetricDivideBy('');
        setVizSeriesMetricCumulative(false);
        setVizShowMetricPercentLine(false);
        setVizCustomDateCompareBaseStart('');
        setVizCustomDateCompareParam('');
        setVizSeriesField('');
        setVizSeriesTimeRangeColumn('');
        setVizLegendPosition('left');
        setVizLegendDisplay(true);
        setVizPieLegendFontSize(DEFAULT_PIE_LEGEND_FONT_SIZE);
        setVizPieShowValueLabel(true);
        setVizPieShowPercentLabel(true);
        setVizPieLabelPosition(DEFAULT_PIE_LABEL_POSITION);
        setVizPieShowNameLabel(false);
        setVizLineFillMissingAsZero(true);
        setVizFillTimeAxisGaps(false);
        setVizLineInterpolation('linear');
        setVizGeoHeatmapRadius(25);
        setVizGeoHeatmapBlur(15);
        setVizDensityPointRadius(14);
        setVizDensitySelectedGroups(null);
        setVizDensityClusterEnabled(false);
        setVizDensityClusterRadiusPx(25);
        setVizDensityClusterMerge('count');
        setVizDensityClusterShowCount(false);
        setVizDensityClusterColorByCount(false);
        setVizDensityTooltipEnabled(false);
        setVizDensityTooltipColumns([]);
        setVizDensityTooltipDetail(null);
        setVizIntervalStartField('');
        setVizIntervalEndField('');
        setVizIntervalSeriesField('');
        setVizDensityPositionMode('columns');
        setVizDensityPositionKeyColumn('');
        setVizDensityManualPositions({});
        setVizDensityMetricColumns([]);
        setVizDensityShowMarkerLabels(false);
        setVizDensityMarkerEnumColumn('');
        setVizDensityMarkerShape('circle');
        setVizDensityMarkerValueColumn('');
        setVizDensityMarkerValueAggregation('first');
        setVizDensityMarkerLabelColumn('');
        setVizDensityMarkerFill('value');
        setVizDensityMarkerFit('text');
        setVizDensityMarkerSize('medium');
        setVizDensityMarkerColor('#ffffff');
        setVizDensityImageWidth(undefined);
        setVizDensityImageHeight(undefined);
      }
      loadContext();
    } else {
      loadedChartIdRef.current = null;
      setFilterEditIndex(null);
      setFilterAnchor(null);
      setReferencePreview(null);
    }
  }, [open, loadContext, initialChart?.id]);

  useEffect(() => {
    setPublishAsNew(false);
  }, [open, initialChart?.id]);

  const loadChartOriginalsMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/chart-originals', { headers }),
  });

  // Compartida por los 4 lugares que piden /api/chart-builder/preview (tabla de zonas,
  // serie de referencia, preview de tabla y preview de grafico): mismo endpoint y forma de body.
  const chartBuilderPreviewMutation = useMutation({
    mutationFn: ({ connectionId, database, spec, timezone, headers }) =>
      axios.post('/api/chart-builder/preview', { connectionId, database, spec, timezone }, { headers }),
  });

  useEffect(() => {
    if (!open || !gridReferenceRequest?.chart?.originalChartId) return;
    const headers = getAuthHeaders?.();
    if (!headers) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await loadChartOriginalsMutation.mutateAsync(headers);
        if (cancelled || !res.data.success) return;
        const original = (res.data.originals || []).find(
          (o) => o.id === gridReferenceRequest.chart.originalChartId
        );
        if (!original) return;
        const entry = mergeLiveLibraryEntry(
          gridReferenceRequest.entry || {
            id: gridReferenceRequest.chart.id,
            title: gridReferenceRequest.chart.label,
            originalChartId: gridReferenceRequest.chart.originalChartId,
            isReference: true,
          }
        );
        setReferencePreview({
          original,
          entry,
          dashboardId: currentDashboardId,
          dashboardName: currentDashboardName || '',
        });
        setLoadedExampleId(null);
        setLoadedExampleTitle('');
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    gridReferenceRequest,
    getAuthHeaders,
    currentDashboardId,
    currentDashboardName,
    mergeLiveLibraryEntry,
  ]);

  useEffect(() => {
    if (vizChartType === 'table') setPreviewTab('table');
  }, [vizChartType]);

  useEffect(() => {
    setFilters((prev) => {
      if (!prev.some((f) => !f.clientId)) return prev;
      return normalizeFiltersClientIds(prev);
    });
  }, [open, filters.length]);

  useEffect(() => {
    if (filterEditIndex == null) return;
    if (filterEditIndex >= filters.length || !filters[filterEditIndex]) {
      setFilterEditIndex(null);
      setFilterAnchor(null);
      return;
    }
    if (filterAnchor && !isValidPopoverAnchor(filterAnchor)) {
      const fallback = filterBarRef.current;
      if (isValidPopoverAnchor(fallback)) {
        setFilterAnchor(fallback);
      } else {
        setFilterEditIndex(null);
        setFilterAnchor(null);
      }
    }
  }, [filterEditIndex, filterAnchor, filters]);

  const { rows: specRows, values: specValues, tableColumns: specTableColumns } = useMemo(
    () => columnsToRowsValues(columns),
    [columns]
  );

  const episodesTableActive = Boolean(episodeAnalysis?.enabled);
  const zonesTableActive = Boolean(spatialZoneAnalysis?.enabled);
  const chartFeed = useMemo(
    () => resolveChartFeedFromPipeline(postProcessOrder, episodesTableActive, zonesTableActive),
    [postProcessOrder, episodesTableActive, zonesTableActive]
  );

  const chartFeedTableData = useMemo(() => {
    if (chartFeed === 'episodes') {
      return chartPreview?.data || pipelinePreviews.episodes?.data || [];
    }
    if (chartFeed === 'zones') {
      return chartPreview?.data || pipelinePreviews.zones?.data || [];
    }
    return preview?.data || [];
  }, [chartFeed, preview, chartPreview, pipelinePreviews]);

  /** Dataset para el widget de gráfico en la pestaña de preview. */
  const chartWidgetPreview = useMemo(() => {
    if ((isRawRowsChartType(vizChartType) || vizChartType === 'tablePlus') && chartFeed === 'base') {
      return preview;
    }
    return chartPreview;
  }, [vizChartType, chartFeed, preview, chartPreview]);

  /** Valor actual de cada filtro del editor, por columna: mismo prop filterValues que ya
   * recibe ChartWidget en el dashboard (ChartGrid.js) para resolver {{marcadores}} de texto
   * narrativo. Sin esto, el canvas de la derecha (a diferencia del panel "Marcadores
   * disponibles"/"Vista previa" de más abajo, que sí los resuelve) no los interpola. */
  const chartWidgetPreviewFilterValues = useMemo(() => {
    const map = {};
    filters.forEach((f) => {
      if (!f?.column) return;
      const value = f.value ?? f.previewValue ?? f.selectedValue;
      if (value == null || value === '') return;
      map[f.column] = value;
    });
    return map;
  }, [filters]);

  /** Marcadores {{clave}} disponibles para "Texto narrativo", calculados desde el preview
   * agrupado (misma forma de datos que Barras: columna de agrupación + columna de valor). */
  const narrativePlaceholders = useMemo(() => {
    const xAlias = chartWidgetPreview?.mapping?.xAlias;
    const yAlias = chartWidgetPreview?.mapping?.yAlias;
    const list = buildNarrativePlaceholders(chartWidgetPreview?.data, xAlias, yAlias);
    // Mismo criterio que en el dashboard (ChartWidget.js): cualquier filtro activo queda
    // disponible como marcador por su propio nombre de columna.
    const placeholderKeysInUse = new Set(list.map((p) => p.key));
    filters.forEach((f) => {
      if (!f?.column || isInternalIdColumn(f.column)) return;
      const value = f.value ?? f.previewValue ?? f.selectedValue;
      if (value == null || value === '') return;
      const key = dedupePlaceholderKey(slugifyPlaceholderKey(f.column), placeholderKeysInUse);
      list.push({ key, label: f.column, valueText: String(value) });
    });
    return list;
  }, [chartWidgetPreview, filters]);

  /** Texto resultante de aplicar la plantilla escrita en el editor a los marcadores actuales. */
  const narrativePreviewSegments = useMemo(
    () => renderNarrativeSegments(vizNarrativeTemplate, narrativePlaceholders),
    [vizNarrativeTemplate, narrativePlaceholders]
  );

  /** Valores distintos de "Series/color" en el preview actual, para elegir entre cuales
   * calcular una diferencia/porcentaje de variacion (ej. columna "año" con 2025/2026). */
  const distinctSeriesLabelValues = useMemo(() => {
    const labelCol = chartWidgetPreview?.mapping?.labelAlias;
    if (!labelCol || !Array.isArray(chartWidgetPreview?.data)) return [];
    const seen = new Set();
    const values = [];
    chartWidgetPreview.data.forEach((row) => {
      const v = row?.[labelCol];
      if (v == null || v === '') return;
      const s = String(v);
      if (!seen.has(s)) {
        seen.add(s);
        values.push(s);
      }
    });
    return values;
  }, [chartWidgetPreview]);

  const chartFeedTableLabel = useMemo(() => {
    if (chartFeed === 'episodes') return 'Episodios';
    if (chartFeed === 'zones') return 'Zonas';
    return 'Serie SQL';
  }, [chartFeed]);

  const spatialZoneGroupCols = useMemo(() => resolveSpatialGroupCols(columns), [columns]);
  const episodeInputSource = useMemo(
    () => getEpisodeInputSource(postProcessOrder, zonesTableActive),
    [postProcessOrder, zonesTableActive]
  );
  const episodeInputFields = useMemo(() => {
    if (episodeInputSource.source !== 'zones') return null;
    return spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols);
  }, [episodeInputSource.source, spatialZoneAnalysis, spatialZoneGroupCols]);
  const episodeGroupCols = useMemo(() => {
    const cfg = episodeAnalysis || {};
    const eligible =
      episodeInputSource.source === 'zones' && episodeInputFields
        ? resolveEpisodeGroupColsFromOutputFields(episodeInputFields, cfg)
        : resolveEpisodeGroupCols(columns, cfg);
    return applyEpisodePreserveGroupColumns(eligible, cfg);
  }, [columns, episodeAnalysis, episodeInputSource, episodeInputFields]);

  const buildSpec = useCallback(({ queryChartType } = {}) => {
    const vizType = vizChartType || 'table';
    const sqlChartType =
      queryChartType ?? (vizType === 'tablePlus' ? 'table' : vizType === 'narrative' ? 'bar' : vizType);
    const cleanFilters = filters
      .filter((f) => {
        if (!f.column) return false;
        const layer = f.layer || (f.dynamic ? 'selection' : 'universe');
        if (f.operator === 'inTimeRange') {
          if (layer === 'selection' || f.dynamic) {
            return Boolean(f.paramName && f.timeRangeSetId);
          }
          return Boolean(f.timeRangeSetId && f.timeRangeId);
        }
        if (f.operator === 'inLast') {
          if (layer === 'selection' || f.dynamic) {
            return Boolean(f.paramName && resolveRelativeTimePreviewId(f));
          }
          if ((f.relativeMode || 'rolling') === 'calendar') {
            return Boolean(f.relativePeriod);
          }
          const n = parseInt(f.relativeAmount, 10);
          return Number.isFinite(n) && n > 0 && Boolean(f.relativeUnit);
        }
        if (f.operator === 'between') {
          if (layer === 'selection' || f.dynamic) {
            return Boolean(f.paramNameStart && f.paramNameEnd);
          }
          return Boolean(String(f.rangeStart ?? '').trim()) && Boolean(String(f.rangeEnd ?? '').trim());
        }
        if (layer === 'selection') return Boolean(f.paramName);
        if (!operatorNeedsValue(f.operator)) return true;
        return String(f.value ?? '').trim() !== '';
      })
      .map((f) => {
        // Un espacio al inicio/final rompe el match de igualdad/rango sin ningún error
        // visible. El campo del editor ya recorta al perder el foco (UX), pero publicar sin
        // salir del campo no dispara ese blur; se recorta también acá, en el único punto por
        // el que pasa cualquier filtro antes de guardarse.
        const trimStr = (v) => (typeof v === 'string' ? v.trim() : v);
        const layer = f.layer || (f.dynamic ? 'selection' : 'universe');
        const isSel = layer === 'selection';
        const matchedCol = columns.find((c) => sameFilterColumn(f, c));
        const isObjectKvFilter =
          Boolean(f.objectKvPart) ||
          f.jsonMode === 'objectKv' ||
          matchedCol?.jsonMode === 'objectKv';
        const isResult =
          !isObjectKvFilter &&
          (f.filterScope === 'result' || (matchedCol && isValueColumn(matchedCol)));
        const repairFromCol =
          matchedCol ||
          columns.find(
            (c) =>
              c?.tableId &&
              c?.column &&
              c.tableId === f.tableId &&
              (c.joinAlias || '') === (f.joinAlias || '') &&
              !isValueColumn(c)
          ) ||
          (f.operator === 'inLast' || f.operator === 'inTimeRange' || f.operator === 'between'
            ? columns.find(
                (c) =>
                  c?.tableId &&
                  c?.column &&
                  (c.column === 'time' || (c.dateBucket && c.dateBucket !== 'raw'))
              )
            : null);
        const invalidFilterColumn =
          !f.column ||
          ['timescaledb', 'postgresql', 'postgres', 'mssql', 'sqlserver', 'default'].includes(
            String(f.column).toLowerCase()
          );
        const measureMeta =
          isResult && matchedCol
            ? {
                jsonMode: matchedCol.jsonMode || f.jsonMode || undefined,
                aggregation: matchedCol.aggregation || f.aggregation || undefined,
                arrayValueField: matchedCol.arrayValueField || f.arrayValueField || undefined,
                arrayFilters: matchedCol.arrayFilters?.length
                  ? matchedCol.arrayFilters
                  : f.arrayFilters?.length
                    ? f.arrayFilters
                    : undefined,
                filterScope: 'result',
              }
            : { filterScope: f.filterScope || 'row' };
        const base = {
          tableId: repairFromCol?.tableId || f.tableId,
          column: invalidFilterColumn && repairFromCol?.column ? repairFromCol.column : f.column,
          jsonPath:
            f.jsonPath ||
            (repairFromCol && !invalidFilterColumn ? repairFromCol.jsonPath : repairFromCol?.jsonPath) ||
            undefined,
          jsonMode: f.jsonMode || measureMeta.jsonMode || repairFromCol?.jsonMode || undefined,
          objectKvPart:
            f.objectKvPart ||
            (matchedCol?.jsonMode === 'objectKv' || f.jsonMode === 'objectKv' ? 'value' : undefined),
          objectValueAgg:
            f.objectValueAgg || matchedCol?.objectValueAgg || repairFromCol?.objectValueAgg || undefined,
          aggregation: f.aggregation || measureMeta.aggregation || undefined,
          arrayValueField: f.arrayValueField || measureMeta.arrayValueField || undefined,
          arrayFilters: f.arrayFilters || measureMeta.arrayFilters || undefined,
          filterScope: measureMeta.filterScope,
          joinAlias: f.joinAlias || repairFromCol?.joinAlias || undefined,
          via: f.via || repairFromCol?.via || undefined,
          viaPath: f.viaPath?.length ? f.viaPath : repairFromCol?.viaPath?.length ? repairFromCol.viaPath : undefined,
          layer:
            f.operator === 'inLast'
              ? isSel
                ? 'selection'
                : 'universe'
              : layer,
          dynamic: f.operator === 'inLast' ? isSel : isSel,
          operator: f.operator || 'eq',
          value: isSel ? undefined : trimStr(f.value),
          paramName: isSel ? f.paramName : undefined,
          previewValue:
            isSel && f.operator === 'inLast'
              ? resolveRelativeTimePreviewId(f)
              : isSel
                ? trimStr(f.previewValue ?? f.value ?? '')
                : undefined,
          ...(isSel && f.allowExecuteWithoutSelection === true
            ? { allowExecuteWithoutSelection: true }
            : {}),
          ...(isSel && f.excludeFromWhere === true ? { excludeFromWhere: true } : {}),
          ...(isSel && f.matchWholeDay === true ? { matchWholeDay: true } : {}),
        };
        if (f.operator === 'inLast') {
          const inLastBase = {
            ...base,
            layer: isSel ? 'selection' : 'universe',
            dynamic: isSel,
            paramName: isSel ? f.paramName : undefined,
            previewValue: isSel ? resolveRelativeTimePreviewId(f) : undefined,
          };
          if ((f.relativeMode || 'rolling') === 'calendar') {
            return {
              ...inLastBase,
              relativeMode: 'calendar',
              relativePeriod: f.relativePeriod || 'week',
            };
          }
          return {
            ...inLastBase,
            relativeMode: 'rolling',
            relativeAmount: Math.max(1, parseInt(f.relativeAmount, 10) || 1),
            relativeUnit: f.relativeUnit || 'days',
          };
        }
        if (f.operator === 'inTimeRange') {
          return {
            ...base,
            filterKind: 'timeRange',
            timeRangeSetId: f.timeRangeSetId,
            timeRangeId: f.timeRangeId,
            ...(isSel
              ? {
                  previewValue: f.previewValue ?? f.timeRangeId ?? TIME_RANGE_ALL_VALUE,
                }
              : {}),
          };
        }
        if (f.operator === 'between') {
          if (isSel) {
            return {
              ...base,
              layer: 'selection',
              dynamic: true,
              paramName: undefined,
              paramNameStart: f.paramNameStart,
              paramNameEnd: f.paramNameEnd,
              previewValue: undefined,
              previewValueStart: trimStr(f.previewValueStart ?? f.rangeStart),
              previewValueEnd: trimStr(f.previewValueEnd ?? f.rangeEnd),
              value: undefined,
              rangeStart: undefined,
              rangeEnd: undefined,
            };
          }
          return {
            ...base,
            layer: 'universe',
            dynamic: false,
            paramName: undefined,
            previewValue: undefined,
            value: undefined,
            rangeStart: trimStr(f.rangeStart),
            rangeEnd: trimStr(f.rangeEnd),
          };
        }
        const cleanedOr = (f.orConditions || [])
          .filter((c) => {
            const cop = c?.operator || f.operator;
            if (!operatorNeedsValue(cop)) return true;
            return String(c?.value ?? '').trim() !== '';
          })
          .map((c) => ({
            operator: c.operator || f.operator,
            value: trimStr(c.value),
          }));
        return {
          ...base,
          ...(cleanedOr.length ? { orConditions: cleanedOr } : {}),
        };
      });

    // cleanFilters ya viene en el orden que el usuario arma con "arrastra
    // para ordenar" en Filtros activos (handleFilterDragEnd reordena el estado
    // filters directamente). Antes, esta funcion volvia a ordenar por la posicion
    // de la columna en la tabla, pisando ese orden manual en cada publish/preview
    // (el filtro de Fecha, cuya columna suele agregarse temprano, siempre quedaba
    // primero sin importar donde se lo arrastrara).
    const orderedFilters = cleanFilters;

    const configured = columns.filter((c) => c.tableId && c.column);
    // Una columna "Solo filtro" no aparece en el SELECT, asi que no se puede ordenar
    // por ella (rompe el GROUP BY). Si el indice guardado apunta a una, cae a la
    // primera columna que si se pueda ordenar.
    const sortableConfigured = configured.filter((c) => c.role !== 'filter');
    let sortCol = columns[orderColumnIndex];
    if (!sortCol?.tableId || !sortCol?.column || sortCol.role === 'filter') {
      sortCol = sortableConfigured[0] || configured[0];
    }
    let resolvedOrderIndex = 0;
    if (sortCol?.tableId && sortCol?.column) {
      const found = specTableColumns.findIndex(
        (c) =>
          c.tableId === sortCol.tableId &&
          c.column === sortCol.column &&
          (c.jsonPath || '') === (sortCol.jsonPath || '') &&
          (c.joinAlias || '') === (sortCol.joinAlias || '')
      );
      resolvedOrderIndex = found >= 0 ? found : 0;
    }

    // Base = primera columna plana (hechos). Nunca el destino de un follow.
    const inferBaseTable = () => {
      const plain = configured.find((c) => c.tableId && !c.joinAlias);
      if (plain?.tableId) return plain.tableId;
      for (const c of configured) {
        if (c.viaPath?.[0]?.fromTableId) return c.viaPath[0].fromTableId;
        if (c.joinAlias && c.via?.fromTableId && !c.via.fromJoinAlias) return c.via.fromTableId;
      }
      return configured[0]?.tableId;
    };

    const seriesSetId = seriesTimeRangeSetIdFromField(vizSeriesField);
    const seriesRowField = seriesSetId ? '' : vizSeriesField;
    const seriesSourceField = seriesSetId
      ? vizSeriesTimeRangeColumn ||
        guessSeriesTimeRangeSourceField({
          chartFeed,
          episodeAnalysis,
          episodeGroupCols,
          spatialZoneAnalysis,
          spatialZoneGroupCols,
          columns,
          filters: orderedFilters,
          setId: seriesSetId,
          context,
          vizXField,
          fieldAlias,
          getColumnMeta,
          guessSqlTimeRangeSourceField: guessTimeRangeSourceField,
        })
      : '';

    let episodeAnalysisSpec;
    if (episodeAnalysis?.enabled) {
      episodeAnalysisSpec = {
        ...episodeAnalysis,
        chartSource: chartFeed === 'episodes' ? 'episodes' : 'original',
      };
      if (
        episodeAnalysisSpec.chartSource !== 'original' &&
        !episodeAnalysisSpec.xDateDisplayFormat
      ) {
        const timeCol = columns.find(
          (c) =>
            c?.tableId &&
            c?.column &&
            c.role !== 'value' &&
            ['minute', 'hour', 'raw'].includes(c.dateBucket || 'raw') &&
            c.dateDisplayFormat &&
            c.dateDisplayFormat !== 'auto'
        );
        if (timeCol?.dateDisplayFormat) {
          episodeAnalysisSpec.xDateDisplayFormat = timeCol.dateDisplayFormat;
        }
      }
    }

    let spatialZoneAnalysisSpec;
    if (spatialZoneAnalysis?.enabled) {
      spatialZoneAnalysisSpec = {
        ...spatialZoneAnalysis,
        chartSource: chartFeed === 'zones' ? 'zones' : 'original',
      };
    }

    return {
      kind: 'dataset',
      chartType: sqlChartType,
      baseTableId: baseTableId || inferBaseTable() || orderedFilters[0]?.tableId,
      rows: specRows,
      values: specValues,
      // Orden de la UI (no reordenar filas→valores)
      tableColumns: specTableColumns,
      filters: orderedFilters,
      orderBy: vizType === 'kpi' ? 'none' : orderByKind,
      orderDirection,
      orderColumnIndex: resolvedOrderIndex,
      limit: rowLimitInputToSpecLimit(rowLimit),
      ...(episodeAnalysisSpec ? { episodeAnalysis: episodeAnalysisSpec } : {}),
      ...(spatialZoneAnalysisSpec ? { spatialZoneAnalysis: spatialZoneAnalysisSpec } : {}),
      visualization: {
        chartFeed,
        postProcessOrder,
        chartType: vizType === 'tablePlus' ? 'tablePlus' : sqlChartType,
        xField:
          vizType === 'imageDensity'
            ? imageDensityAxisForSpec(vizXField, IMAGE_DENSITY_AXIS_AUTO_X)
            : vizType === 'geoHeatmap'
              ? geoHeatmapAxisForSpec(vizXField, GEO_HEATMAP_AUTO_LAT) || vizXField
              : vizXField || undefined,
        yField:
          vizType === 'imageDensity'
            ? imageDensityAxisForSpec(vizYField, IMAGE_DENSITY_AXIS_AUTO_Y)
            : vizType === 'geoHeatmap'
              ? geoHeatmapAxisForSpec(vizYField, GEO_HEATMAP_AUTO_LNG) || vizYField
              : vizYField || undefined,
        latField:
          vizType === 'geoHeatmap'
            ? geoHeatmapAxisForSpec(vizXField, GEO_HEATMAP_AUTO_LAT) || vizXField
            : undefined,
        lngField:
          vizType === 'geoHeatmap'
            ? geoHeatmapAxisForSpec(vizYField, GEO_HEATMAP_AUTO_LNG) || vizYField
            : undefined,
        ...(vizType === 'kpi' && vizKpiAggregation ? { kpiAggregation: vizKpiAggregation } : {}),
        ...(vizType === 'narrative' ? { isNarrative: true, narrativeTemplate: vizNarrativeTemplate } : {}),
        ...(vizType === 'kpi' &&
        (vizKpiValueFontSize === 'small' || vizKpiValueFontSize === 'medium')
          ? { kpiValueFontSize: vizKpiValueFontSize }
          : {}),
        ...(vizType === 'kpi' && vizKpiComparePriorYear ? { kpiComparePriorYear: true } : {}),
        ...((vizType === 'bar' || vizType === 'line') && vizComparePeriod
          ? { comparePeriod: vizComparePeriod }
          : {}),
        ...((vizType === 'bar' || vizType === 'line') && vizSeriesMetricA && vizSeriesMetricB
          ? {
              seriesMetricCompare: {
                seriesA: vizSeriesMetricA,
                seriesB: vizSeriesMetricB,
                ...(vizSeriesMetricCumulative ? { cumulative: true } : {}),
              },
            }
          : {}),
        ...((vizType === 'bar' || vizType === 'line' || vizType === 'table') &&
        vizCustomDateCompareBaseStart &&
        vizCustomDateCompareParam
          ? {
              customDateCompare: {
                baseParamNameStart: vizCustomDateCompareBaseStart,
                // Filtro de rango (paramNameStart/paramNameEnd) o de un solo dia (paramName
                // unico, ver customDateCompareBaseOptions): en este ultimo caso queda igual
                // al de arriba, para que el servidor lo trate como un rango de un dia.
                baseParamNameEnd:
                  filters.find((f) => f.operator === 'between' && f.paramNameStart === vizCustomDateCompareBaseStart)
                    ?.paramNameEnd ||
                  filters.find((f) => f.operator !== 'between' && f.paramName === vizCustomDateCompareBaseStart)
                    ?.paramName ||
                  '',
                compareParamName: vizCustomDateCompareParam,
              },
            }
          : {}),
        // Mismo mecanismo que xField/yField/seriesField: un alias que el servidor resuelve
        // contra tableColumns (normalizeSpec), en vez de que el cliente calcule por su
        // cuenta el alias real que va a tener la columna en el SQL.
        ...((vizType === 'bar' || vizType === 'line') && vizSeriesMetricDivideBy
          ? { divideByField: vizSeriesMetricDivideBy }
          : {}),
        ...(vizShowMetricPercentLine ? { showMetricPercentLine: true } : {}),
        seriesField: seriesRowField || undefined,
        seriesTimeRangeSetId: seriesSetId || undefined,
        seriesTimeRangeSourceField: seriesSourceField || undefined,
        backgroundImageUrl: vizType === 'imageDensity' ? vizBgImageUrl || undefined : undefined,
        ...(vizType === 'intervals'
          ? {
              intervalStartField: vizIntervalStartField || undefined,
              intervalEndField: vizIntervalEndField || undefined,
              intervalSeriesField: vizIntervalSeriesField || undefined,
            }
          : {}),
        ...(vizType === 'imageCarousel'
          ? {
              carouselSensorTypeColumn: vizCarouselSensorTypeField || undefined,
              carouselSensorIdColumn: vizCarouselSensorIdField || undefined,
              carouselContentColumn: vizCarouselContentField || undefined,
              carouselInZoneColumn: vizCarouselInZoneField || undefined,
              carouselSingleImageMode: vizCarouselSingleImageMode || undefined,
              carouselShowOnlyInZone: vizCarouselShowOnlyInZone || undefined,
              carouselCropMode: vizCarouselCropMode || undefined,
              carouselCropClassFilter: vizCarouselCropClassFilter || undefined,
            }
          : {}),
        ...(vizType === 'geoHeatmap'
          ? {
              densityWeightColumn: vizDensityWeightCol || undefined,
              geoHeatmapRadius: vizGeoHeatmapRadius,
              geoHeatmapBlur: vizGeoHeatmapBlur,
              geoHeatmapUseValue: Boolean(vizDensityWeightCol),
              densityShowLegend: vizDensityShowLegend,
              geoFitBoundsOnLoad: true,
              densityTooltipEnabled: vizDensityTooltipEnabled || undefined,
              densityTooltipColumns:
                vizDensityTooltipColumns?.length > 0 ? vizDensityTooltipColumns : undefined,
            }
          : {}),
        ...(vizType === 'imageDensity'
          ? {
              densityRenderMode: vizDensityRenderMode,
              densityHeatmapStyle: vizDensityHeatmapStyle,
              densityIntensity: vizDensityIntensity,
              densityOverlayOpacity: vizDensityOverlayOpacity,
              densityBlur: vizDensityBlur,
              densityPointSize: vizDensityPointSize,
              densitySeparation: vizDensitySeparation,
              densityBackgroundDim: vizDensityBackgroundDim,
              densityGroupByColumn: vizDensityGroupBy || undefined,
              densityWeightColumn: vizDensityWeightCol || undefined,
              densityShowLegend: vizDensityShowLegend,
              densityPointRadius: vizDensityPointRadius,
              densitySelectedGroups: vizDensitySelectedGroups,
              densityClusterEnabled: vizDensityClusterEnabled || undefined,
              densityClusterRadiusPx: vizDensityClusterRadiusPx,
              densityClusterMerge: vizDensityClusterMerge,
              densityClusterShowCount: vizDensityClusterShowCount || undefined,
              densityClusterColorByCount: vizDensityClusterColorByCount || undefined,
              densityTooltipEnabled: vizDensityTooltipEnabled || undefined,
              densityTooltipColumns:
                vizDensityTooltipColumns?.length > 0 ? vizDensityTooltipColumns : undefined,
              densityTooltipDetail: vizDensityTooltipDetail || undefined,
              densityPositionMode: vizDensityPositionMode === 'manual' ? 'manual' : undefined,
              densityPositionKeyColumn: vizDensityPositionKeyColumn || undefined,
              densityManualPositions:
                vizDensityManualPositions && Object.keys(vizDensityManualPositions).length
                  ? vizDensityManualPositions
                  : undefined,
              densityMetricColumns:
                vizDensityMetricColumns?.length > 0 ? vizDensityMetricColumns : undefined,
              densityShowMarkerLabels: vizDensityShowMarkerLabels || undefined,
              densityMarkerEnumColumn: vizDensityMarkerEnumColumn || undefined,
              densityMarkerShape:
                vizDensityMarkerShape && vizDensityMarkerShape !== 'circle'
                  ? vizDensityMarkerShape
                  : undefined,
              densityMarkerValueColumn: vizDensityMarkerValueColumn || undefined,
              densityMarkerValueAggregation:
                vizDensityMarkerValueAggregation && vizDensityMarkerValueAggregation !== 'first'
                  ? vizDensityMarkerValueAggregation
                  : undefined,
              densityMarkerLabelColumn: vizDensityMarkerLabelColumn || undefined,
              densityMarkerFill: vizDensityMarkerFill === 'none' ? 'none' : undefined,
              densityMarkerFit: vizDensityMarkerFit === 'marker' ? 'marker' : undefined,
              densityMarkerSize:
                vizDensityMarkerSize === 'small' || vizDensityMarkerSize === 'large'
                  ? vizDensityMarkerSize
                  : undefined,
              densityMarkerColor:
                vizDensityMarkerColor && vizDensityMarkerColor !== '#ffffff'
                  ? vizDensityMarkerColor
                  : undefined,
              densityImageWidth: vizDensityImageWidth,
              densityImageHeight: vizDensityImageHeight,
              densityPositionColumnsCommitted: vizDensityPositionColumnsCommitted || undefined,
            }
          : {}),
        ...(vizType !== 'imageDensity' &&
        (vizDensityPositionColumnsCommitted || isNonEmptyManualPositions(vizDensityManualPositions))
          ? {
              densityPositionMode: vizDensityPositionMode === 'manual' ? 'manual' : undefined,
              densityPositionKeyColumn: vizDensityPositionKeyColumn || undefined,
              densityManualPositions:
                vizDensityManualPositions && Object.keys(vizDensityManualPositions).length
                  ? vizDensityManualPositions
                  : undefined,
              densityPositionColumnsCommitted: vizDensityPositionColumnsCommitted || undefined,
            }
          : {}),
        stacked: vizType === 'bar' && vizStacked ? true : undefined,
        horizontal: vizType === 'bar' && vizHorizontal ? true : undefined,
        barShowValueLabels: vizType === 'bar' && vizBarShowValueLabels ? true : undefined,
        lineShowValueLabels: vizType === 'line' && vizLineShowValueLabels ? true : undefined,
        lineFillMissingAsZero: vizType === 'line' ? vizLineFillMissingAsZero : undefined,
        fillTimeAxisGaps:
          vizType === 'line' || vizType === 'bar' ? vizFillTimeAxisGaps || undefined : undefined,
        lineInterpolation: vizType === 'line' ? vizLineInterpolation : undefined,
        ...(vizType === 'pie'
          ? {
              pieShowMode: pieShowModeFromLabels(vizPieShowValueLabel, vizPieShowPercentLabel),
              pieShowValueLabel: vizPieShowValueLabel,
              pieShowPercentLabel: vizPieShowPercentLabel,
              pieLabelPosition: vizPieLabelPosition,
              pieShowNameLabel: vizPieShowNameLabel,
              legendPosition: vizLegendPosition,
              pieLegendFontSize: vizPieLegendFontSize,
            }
          : {}),
        ...(vizType === 'bar' || vizType === 'line'
          ? {
              xAxisTitle: vizXAxisTitle.trim() || undefined,
              yAxisTitle: vizYAxisTitle.trim() || undefined,
            }
          : {}),
        ...(vizType === 'tablePlus'
          ? {
              tablePlusApplyTimezone,
              tablePlusGroupColumns,
              ...(Array.isArray(tablePlusVisibleColumns) ? { tablePlusVisibleColumns } : {}),
            }
          : {}),
        ...(Array.isArray(tableCalculatedColumns) && tableCalculatedColumns.length
          ? { tableCalculatedColumns }
          : {}),
      },
    };
  }, [
    columns,
    filters,
    baseTableId,
    specRows,
    specValues,
    specTableColumns,
    rowLimit,
    orderByKind,
    orderDirection,
    orderColumnIndex,
    vizChartType,
    tablePlusApplyTimezone,
    tablePlusGroupColumns,
    tablePlusVisibleColumns,
    tableCalculatedColumns,
    vizXField,
    vizYField,
    vizKpiAggregation,
    vizNarrativeTemplate,
    vizKpiValueFontSize,
    vizKpiComparePriorYear,
    vizComparePeriod,
    vizSeriesMetricA,
    vizSeriesMetricB,
    vizSeriesMetricDivideBy,
    vizSeriesMetricCumulative,
    vizShowMetricPercentLine,
    vizCustomDateCompareBaseStart,
    vizCustomDateCompareParam,
    vizSeriesField,
    vizSeriesTimeRangeColumn,
    vizBgImageUrl,
    vizDensityRenderMode,
    vizDensityHeatmapStyle,
    vizDensityIntensity,
    vizDensityOverlayOpacity,
    vizDensityBlur,
    vizGeoHeatmapRadius,
    vizGeoHeatmapBlur,
    vizDensityPointSize,
    vizDensitySeparation,
    vizDensityBackgroundDim,
    vizDensityGroupBy,
    vizDensityWeightCol,
    vizDensityShowLegend,
    vizDensityPointRadius,
    vizDensitySelectedGroups,
    vizDensityClusterEnabled,
    vizDensityClusterRadiusPx,
    vizDensityClusterMerge,
    vizDensityClusterShowCount,
    vizDensityClusterColorByCount,
    vizDensityTooltipEnabled,
    vizDensityTooltipColumns,
    vizDensityTooltipDetail,
    vizIntervalStartField,
    vizIntervalEndField,
    vizIntervalSeriesField,
    vizCarouselSensorTypeField,
    vizCarouselSensorIdField,
    vizCarouselContentField,
    vizCarouselInZoneField,
    vizCarouselSingleImageMode,
    vizCarouselShowOnlyInZone,
    vizCarouselCropMode,
    vizCarouselCropClassFilter,
    vizDensityPositionMode,
    vizDensityPositionKeyColumn,
    vizDensityManualPositions,
    vizDensityMetricColumns,
    vizDensityShowMarkerLabels,
    vizDensityMarkerEnumColumn,
    vizDensityMarkerShape,
    vizDensityMarkerValueColumn,
    vizDensityMarkerValueAggregation,
    vizDensityMarkerLabelColumn,
    vizDensityMarkerFill, vizDensityMarkerFit, vizDensityMarkerSize, vizDensityMarkerColor,
    vizDensityImageWidth,
    vizDensityImageHeight,
    vizDensityPositionColumnsCommitted,
    vizStacked,
    vizHorizontal,
    vizBarShowValueLabels,
    vizLineShowValueLabels,
    vizLineFillMissingAsZero,
    vizFillTimeAxisGaps,
    vizLineInterpolation,
    vizPieShowValueLabel,
    vizPieShowPercentLabel,
    vizPieLabelPosition,
    vizPieShowNameLabel,
    vizLegendPosition,
    vizLegendDisplay,
    vizXAxisTitle,
    vizYAxisTitle,
    episodeAnalysis,
    spatialZoneAnalysis,
    chartFeed,
    postProcessOrder,
    episodeGroupCols,
    spatialZoneGroupCols,
    vizSeriesTimeRangeColumn,
    context,
  ]);

  useEffect(() => {
    buildSpecRef.current = buildSpec;
  }, [buildSpec]);

  const previewDataKey = useMemo(
    () =>
      JSON.stringify({
        columns,
        filters,
        baseTableId,
        rowLimit,
        orderByKind,
        orderDirection,
        orderColumnIndex,
        vizChartType,
        vizXField,
        vizYField,
        vizKpiAggregation,
        vizKpiValueFontSize,
        vizKpiComparePriorYear,
        vizComparePeriod,
        vizSeriesMetricA,
        vizSeriesMetricB,
        vizSeriesMetricDivideBy,
        vizSeriesMetricCumulative,
        vizShowMetricPercentLine,
        vizCustomDateCompareBaseStart,
        vizCustomDateCompareParam,
        vizSeriesField,
        vizSeriesTimeRangeColumn,
        vizStacked,
        vizHorizontal,
        vizBgImageUrl,
        vizDensityRenderMode,
        vizDensityHeatmapStyle,
        vizDensityIntensity,
        vizDensityOverlayOpacity,
        vizDensityBlur,
        vizDensityPointSize,
        vizDensitySeparation,
        vizDensityBackgroundDim,
        vizDensityGroupBy,
        vizDensityWeightCol,
        vizDensityPointRadius,
        vizDensitySelectedGroups,
        vizDensityClusterEnabled,
        vizDensityClusterRadiusPx,
        vizDensityClusterMerge,
        vizDensityClusterShowCount,
        vizDensityClusterColorByCount,
        vizDensityTooltipEnabled,
        vizDensityTooltipColumns,
        episodeAnalysis,
        spatialZoneAnalysis,
        chartFeed,
        postProcessOrder,
      }),
    [
      columns,
      filters,
      baseTableId,
      rowLimit,
      orderByKind,
      orderDirection,
      orderColumnIndex,
      vizChartType,
      vizXField,
      vizYField,
      vizKpiAggregation,
      vizKpiValueFontSize,
      vizKpiComparePriorYear,
      vizComparePeriod,
      vizSeriesMetricA,
      vizSeriesMetricB,
      vizSeriesMetricDivideBy,
      vizSeriesMetricCumulative,
      vizShowMetricPercentLine,
      vizCustomDateCompareBaseStart,
      vizCustomDateCompareParam,
      vizSeriesField,
      vizSeriesTimeRangeColumn,
      vizStacked,
      vizHorizontal,
      vizBgImageUrl,
      vizDensityRenderMode,
      vizDensityHeatmapStyle,
      vizDensityIntensity,
      vizDensityOverlayOpacity,
      vizDensityBlur,
      vizDensityPointSize,
      vizDensitySeparation,
      vizDensityBackgroundDim,
      vizDensityGroupBy,
      vizDensityWeightCol,
      vizDensityPointRadius,
      vizDensitySelectedGroups,
      vizDensityClusterEnabled,
      vizDensityClusterRadiusPx,
      vizDensityClusterMerge,
      vizDensityClusterShowCount,
      vizDensityClusterColorByCount,
      vizDensityTooltipEnabled,
      vizDensityTooltipColumns,
      episodeAnalysis,
      spatialZoneAnalysis,
      chartFeed,
      postProcessOrder,
    ]
  );

  const refreshPreviewData = useCallback(() => {
    setPreviewRefreshKey((k) => k + 1);
  }, []);

  const chartSpecReady = useMemo(() => {
    if (vizChartType === 'button') return isSafeButtonUrl(vizButtonUrl);
    if (vizChartType === 'weather') {
      return Number.isFinite(parseFloat(vizWeatherLat)) && Number.isFinite(parseFloat(vizWeatherLon));
    }
    if (vizChartType === 'calendar') return true;
    if (vizChartType === 'narrative') {
      const hasText = Boolean(vizNarrativeTemplate?.trim());
      if (!hasText) return false;
      // Si la plantilla no tiene ningun marcador {{...}}, es texto fijo puro (ej. una nota o
      // instrucciones): no depende de ninguna columna de datos, no hace falta Fila/Valor. Pero
      // tampoco depende de ningun filtro: si hay uno configurado, no cambiaria el resultado, asi
      // que se bloquea publicar en ese estado (ver aviso en narrativeOptionsPanel) en vez de dar
      // la falsa impresion de que algo se esta filtrando.
      if (!hasNarrativePlaceholder(vizNarrativeTemplate)) return !filters.some(isFilterActive);
      return columns.some((c) => c.tableId && c.column);
    }
    return columns.some((c) => c.tableId && c.column);
  }, [vizChartType, vizButtonUrl, vizWeatherLat, vizWeatherLon, vizNarrativeTemplate, columns, filters]);

  // Botón/Clima/Calendario, y Texto narrativo cuando es texto fijo sin {{marcadores}}: no
  // dependen de ninguna consulta ni del modelo de datos activo. Se recalcula en cada render
  // (chequeo barato) en vez de useMemo para no tener que mantener su propio arreglo de
  // dependencias en cada uno de los puntos del editor que lo usan. Solo para decisiones
  // puntuales (publicar, deshabilitar "Duplicar"): como depende del texto que se está
  // escribiendo, NO se usa para mostrar/ocultar secciones del editor (eso cambiaría de
  // estructura en cada tecleo y rompería el foco del campo de texto a medio escribir). Para
  // eso se usa isStaticQuerylessType más abajo, que nunca es true para narrativo.
  const chartTypeIsQueryless = isQuerylessChartType(vizChartType, { narrativeTemplate: vizNarrativeTemplate });
  // Secciones del editor (Filtros activos, pestañas Tabla/Gráfico, barra de SQL/Episodios/
  // Zonas): solo Botón/Clima/Calendario las ocultan. Texto narrativo siempre las conserva,
  // tenga o no {{marcadores}} en este momento, porque puede volver a necesitarlas con solo
  // agregar un marcador y porque son las mismas columnas/filtros que ya tenía configurados.
  const isStaticQuerylessType = QUERYLESS_CHART_TYPES.has(vizChartType);

  const hasUnnestColumn = useMemo(() => hasUnnestDatasetColumn(columns), [columns]);

  const episodesChartActive = chartFeed === 'episodes' && episodesTableActive;
  const spatialZonesChartActive = chartFeed === 'zones' && zonesTableActive;
  const postProcessChartActive = episodesTableActive || zonesTableActive;
  const needsPipelinePreview = postProcessChartActive;
  /** Líneas/barras/torta/KPI sobre Serie SQL necesitan SQL agregado (no la preview de tabla). */
  const needsChartSqlPreview =
    needsPipelinePreview ||
    (vizChartType !== 'table' && vizChartType !== 'tablePlus' && !isRawRowsChartType(vizChartType));
  const pipelineTabSteps = useMemo(
    () => orderedPipelineSteps(postProcessOrder, episodesTableActive, zonesTableActive),
    [postProcessOrder, episodesTableActive, zonesTableActive]
  );

  const chartFeedDurationFormats = useMemo(() => {
    if (chartFeed !== 'episodes' || !episodesTableActive) return undefined;
    const map = {};
    for (const f of episodeOutputFields(episodeAnalysis, episodeGroupCols)) {
      if (f.duration || isDurationFieldBase(f.base)) {
        map[f.value] = resolveFieldDurationFormat(episodeAnalysis, f.base);
      }
    }
    return Object.keys(map).length ? map : undefined;
  }, [chartFeed, episodesTableActive, episodeAnalysis, episodeGroupCols]);

  const episodeSeriesReady = useMemo(() => {
    if (episodeInputSource.source === 'zones') {
      return resolveEpisodeSeriesColumnsFromOutputFields(episodeInputFields, episodeAnalysis || {}).ready;
    }
    return episodeConfigReady(columns, episodeAnalysis, context);
  }, [columns, episodeAnalysis, context, episodeInputSource, episodeInputFields]);

  const handlePostProcessOrderChange = useCallback(
    (nextOrder) => {
      const normalized = normalizePostProcessOrder(nextOrder);
      setPostProcessOrder(normalized);
      const feed = resolveChartFeedFromPipeline(
        normalized,
        Boolean(episodeAnalysis?.enabled),
        Boolean(spatialZoneAnalysis?.enabled)
      );
      if (feed === 'episodes') setPreviewTab('episodes');
      else if (feed === 'zones') setPreviewTab('zones');
    },
    [episodeAnalysis?.enabled, spatialZoneAnalysis?.enabled]
  );

  /** Serie de referencia para el calibrador (lee la tabla fuente del paso episodios). */
  const fetchEpisodeSeries = useCallback(async (episodeCfg) => {
    const headers = getAuthHeaders?.();
    if (!headers || !buildSpecRef.current) return { groups: [], groupColumns: [], rowCount: 0 };

    const cfg = episodeCfg || episodeAnalysis || {};
    const fromZones = episodeInputSource.source === 'zones';
    const resolved = fromZones
      ? resolveEpisodeSeriesColumnsFromOutputFields(episodeInputFields, cfg)
      : resolveEpisodeSeriesColumns(columns, cfg, context);
    const groupCols = fromZones
      ? resolveEpisodeGroupColsFromOutputFields(episodeInputFields, cfg)
      : resolveEpisodeGroupCols(columns, cfg);

    if (!resolved.ready) {
      return {
        groups: [],
        groupColumns: groupCols,
        rowCount: 0,
        hint: fromZones
          ? 'Elige columna de tiempo y medida de la tabla de zonas.'
          : 'Elige columna de tiempo y medida en el diálogo de episodios.',
      };
    }

    let rows = [];
    if (fromZones) {
      const spec = specForPreviewApi(buildSpecRef.current({ queryChartType: 'table' }));
      spec.limit = 5000;
      const res = await chartBuilderPreviewMutation.mutateAsync({ connectionId, database, spec, timezone, headers });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Error al cargar la tabla de zonas');
      }
      const zoneDs = res.data.datasets?.zones;
      if (zoneDs?.error) {
        throw new Error(zoneDs.error);
      }
      rows = zoneDs?.data || [];
    } else {
      const spec = specForPreviewApi({ ...buildSpecRef.current({ queryChartType: 'table' }) });
      delete spec.episodeAnalysis;
      delete spec.spatialZoneAnalysis;
      spec.limit = 5000;
      const res = await chartBuilderPreviewMutation.mutateAsync({ connectionId, database, spec, timezone, headers });
      if (!res.data?.success) {
        throw new Error(res.data?.error || 'Error al cargar la serie de referencia');
      }
      rows = res.data.data || [];
    }

    if (!rows.length) {
      return {
        groups: [],
        groupColumns: groupCols,
        rowCount: 0,
        hint: fromZones
          ? 'La tabla de zonas no devolvió filas. Revisa zonas, filtros y límite.'
          : 'La vista previa no devolvió filas. Revisa filtros, rango de fechas y límite de filas.',
      };
    }

    const findKey = (row, alias) => {
      const lower = String(alias).toLowerCase();
      return Object.keys(row).find((k) => k.toLowerCase() === lower) || null;
    };
    const timeAlias = resolved.timeColumn;
    const valueAlias = resolved.valueColumn;
    const tKey = findKey(rows[0], timeAlias);
    const vKey = findKey(rows[0], valueAlias);
    if (!tKey || !vKey) {
      return {
        groups: [],
        groupColumns: groupCols,
        rowCount: rows.length,
        hint: `No se encontraron «${timeAlias}» y «${valueAlias}» en ${fromZones ? 'zonas' : 'SQL'} (${Object.keys(rows[0]).join(', ')}).`,
      };
    }

    const partitionCol = String(cfg.groupColumn || '').trim();
    const groupKeyParts = [];
    if (partitionCol) {
      const match = groupCols.find((g) => String(g.base).toLowerCase() === partitionCol.toLowerCase());
      const key = match?.synthetic
        ? findKey(rows[0], match.value || match.base)
        : match
          ? findKey(rows[0], episodeColumnAlias(columns[match.index], match.index))
          : findKey(rows[0], partitionCol);
      groupKeyParts.push({ base: match?.base || partitionCol, key });
    }

    const groupsMap = new Map();
    let parsedPoints = 0;
    for (const row of rows) {
      const ms = parseSeriesTimestamp(row[tKey]);
      const value = Number(row[vKey]);
      if (ms == null || !Number.isFinite(value)) continue;
      parsedPoints += 1;

      const groupValues = {};
      const keyParts = [];
      for (const g of groupKeyParts) {
        const val = g.key ? row[g.key] : null;
        groupValues[g.base] = val;
        keyParts.push(String(val ?? ''));
      }
      const key = keyParts.join('\u0001');
      let entry = groupsMap.get(key);
      if (!entry) {
        entry = { key, groupValues, points: [] };
        groupsMap.set(key, entry);
      }
      entry.points.push({ ms, value });
    }

    const groups = [...groupsMap.values()].map((entry) => {
      entry.points.sort((a, b) => a.ms - b.ms);
      const label =
        episodeGroupCols.length > 0
          ? episodeGroupCols.map((g) => String(entry.groupValues[g.base] ?? '—')).join(' · ')
          : 'Serie completa';
      return { ...entry, label };
    });
    groups.sort((a, b) => a.label.localeCompare(b.label));
    if (!groups.length || groups.every((g) => !g.points?.length)) {
      return {
        groups: [],
        groupColumns: groupCols,
        rowCount: rows.length,
        hint:
          parsedPoints === 0 && rows.length > 0
            ? `Hay ${rows.length} filas pero no se pudieron interpretar fecha/medida. Revisa formato de «${timeAlias}» / «${valueAlias}».`
            : undefined,
      };
    }
    return { groups, groupColumns: groupCols, rowCount: rows.length, inputSource: episodeInputSource };
  }, [
    getAuthHeaders,
    connectionId,
    database,
    timezone,
    columns,
    context,
    episodeAnalysis,
    episodeInputSource,
    episodeInputFields,
  ]);

  const patchEpisodeAnalysis = useCallback((patch) => {
    setEpisodeAnalysis((prev) => ({
      ...(prev || DEFAULT_EPISODE_CONFIG),
      ...patch,
      resultOps: {
        ...(prev?.resultOps || DEFAULT_EPISODE_CONFIG.resultOps),
        ...(patch?.resultOps || {}),
      },
      columnLabels: {
        ...(prev?.columnLabels || {}),
        ...(patch?.columnLabels || {}),
      },
    }));
  }, []);

  const patchSpatialZoneAnalysis = useCallback((patch) => {
    setSpatialZoneAnalysis((prev) => ({
      ...(prev || DEFAULT_SPATIAL_ZONE_CONFIG),
      ...patch,
      resultOps: {
        ...(prev?.resultOps || DEFAULT_SPATIAL_ZONE_CONFIG.resultOps),
        ...(patch?.resultOps || {}),
      },
      columnLabels: {
        ...(prev?.columnLabels || {}),
        ...(patch?.columnLabels || {}),
      },
    }));
  }, []);

  const handleSpatialZoneAnalysisChange = useCallback(
    (next) => {
      setSpatialZoneAnalysis(next);
      if (next?.enabled) {
        const feed = resolveChartFeedFromPipeline(
          postProcessOrder,
          Boolean(episodeAnalysis?.enabled),
          true
        );
        if (feed === 'zones') setPreviewTab('zones');
      } else {
        setPreviewTab((tab) => (tab === 'zones' ? 'table' : tab));
      }
    },
    [postProcessOrder, episodeAnalysis?.enabled]
  );

  const heatmapFieldsEnabled =
    vizDensityPositionColumnsCommitted || isNonEmptyManualPositions(vizDensityManualPositions);

  const resultFieldOptions = useMemo(() => {
    if (spatialZonesChartActive) {
      return withHeatmapPositionFieldOptions(
        spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols).map((f) => ({
          value: f.value,
          label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value,
        })),
        heatmapFieldsEnabled
      );
    }
    // Con episodios activos, el gráfico se alimenta de la tabla de episodios
    if (episodesChartActive) {
      return withHeatmapPositionFieldOptions(
        episodeOutputFields(episodeAnalysis, episodeGroupCols).map((f) => ({
          value: f.value,
          label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value,
        })),
        heatmapFieldsEnabled
      );
    }
    const raw = collectResultFieldEntries(columns);
    const labelCounts = new Map();
    raw.forEach(({ column: c, objectKvField }) => {
      const base = objectKvField
        ? objectKvField.alias ||
          (objectKvField.path === '$key' || objectKvField.path === 'key' ? 'Clave' : 'Valor')
        : headerLabel(c, context);
      labelCounts.set(base, (labelCounts.get(base) || 0) + 1);
    });
    return withHeatmapPositionFieldOptions(
      withCalculatedFieldOptions(
        raw.map(({ value, column: c, objectKvField, virtualKind }) => {
          const base = objectKvField
            ? objectKvField.alias ||
              (objectKvField.path === '$key' || objectKvField.path === 'key' ? 'Clave' : 'Valor')
            : headerLabel(c, context);
          const duplicated = (labelCounts.get(base) || 0) > 1;
          const roleTag =
            virtualKind === 'value' ||
            c.role === 'value' ||
            c.jsonMode === 'arrayCount' ||
            c.jsonMode === 'arraySum'
              ? 'medida'
              : 'eje';
          return {
            value,
            label: duplicated ? `${base} (${roleTag})` : base,
          };
        }),
        tableCalculatedColumns
      ),
      heatmapFieldsEnabled
    );
  }, [columns, context, episodesChartActive, spatialZonesChartActive, episodeAnalysis, spatialZoneAnalysis, episodeGroupCols, spatialZoneGroupCols, heatmapFieldsEnabled, tableCalculatedColumns]);

  /** Solo columnas "Valor" (medida), para elegir la columna divisor del cálculo entre
   * series (ej. una columna traída con "Seguir referencia"), no un eje ni una serie. */
  const valueColumnFieldOptions = useMemo(() => {
    const raw = collectResultFieldEntries(columns).filter(
      ({ column: c, objectKvField }) =>
        !objectKvField && (c.role === 'value' || c.jsonMode === 'arrayCount' || c.jsonMode === 'arraySum')
    );
    return raw.map(({ value, column: c }) => ({ value, label: headerLabel(c, context) }));
  }, [columns, context]);

  // Comparacion por segunda fecha: opciones para los selectores "Fecha base" y
  // "Fecha Compara", derivadas de los filtros dinamicos ya configurados en este grafico
  // (no de columns, a diferencia de los useMemo de arriba). "Fecha base" acepta tanto un
  // filtro de rango (paramNameStart/paramNameEnd, ej. Desde/Hasta) como uno de un solo
  // dia (un unico paramName, ej. "Fecha"): en ese caso paramNameStart y paramNameEnd
  // quedan iguales, asi que sustituir "hasta" termina siendo lo mismo que sustituir
  // "desde" (duracion de un dia), sin pedirle a quien mira el reporte dos controles
  // para elegir un solo dia.
  const customDateCompareBaseOptions = useMemo(() => {
    const isDateFilter = (f) => resolveFilterLogicalTypeForFilter(context, f) === 'datetime';
    const rangeOptions = filters
      .filter(
        (f) =>
          f.operator === 'between' &&
          (f.dynamic || f.layer === 'selection') &&
          f.paramNameStart &&
          f.paramNameEnd &&
          isDateFilter(f)
      )
      .map((f) => ({
        key: f.paramNameStart,
        paramNameStart: f.paramNameStart,
        paramNameEnd: f.paramNameEnd,
        label: `@${f.paramNameStart} / @${f.paramNameEnd}`,
      }));
    const singleDayOptions = filters
      .filter(
        (f) =>
          f.operator !== 'between' &&
          (f.dynamic || f.layer === 'selection') &&
          f.paramName &&
          f.excludeFromWhere !== true &&
          isDateFilter(f)
      )
      .map((f) => ({
        key: f.paramName,
        paramNameStart: f.paramName,
        paramNameEnd: f.paramName,
        label: `@${f.paramName} (día único)`,
      }));
    return [...rangeOptions, ...singleDayOptions];
  }, [filters, context]);
  const customDateCompareParamOptions = useMemo(
    () =>
      filters.filter(
        (f) =>
          f.excludeFromWhere === true &&
          (f.dynamic || f.layer === 'selection') &&
          f.paramName &&
          resolveFilterLogicalTypeForFilter(context, f) === 'datetime'
      ),
    [filters, context]
  );

  const pieLegendFieldOptions = useMemo(() => {
    if (spatialZonesChartActive) {
      return spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols)
        .filter((f) => !f.numeric)
        .map((f) => ({ value: f.value, label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value }));
    }
    if (episodesChartActive) {
      return episodeOutputFields(episodeAnalysis, episodeGroupCols)
        .filter((f) => !f.numeric)
        .map((f) => ({ value: f.value, label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value }));
    }
    const raw = collectResultFieldEntries(columns).filter(
      ({ column: c, virtualKind, objectKvField }) =>
        virtualKind === 'row' ||
        (!objectKvField &&
          c.role !== 'value' &&
          c.jsonMode !== 'arrayCount' &&
          c.jsonMode !== 'arraySum')
    );
    const labelCounts = new Map();
    raw.forEach(({ column: c, objectKvField }) => {
      const base = objectKvField
        ? objectKvField.alias ||
          (objectKvField.path === '$key' || objectKvField.path === 'key' ? 'Clave' : 'Valor')
        : headerLabel(c, context);
      labelCounts.set(base, (labelCounts.get(base) || 0) + 1);
    });
    return withCalculatedFieldOptions(
      raw.map(({ value, column: c, objectKvField }) => {
        const base = objectKvField
          ? objectKvField.alias ||
            (objectKvField.path === '$key' || objectKvField.path === 'key' ? 'Clave' : 'Valor')
          : headerLabel(c, context);
        const duplicated = (labelCounts.get(base) || 0) > 1;
        return {
          value,
          label: duplicated ? `${base} (eje)` : base,
        };
      }),
      tableCalculatedColumns
    );
  }, [columns, context, episodesChartActive, spatialZonesChartActive, episodeAnalysis, spatialZoneAnalysis, episodeGroupCols, spatialZoneGroupCols, tableCalculatedColumns]);

  const pieValueFieldOptions = useMemo(() => {
    if (spatialZonesChartActive) {
      return spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols)
        .filter((f) => f.numeric)
        .map((f) => ({ value: f.value, label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value }));
    }
    if (episodesChartActive) {
      return episodeOutputFields(episodeAnalysis, episodeGroupCols)
        .filter((f) => f.numeric)
        .map((f) => ({ value: f.value, label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value }));
    }
    const raw = collectResultFieldEntries(columns).filter(
      ({ column: c, virtualKind, objectKvField }) =>
        virtualKind === 'value' ||
        (!objectKvField &&
          (c.role === 'value' || c.jsonMode === 'arrayCount' || c.jsonMode === 'arraySum'))
    );
    const labelCounts = new Map();
    raw.forEach(({ column: c, objectKvField }) => {
      const base = objectKvField
        ? objectKvField.alias ||
          (objectKvField.path === '$value' || objectKvField.path === 'value' ? 'Valor' : 'Clave')
        : headerLabel(c, context);
      labelCounts.set(base, (labelCounts.get(base) || 0) + 1);
    });
    return withCalculatedFieldOptions(
      raw.map(({ value, column: c, objectKvField }) => {
        const base = objectKvField
          ? objectKvField.alias ||
            (objectKvField.path === '$key' || objectKvField.path === 'key' ? 'Clave' : 'Valor')
          : headerLabel(c, context);
        const duplicated = (labelCounts.get(base) || 0) > 1;
        return {
          value,
          label: duplicated ? `${base} (medida)` : base,
        };
      }),
      normalizeCalculatedColumns(tableCalculatedColumns).filter((def) => def.op !== 'concat')
    );
  }, [columns, context, episodesChartActive, spatialZonesChartActive, episodeAnalysis, spatialZoneAnalysis, episodeGroupCols, spatialZoneGroupCols, tableCalculatedColumns]);

  const seriesFieldOptions = useMemo(() => {
    if (spatialZonesChartActive) {
      return spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols)
        .filter((f) => !f.numeric)
        .map((f) => ({
          value: f.value,
          label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value,
        }));
    }
    if (!episodesChartActive) return resultFieldOptions;
    return episodeOutputFields(episodeAnalysis, episodeGroupCols)
      .filter((f) => !f.numeric)
      .map((f) => ({
        value: f.value,
        label: f.value === f.base ? f.base.replace(/_/g, ' ') : f.value,
      }));
  }, [resultFieldOptions, episodesChartActive, spatialZonesChartActive, episodeAnalysis, spatialZoneAnalysis, episodeGroupCols, spatialZoneGroupCols]);

  const densityAxisFieldOptions = useMemo(() => {
    if (!isRawRowsChartType(vizChartType)) return resultFieldOptions;
    if (chartFeed !== 'base') {
      const sample = chartFeedTableData[0];
      if (sample) {
        return Object.keys(sample)
          .map((name) => ({ value: name, label: name.replace(/_/g, ' ') }))
          .sort((a, b) => a.label.localeCompare(b.label));
      }
      if (spatialZonesChartActive) {
        return spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols).map((f) => ({
          value: f.value,
          label: f.value.replace(/_/g, ' '),
        }));
      }
      if (episodesChartActive) {
        return episodeOutputFields(episodeAnalysis, episodeGroupCols).map((f) => ({
          value: f.value,
          label: f.value.replace(/_/g, ' '),
        }));
      }
    }
    const byValue = new Map();
    resultFieldOptions.forEach((o) => byValue.set(o.value, o));
    previewResultFieldNames(preview).forEach((name) => {
      if (!byValue.has(name)) {
        byValue.set(name, { value: name, label: name });
      }
    });
    return Array.from(byValue.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [
    vizChartType,
    resultFieldOptions,
    preview,
    chartFeed,
    chartFeedTableData,
    spatialZonesChartActive,
    spatialZoneAnalysis,
    spatialZoneGroupCols,
    episodesChartActive,
    episodeAnalysis,
    episodeGroupCols,
  ]);

  const densityAxisOptionLabelByValue = useMemo(() => {
    const map = new Map();
    densityAxisFieldOptions.forEach((o) => map.set(o.value, o.label));
    return map;
  }, [densityAxisFieldOptions]);

  const densityAxisFieldOptionValues = useMemo(
    () => new Set(densityAxisFieldOptions.map((o) => o.value)),
    [densityAxisFieldOptions]
  );

  useEffect(() => {
    if (!isPointDatasetChartType(vizChartType)) return;
    setVizXField((prev) => normalizeImageDensityAxisSelectValue(prev, 'x', densityAxisFieldOptionValues));
    setVizYField((prev) => normalizeImageDensityAxisSelectValue(prev, 'y', densityAxisFieldOptionValues));
  }, [vizChartType, densityAxisFieldOptionValues]);

  useEffect(() => {
    if (!isPointDatasetChartType(vizChartType)) return;
    const sample = chartFeedTableData[0];
    if (!sample) return;
    const available = new Set(Object.keys(sample));
    setVizDensityTooltipColumns((prev) => {
      const next = (prev || []).filter((c) => available.has(c));
      if (next.length === (prev || []).length && next.every((c, i) => c === prev[i])) return prev;
      return next;
    });
  }, [vizChartType, chartFeed, chartFeedTableData]);

  const vizXColumnInfo = useMemo(() => {
    const autoRow = columns.findIndex(
      (c) =>
        c?.tableId &&
        c?.column &&
        c.role !== 'value' &&
        c.jsonMode !== 'arrayCount' &&
        c.jsonMode !== 'arraySum' &&
        c.jsonMode !== 'arrayUnnest' &&
        c.jsonMode !== 'objectKv'
    );
    const plainAliases = resolvePlainColumnAliases(columns);
    const targetKey = vizXField || (autoRow >= 0 ? plainAliases[autoRow] : '');
    if (!targetKey) return null;
    for (let i = 0; i < columns.length; i += 1) {
      const c = columns[i];
      if (!c?.tableId || !c?.column) continue;
      const key = plainAliases[i];
      if (key !== targetKey) continue;
      const meta = getColumnMeta(context, c);
      return {
        index: i,
        column: c,
        meta,
        isDate: meta?.logicalType === 'date' || meta?.logicalType === 'datetime',
      };
    }
    return null;
  }, [vizXField, columns, context]);

  const vizSeriesColumnInfo = useMemo(() => {
    if (!vizSeriesField) return null;
    const plainAliases = resolvePlainColumnAliases(columns);
    for (let i = 0; i < columns.length; i += 1) {
      const c = columns[i];
      if (!c?.tableId || !c?.column) continue;
      const key = plainAliases[i];
      if (key !== vizSeriesField) continue;
      const meta = getColumnMeta(context, c);
      return {
        index: i,
        column: c,
        meta,
        isDate: meta?.logicalType === 'date' || meta?.logicalType === 'datetime',
      };
    }
    return null;
  }, [vizSeriesField, columns, context]);

  const vizSupportsFillTimeAxisGaps = useMemo(() => {
    if (vizChartType !== 'line' && vizChartType !== 'bar') return false;
    const bucket = episodesChartActive
      ? episodeAnalysis?.xDateBucket
      : vizXColumnInfo?.column?.dateBucket;
    if (bucket && bucket !== 'raw') return supportsFillTimeAxisGaps(bucket);
    return Boolean(vizXColumnInfo?.isDate);
  }, [vizChartType, episodesChartActive, episodeAnalysis?.xDateBucket, vizXColumnInfo]);

  const vizYColumnInfo = useMemo(() => {
    if (!vizYField) return null;
    const plainAliases = resolvePlainColumnAliases(columns);
    for (let i = 0; i < columns.length; i += 1) {
      const c = columns[i];
      if (!c?.tableId || !c?.column) continue;
      const key = plainAliases[i];
      if (key !== vizYField) continue;
      return { index: i, column: c };
    }
    return null;
  }, [vizYField, columns]);

  const kpiAggregationOptions = useMemo(
    () =>
      (context?.aggregations || []).filter(
        (a) => a.value !== 'avgDailySum' && a.value !== 'avgPeriodSum'
      ),
    [context]
  );

  const patchColumnAt = useCallback((index, patch) => {
    if (Object.prototype.hasOwnProperty.call(patch, 'objectKvFields')) {
      const current = columns[index];
      const oldFields = current?.objectKvFields?.length
        ? current.objectKvFields
        : DEFAULT_OBJECT_KV_FIELDS;
      const newFields = Array.isArray(patch.objectKvFields)
        ? patch.objectKvFields
        : DEFAULT_OBJECT_KV_FIELDS;
      const rename = new Map();
      newFields.forEach((field, i) => {
        const from = objectKvFieldAlias(oldFields[i] || field, i);
        const to = objectKvFieldAlias(field, i);
        if (from && to && from !== to) rename.set(from, to);
      });
      if (rename.size) {
        const mapField = (value) => (rename.has(value) ? rename.get(value) : value);
        setVizXField(mapField);
        setVizYField(mapField);
        setVizSeriesField(mapField);
        setVizSeriesTimeRangeColumn(mapField);
        setVizDensityGroupBy(mapField);
        setVizDensityWeightCol(mapField);
        setVizDensityMarkerEnumColumn(mapField);
        setVizDensityMarkerValueColumn(mapField);
        setVizDensityMarkerLabelColumn(mapField);
      }
    }
    setColumns((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  }, [columns]);

  /** Formato del eje X cuando el gráfico usa la tabla de episodios. */
  const episodeChartDateFormat = useMemo(() => {
    if (!episodesChartActive) return undefined;
    if (episodeAnalysis?.xDateDisplayFormat && episodeAnalysis.xDateDisplayFormat !== 'auto') {
      return episodeAnalysis.xDateDisplayFormat;
    }
    const timeCol = columns.find(
      (c) =>
        c?.tableId &&
        c?.column &&
        c.role !== 'value' &&
        ['minute', 'hour', 'raw'].includes(c.dateBucket || 'raw') &&
        c.dateDisplayFormat &&
        c.dateDisplayFormat !== 'auto'
    );
    return timeCol?.dateDisplayFormat;
  }, [episodesChartActive, episodeAnalysis?.xDateDisplayFormat, columns]);

  const feedTemporalMaps = useMemo(() => {
    const aliases = resolvePlainColumnAliases(columns);
    return temporalMapsFromColumns(columns, aliases, {
      episodeDateFormat: chartFeed === 'episodes' ? episodeChartDateFormat : undefined,
    });
  }, [columns, chartFeed, episodeChartDateFormat]);

  const patchEpisodeDateFormat = useCallback((dateDisplayFormat) => {
    setEpisodeAnalysis((prev) => ({
      ...(prev || DEFAULT_EPISODE_CONFIG),
      xDateDisplayFormat:
        !dateDisplayFormat || dateDisplayFormat === 'auto' ? undefined : dateDisplayFormat,
    }));
  }, []);

  const runPreview = useCallback(async () => {
    const headers = getAuthHeaders?.();
    if (!headers || !chartSpecReady || !buildSpec || isQuerylessChartType(vizChartType, { narrativeTemplate: vizNarrativeTemplate })) return;
    const requestSeq = ++previewRequestSeq.current;
    setPreviewLoading(true);
    try {
      const spec = specForPreviewApi({ ...buildSpec({ queryChartType: 'table' }) });
      // La tabla del dataset muestra la serie original; el post-proceso
      // se ve en pestañas dedicadas y en la vista previa del gráfico.
      delete spec.episodeAnalysis;
      delete spec.spatialZoneAnalysis;
      const res = await chartBuilderPreviewMutation.mutateAsync({ connectionId, database, spec, timezone, headers });
      if (requestSeq !== previewRequestSeq.current) return;
      setPreview(res.data);
      if (!res.data.success) {
        showError(res.data.error || 'Error en vista previa');
      }
    } catch (err) {
      if (requestSeq !== previewRequestSeq.current) return;
      const errData = err.response?.data;
      setPreview(
        errData?.sql
          ? {
              success: false,
              sql: errData.sql,
              data: [],
              columns: [],
              warnings: errData.warnings || [],
              error: errData.error,
            }
          : null
      );
      showError(errData?.error || err.message || 'Error en vista previa');
    } finally {
      if (requestSeq === previewRequestSeq.current) {
        setPreviewLoading(false);
      }
    }
  }, [getAuthHeaders, chartSpecReady, buildSpec, vizChartType, vizNarrativeTemplate, connectionId, database, timezone, showError]);

  const runChartPreview = useCallback(async () => {
    if (!needsChartSqlPreview || isQuerylessChartType(vizChartType, { narrativeTemplate: vizNarrativeTemplate })) {
      setChartPreview(null);
      setChartPreviewError('');
      setPipelinePreviews({ episodes: null, zones: null });
      return;
    }
    const headers = getAuthHeaders?.();
    if (!headers || !chartSpecReady || !buildSpec) return;
    const requestSeq = ++chartPreviewRequestSeq.current;
    const requestDataKey = previewDataKey;
    setChartPreviewLoading(true);
    setChartPreviewError('');
    setChartPreview(null);
    chartPreviewDataKeyRef.current = null;
    try {
      const spec = specForPreviewApi(buildSpec());
      if (!needsPipelinePreview) {
        delete spec.episodeAnalysis;
        delete spec.spatialZoneAnalysis;
      }
      const res = await chartBuilderPreviewMutation.mutateAsync({ connectionId, database, spec, timezone, headers });
      if (requestSeq !== chartPreviewRequestSeq.current) return;
      chartPreviewDataKeyRef.current = requestDataKey;
      setChartPreview(res.data);
      if (res.data.datasets) {
        setPipelinePreviews({
          episodes: res.data.datasets.episodes || null,
          zones: res.data.datasets.zones || null,
        });
      } else {
        setPipelinePreviews({ episodes: null, zones: null });
      }
      if (!res.data.success) {
        setChartPreviewError(res.data.error || 'Error en vista previa del gráfico');
      } else {
        setChartPreviewError('');
      }
    } catch (err) {
      if (requestSeq !== chartPreviewRequestSeq.current) return;
      const errData = err.response?.data;
      setChartPreview(
        errData?.sql
          ? {
              success: false,
              sql: errData.sql,
              data: [],
              columns: [],
              warnings: errData.warnings || [],
              error: errData.error,
            }
          : null
      );
      setPipelinePreviews({ episodes: null, zones: null });
      setChartPreviewError(errData?.error || err.message || 'Error en vista previa del gráfico');
    } finally {
      if (requestSeq === chartPreviewRequestSeq.current) {
        setChartPreviewLoading(false);
      }
    }
  }, [
    getAuthHeaders,
    chartSpecReady,
    buildSpec,
    vizChartType,
    vizNarrativeTemplate,
    connectionId,
    database,
    timezone,
    needsChartSqlPreview,
    needsPipelinePreview,
    previewDataKey,
  ]);

  useEffect(() => {
    if (!open || !chartSpecReady || loading || isQuerylessChartType(vizChartType, { narrativeTemplate: vizNarrativeTemplate })) return undefined;
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(() => runPreview(), 700);
    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current);
    };
  }, [open, chartSpecReady, loading, vizChartType, vizNarrativeTemplate, previewDataKey, previewRefreshKey, runPreview]);

  useEffect(() => {
    if (!open || !chartSpecReady || loading || isQuerylessChartType(vizChartType, { narrativeTemplate: vizNarrativeTemplate })) return undefined;
    if (!needsChartSqlPreview) {
      chartPreviewDataKeyRef.current = null;
      setChartPreview(null);
      setChartPreviewError('');
      setPipelinePreviews({ episodes: null, zones: null });
      return undefined;
    }
    chartPreviewDataKeyRef.current = null;
    setChartPreview(null);
    setChartPreviewError('');
    if (chartPreviewTimer.current) clearTimeout(chartPreviewTimer.current);
    chartPreviewTimer.current = setTimeout(() => runChartPreview(), 700);
    return () => {
      if (chartPreviewTimer.current) clearTimeout(chartPreviewTimer.current);
    };
  }, [open, chartSpecReady, loading, vizChartType, vizNarrativeTemplate, previewDataKey, previewRefreshKey, needsChartSqlPreview, runChartPreview]);

  const flushPreview = useCallback(() => {
    if (previewTimer.current) clearTimeout(previewTimer.current);
    if (chartPreviewTimer.current) clearTimeout(chartPreviewTimer.current);
    runPreview();
    if (needsChartSqlPreview) {
      runChartPreview();
    }
  }, [runPreview, runChartPreview, needsChartSqlPreview]);

  const handleColumnsChange = (next, meta) => {
    if (meta?.movedFrom != null && meta?.movedTo != null) {
      const { movedFrom, movedTo } = meta;
      setOrderColumnIndex((prev) => {
        if (prev === movedFrom) return movedTo;
        if (movedFrom < prev && movedTo >= prev) return prev - 1;
        if (movedFrom > prev && movedTo <= prev) return prev + 1;
        return prev;
      });
    }
    setColumns(next);
    // Base = primera columna plana (no followed); evita FROM en la tabla destino del follow
    const firstPlain = next.find((c) => c.tableId && !c.joinAlias);
    if (firstPlain?.tableId) setBaseTableId(firstPlain.tableId);
    if (orderColumnIndex >= next.length) {
      setOrderColumnIndex(Math.max(0, next.length - 1));
    }
  };

  const handleSort = (index, direction) => {
    setOrderByKind('column');
    setOrderColumnIndex(index);
    setOrderDirection(direction);
  };

  const handleFilterColumn = (columnIndex, eventTarget, { objectKvPart: filterObjectKvPart } = {}) => {
    const col = columns[columnIndex];
    if (!col?.tableId || !col?.column) return;
    const resolvedObjectKvPart =
      filterObjectKvPart ||
      col.objectKvPart ||
      (col.jsonMode === 'objectKv' ? 'value' : undefined);
    const filterColRef = resolvedObjectKvPart
      ? { ...col, objectKvPart: resolvedObjectKvPart }
      : col;
    const existing = filters.findIndex((f) => sameFilterEntry(f, filterColRef));
    const isObjectKvValueFilter =
      col.jsonMode === 'objectKv' ||
      resolvedObjectKvPart === 'value' ||
      col.objectKvPart === 'value';
    const isResult = isValueColumn(col) && !isObjectKvValueFilter;
    if (existing >= 0) {
      setFilterEditIndex(existing);
    } else {
      const next = emptyFilter({
        tableId: col.tableId,
        column: col.column,
        jsonPath: col.jsonPath || '',
        jsonMode: col.jsonMode || undefined,
        objectKvPart: resolvedObjectKvPart,
        objectValueAgg: col.objectValueAgg || undefined,
        aggregation: col.aggregation || undefined,
        arrayValueField: col.arrayValueField || undefined,
        arrayFilters: col.arrayFilters?.length ? col.arrayFilters : undefined,
        transform: col.transform || undefined,
        coalesceParent: col.coalesceParent || undefined,
        filterScope: isResult ? 'result' : 'row',
        ...columnFollowFields(col),
        layer: 'universe',
      });
      setFilterEditIndex(filters.length);
      setFilters((prev) => [...prev, next]);
    }
    setFilterAnchor(eventTarget || filterBarRef.current);
  };

  const handleFilterDragEnd = (result) => {
    const { destination, source } = result;
    if (!destination) return;
    if (destination.index === source.index) return;

    setFilters((prev) => {
      const next = [...prev];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return next;
    });

    setFilterEditIndex((prevIdx) => {
      if (prevIdx == null) return prevIdx;
      if (prevIdx === source.index) return destination.index;
      if (source.index < prevIdx && destination.index >= prevIdx) return prevIdx - 1;
      if (source.index > prevIdx && destination.index <= prevIdx) return prevIdx + 1;
      return prevIdx;
    });
  };

  const applyLoadedState = useCallback((state, warnings, exampleTitle) => {
    setTitle(state.title || '');
    setBaseTableId(state.baseTableId || '');
    let nextCols = [];
    const validTableColumns = (state.tableColumns || []).filter((c) => c?.tableId && c?.column);
    if (validTableColumns.length) {
      nextCols = validTableColumns.map((c) => {
        const isValue =
          c.role === 'value' ||
          c.jsonMode === 'arrayCount' ||
          c.jsonMode === 'arraySum' ||
          c.jsonMode === 'arrayUnnest' ||
          c.jsonMode === 'objectKv' ||
          c.jsonMode === 'jsonDiff' ||
          (c.aggregation && c.aggregation !== 'none');
        // Sin esto, una columna "Solo filtro" guardada vuelve a "Fila" al reabrir o
        // duplicar el grafico, aunque el filtro que la acota a un solo valor siga ahi.
        const isFilterOnly = !isValue && c.role === 'filter';
        const role = isValue ? 'value' : isFilterOnly ? 'filter' : 'row';
        return emptyCol({
          ...c,
          role,
          aggregation: isValue ? c.aggregation || 'sum' : 'none',
          dateBucket: c.dateBucket || 'raw',
        });
      });
    } else if (Array.isArray(state.rows) || Array.isArray(state.values)) {
      nextCols = rowsValuesToColumns(state.rows, state.values);
    } else if (state.dimension?.column || state.measure?.column) {
      nextCols = rowsValuesToColumns(
        state.dimension?.column ? [state.dimension] : [],
        state.measure?.column ? [state.measure] : []
      );
    }
    setColumns(nextCols);
    setFilters(normalizeFiltersClientIds(state.filters || []));
    setRowLimit(
      specLimitToRowLimitInput(
        state.rowLimit != null && state.rowLimit !== '' ? state.rowLimit : state.limit
      )
    );
    setOrderByKind(state.orderByKind || state.orderBy || 'column');
    setOrderDirection(state.orderDirection || 'desc');
    setOrderColumnIndex(state.orderColumnIndex || 0);
    setEpisodeAnalysis(state.episodeAnalysis?.enabled ? state.episodeAnalysis : null);
    setSpatialZoneAnalysis(state.spatialZoneAnalysis?.enabled ? state.spatialZoneAnalysis : null);
    const viz = state.visualization || {};
    const epEnabled = Boolean(state.episodeAnalysis?.enabled);
    const znEnabled = Boolean(state.spatialZoneAnalysis?.enabled);
    setPostProcessOrder(
      migratePostProcessOrder(viz.postProcessOrder, epEnabled, znEnabled, viz.chartFeed)
    );
    const idCfg = state.imageDensityConfig || state.advancedConfig?.imageDensityConfig || {};
    setVizChartType(viz.isNarrative ? 'narrative' : viz.chartType || state.chartType || 'table');
    setTablePlusApplyTimezone(viz.tablePlusApplyTimezone !== false);
    setTablePlusGroupColumns(Array.isArray(viz.tablePlusGroupColumns) ? viz.tablePlusGroupColumns : []);
    setTablePlusVisibleColumns(Array.isArray(viz.tablePlusVisibleColumns) ? viz.tablePlusVisibleColumns : null);
    setTableCalculatedColumns(
      Array.isArray(viz.tableCalculatedColumns)
        ? viz.tableCalculatedColumns
        : Array.isArray(state.advancedConfig?.tableCalculatedColumns)
          ? state.advancedConfig.tableCalculatedColumns
          : []
    );
    const loadedChartType = viz.isNarrative ? 'narrative' : viz.chartType || state.chartType || 'table';
    const loadedXField = viz.xField || idCfg.xColumn || '';
    const loadedYField = viz.yField || idCfg.yColumn || '';
    if (loadedChartType === 'imageDensity') {
      setVizXField(loadedXField || IMAGE_DENSITY_AXIS_AUTO_X);
      setVizYField(loadedYField || IMAGE_DENSITY_AXIS_AUTO_Y);
    } else if (loadedChartType === 'geoHeatmap') {
      const geoCfg = state.geoHeatmapConfig || state.advancedConfig?.geoHeatmapConfig || {};
      setVizXField(loadedXField || geoCfg.latColumn || GEO_HEATMAP_AUTO_LAT);
      setVizYField(loadedYField || geoCfg.lngColumn || GEO_HEATMAP_AUTO_LNG);
      setVizGeoHeatmapRadius(
        viz.geoHeatmapRadius != null ? viz.geoHeatmapRadius : geoCfg.heatmapRadius != null ? geoCfg.heatmapRadius : 25
      );
      setVizGeoHeatmapBlur(
        viz.geoHeatmapBlur != null ? viz.geoHeatmapBlur : geoCfg.heatmapBlur != null ? geoCfg.heatmapBlur : 15
      );
    } else {
      setVizXField(loadedXField);
      setVizYField(loadedYField);
    }
    setVizKpiAggregation(viz.kpiAggregation || '');
    setVizNarrativeTemplate(viz.narrativeTemplate || '');
    setVizKpiValueFontSize(
      viz.kpiValueFontSize === 'small' || viz.kpiValueFontSize === 'medium'
        ? viz.kpiValueFontSize
        : 'large'
    );
    setVizKpiComparePriorYear(Boolean(viz.kpiComparePriorYear));
    setVizComparePeriod(viz.comparePeriod || (viz.compareWeekdayAlignedPriorYear ? 'year' : ''));
    setVizSeriesMetricA(viz.seriesMetricCompare?.seriesA || '');
    setVizSeriesMetricB(viz.seriesMetricCompare?.seriesB || '');
    // Compatibilidad: un gráfico guardado con el formato anterior (seriesMetricCompare.
    // divideByAlias, un alias calculado por el cliente) sigue cargando el selector.
    setVizSeriesMetricDivideBy(viz.divideByField || viz.seriesMetricCompare?.divideByAlias || '');
    setVizSeriesMetricCumulative(Boolean(viz.seriesMetricCompare?.cumulative));
    setVizShowMetricPercentLine(Boolean(viz.showMetricPercentLine));
    setVizCustomDateCompareBaseStart(viz.customDateCompare?.baseParamNameStart || '');
    setVizCustomDateCompareParam(viz.customDateCompare?.compareParamName || '');
    setVizSeriesField(
      viz.seriesTimeRangeSetId
        ? `${SERIES_TIME_RANGE_PREFIX}${viz.seriesTimeRangeSetId}`
        : viz.seriesField || ''
    );
    setVizSeriesTimeRangeColumn(viz.seriesTimeRangeSourceField || '');
    setVizBgImageUrl(viz.backgroundImageUrl || idCfg.backgroundImageUrl || '');
    setVizDensityRenderMode(viz.densityRenderMode || idCfg.renderMode || 'heatmap');
    setVizDensityHeatmapStyle(viz.densityHeatmapStyle || idCfg.heatmapStyle || 'smooth');
    setVizDensityIntensity(
      viz.densityIntensity != null ? viz.densityIntensity : idCfg.intensity != null ? idCfg.intensity : 1
    );
    setVizDensityOverlayOpacity(
      viz.densityOverlayOpacity != null
        ? viz.densityOverlayOpacity
        : idCfg.overlayOpacity != null
          ? idCfg.overlayOpacity
          : 1
    );
    setVizDensityBlur(
      viz.densityBlur != null ? viz.densityBlur : idCfg.blur != null ? idCfg.blur : 7
    );
    setVizDensityPointSize(
      viz.densityPointSize != null ? viz.densityPointSize : idCfg.pointSize != null ? idCfg.pointSize : 1
    );
    setVizDensitySeparation(
      viz.densitySeparation != null
        ? viz.densitySeparation
        : idCfg.separation != null
          ? idCfg.separation
          : 0.55
    );
    setVizDensityBackgroundDim(
      viz.densityBackgroundDim != null
        ? viz.densityBackgroundDim
        : idCfg.backgroundDim != null
          ? idCfg.backgroundDim
          : 0.72
    );
    setVizDensityGroupBy(viz.densityGroupByColumn || idCfg.groupByColumn || '');
    setVizDensityWeightCol(viz.densityWeightColumn || idCfg.weightColumn || '');
    setVizDensityShowLegend(
      viz.densityShowLegend !== false && idCfg.showLegend !== false
    );
    setVizDensityPointRadius(
      viz.densityPointRadius != null
        ? viz.densityPointRadius
        : idCfg.pointRadius != null
          ? idCfg.pointRadius
          : viz.pointRadius != null
            ? viz.pointRadius
            : 14
    );
    setVizDensitySelectedGroups(
      viz.densitySelectedGroups !== undefined
        ? viz.densitySelectedGroups
        : idCfg.selectedGroups !== undefined
          ? idCfg.selectedGroups
          : null
    );
    setVizDensityClusterEnabled(
      viz.densityClusterEnabled === true || idCfg.clusterEnabled === true
    );
    setVizDensityClusterRadiusPx(
      viz.densityClusterRadiusPx != null
        ? viz.densityClusterRadiusPx
        : idCfg.clusterRadiusPx != null
          ? idCfg.clusterRadiusPx
          : 25
    );
    setVizDensityClusterMerge(
      viz.densityClusterMerge || idCfg.clusterMerge || 'count'
    );
    setVizDensityClusterShowCount(
      viz.densityClusterShowCount === true || idCfg.clusterShowCount === true
    );
    setVizDensityClusterColorByCount(
      viz.densityClusterColorByCount === true || idCfg.clusterColorByCount === true
    );
    setVizDensityTooltipEnabled(
      viz.densityTooltipEnabled === true || idCfg.tooltipEnabled === true
    );
    setVizDensityTooltipDetail(
      (viz.densityTooltipDetail && typeof viz.densityTooltipDetail === 'object'
        ? viz.densityTooltipDetail
        : null) ||
        (idCfg.tooltipDetail && typeof idCfg.tooltipDetail === 'object' ? idCfg.tooltipDetail : null)
    );
    const ivCfg = state.intervalConfig || state.advancedConfig?.intervalConfig || {};
    setVizIntervalStartField(viz.intervalStartField || ivCfg.startColumn || '');
    setVizIntervalEndField(viz.intervalEndField || ivCfg.endColumn || '');
    setVizIntervalSeriesField(viz.intervalSeriesField || ivCfg.seriesColumn || '');
    const carCfg = state.imageCarouselConfig || state.advancedConfig?.imageCarouselConfig || {};
    setVizCarouselSensorTypeField(
      normalizeCarouselColumnFromStorage(
        viz.carouselSensorTypeColumn || carCfg.sensorTypeColumn,
        DEFAULT_IMAGE_CAROUSEL.sensorTypeColumn
      )
    );
    setVizCarouselSensorIdField(
      normalizeCarouselColumnFromStorage(
        viz.carouselSensorIdColumn || carCfg.sensorIdColumn,
        DEFAULT_IMAGE_CAROUSEL.sensorIdColumn
      )
    );
    setVizCarouselContentField(
      normalizeCarouselColumnFromStorage(
        viz.carouselContentColumn || carCfg.contentColumn,
        DEFAULT_IMAGE_CAROUSEL.contentColumn
      )
    );
    setVizCarouselInZoneField(
      normalizeCarouselColumnFromStorage(viz.carouselInZoneColumn || carCfg.inZoneColumn, '')
    );
    setVizCarouselSingleImageMode(viz.carouselSingleImageMode === true || carCfg.singleImageMode === true);
    setVizCarouselShowOnlyInZone(viz.carouselShowOnlyInZone === true || carCfg.showOnlyInZone === true);
    setVizCarouselCropMode(viz.carouselCropMode === true || carCfg.cropMode === true);
    setVizCarouselCropClassFilter(viz.carouselCropClassFilter || carCfg.cropClassFilter || '');
    setVizDensityTooltipColumns(
      Array.isArray(viz.densityTooltipColumns)
        ? viz.densityTooltipColumns
        : Array.isArray(idCfg.tooltipColumns)
          ? idCfg.tooltipColumns
          : []
    );
    setVizDensityPositionMode(
      viz.densityPositionMode === 'manual' || idCfg.positionMode === 'manual' ? 'manual' : 'columns'
    );
    setVizDensityPositionKeyColumn(viz.densityPositionKeyColumn || idCfg.positionKeyColumn || '');
    setVizDensityManualPositions(
      coalesceManualPositions(viz.densityManualPositions, idCfg.manualPositions)
    );
    setVizDensityMetricColumns(
      Array.isArray(viz.densityMetricColumns)
        ? viz.densityMetricColumns
        : Array.isArray(idCfg.metricColumns)
          ? idCfg.metricColumns
          : []
    );
    setVizDensityShowMarkerLabels(
      viz.densityShowMarkerLabels === true ||
        idCfg.showMarkerLabels === true ||
        viz.densityPositionMode === 'manual' ||
        idCfg.positionMode === 'manual'
    );
    setVizDensityMarkerEnumColumn(viz.densityMarkerEnumColumn || idCfg.markerEnumColumn || '');
    setVizDensityMarkerShape(
      ['circle', 'square', 'pin', 'diamond'].includes(viz.densityMarkerShape || idCfg.markerShape)
        ? viz.densityMarkerShape || idCfg.markerShape
        : 'circle'
    );
    setVizDensityMarkerValueColumn(viz.densityMarkerValueColumn || idCfg.markerValueColumn || '');
    setVizDensityMarkerValueAggregation(
      viz.densityMarkerValueAggregation || idCfg.markerValueAggregation || 'first'
    );
    setVizDensityMarkerLabelColumn(viz.densityMarkerLabelColumn || idCfg.markerLabelColumn || '');
    setVizDensityMarkerFill(
      (viz.densityMarkerFill || idCfg.markerFill) === 'none' ? 'none' : 'value'
    );
    setVizDensityMarkerFit((viz.densityMarkerFit || idCfg.markerFit) === 'marker' ? 'marker' : 'text');
    setVizDensityMarkerSize(
      ['small', 'medium', 'large'].includes(viz.densityMarkerSize || idCfg.markerSize)
        ? viz.densityMarkerSize || idCfg.markerSize
        : 'medium'
    );
    setVizDensityMarkerColor(viz.densityMarkerColor || idCfg.markerColor || '#ffffff');
    setVizDensityImageWidth(viz.densityImageWidth ?? idCfg.imageWidth);
    setVizDensityImageHeight(viz.densityImageHeight ?? idCfg.imageHeight);
    setVizDensityPositionColumnsCommitted(
      viz.densityPositionColumnsCommitted === true ||
        idCfg.positionColumnsCommitted === true ||
        isNonEmptyManualPositions(coalesceManualPositions(viz.densityManualPositions, idCfg.manualPositions))
    );
    setHeatmapPositionDraft(null);
    setVizStacked(Boolean(viz.stacked ?? state.stacked));
    setVizHorizontal(Boolean(viz.horizontal ?? state.horizontal));
    setVizBarShowValueLabels(
      Boolean(
        viz.barShowValueLabels ??
          viz.barShowTotalLabel ??
          viz.barShowSegmentLabel ??
          state.barShowValueLabels
      )
    );
    setVizLineShowValueLabels(
      Boolean(viz.lineShowValueLabels ?? state.lineShowValueLabels ?? state.lineShowValueLabel)
    );
    const adv = state.advancedConfig || {};
    setVizLineFillMissingAsZero(
      viz.lineFillMissingAsZero ??
        adv.lineFillMissingAsZero ??
        state.lineFillMissingAsZero ??
        true
    );
    setVizFillTimeAxisGaps(
      Boolean(
        viz.fillTimeAxisGaps ?? adv.fillTimeAxisGaps ?? state.fillTimeAxisGaps ?? false
      )
    );
    setVizLineInterpolation(
      viz.lineInterpolation ?? adv.lineInterpolation ?? state.lineInterpolation ?? 'linear'
    );
    const modeDerived = pieShowLabelsFromMode(
      viz.pieShowMode ||
        pieShowModeFromLabels(
          viz.pieShowValueLabel ?? adv.pieShowValueLabel,
          viz.pieShowPercentLabel ?? adv.pieShowPercentLabel
        )
    );
    setVizPieShowValueLabel(
      viz.pieShowValueLabel !== undefined
        ? viz.pieShowValueLabel !== false
        : modeDerived.showValue
    );
    setVizPieShowPercentLabel(
      viz.pieShowPercentLabel !== undefined
        ? viz.pieShowPercentLabel !== false
        : modeDerived.showPercent
    );
    setVizPieLabelPosition(normalizePieLabelPosition(viz.pieLabelPosition ?? adv.pieLabelPosition));
    setVizPieShowNameLabel(
      viz.pieShowNameLabel === true || adv.pieShowNameLabel === true
    );
    setVizLegendPosition(viz.legendPosition || adv.legendPosition || 'left');
    setVizLegendDisplay(viz.legendDisplay !== false && adv.legendDisplay !== false);
    setVizXAxisTitle(viz.xAxisTitle ?? adv.xAxisTitle ?? '');
    setVizYAxisTitle(viz.yAxisTitle ?? adv.yAxisTitle ?? '');
    setVizPieLegendFontSize(
      normalizePieLegendFontSize(viz.pieLegendFontSize ?? adv.pieLegendFontSize)
    );
    setLoadedExampleId(state.loadedExampleId);
    setLoadedExampleTitle(state.loadedExampleTitle || exampleTitle);
    setLoadWarnings(warnings || []);
    setPreviewRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!open || !initialChart?.id) return;
    if (loadedChartIdRef.current === initialChart.id) return;
    const spec = initialChart.visualizationSpec;
    const hasSavedDataset = Boolean(spec?.tableColumns?.length || spec?.rows?.length || spec?.values?.length);
    // Tipos sin consulta (Boton/Clima/Calendario, y Texto narrativo de texto fijo): su propia
    // config no depende del contexto de la base de datos, se cargan directo desde sus propios
    // campos en vez de por applyLoadedState (que asume tableColumns/rows/values y requiere el
    // context ya cargado). Narrativo es distinto de los otros 3: puede además traer columnas/
    // filtros guardados (los tenía antes de quedar sin marcador, o se conservan por si se
    // agrega uno después) — en ese caso, sigue más abajo para restaurarlos también, igual que
    // cualquier otro gráfico con dataset.
    if (isQuerylessChartType(initialChart.type, { narrativeTemplate: initialChart.advancedConfig?.template })) {
      setTitle(initialChart.label || '');
      if (initialChart.type === 'button') {
        setVizButtonUrl(initialChart.buttonUrl || '');
      } else if (initialChart.type === 'weather') {
        setVizWeatherLat(initialChart.weatherConfig?.lat != null ? String(initialChart.weatherConfig.lat) : '');
        setVizWeatherLon(initialChart.weatherConfig?.lon != null ? String(initialChart.weatherConfig.lon) : '');
      } else if (initialChart.type === 'calendar') {
        setVizCalendarEvents(initialChart.calendarEvents || []);
      } else if (initialChart.type === 'narrative') {
        setVizNarrativeTemplate(initialChart.advancedConfig?.template || '');
      }
      setVizChartType(initialChart.type);
      if (initialChart.type !== 'narrative' || !hasSavedDataset) {
        // Nada más que cargar: o es button/weather/calendar (nunca dependen de context), o
        // es un narrativo sin columnas guardadas. Queda listo sin esperar el modelo de datos.
        loadedChartIdRef.current = initialChart.id;
        return;
      }
      // Narrativo con columnas/filtros guardados: si el modelo de datos todavía no cargó,
      // esperar al próximo render sin marcar como cargado (si no, nunca se restaurarían).
      if (!context) return;
      // Sigue abajo para restaurarlos, igual que cualquier otro gráfico con dataset.
    } else if (!context) {
      return;
    }
    if (!hasSavedDataset) {
      if (initialChart.type === 'narrative') {
        // Texto fijo sin ningún dato guardado: estado válido, no es un gráfico roto.
        loadedChartIdRef.current = initialChart.id;
        return;
      }
      showError('Este gráfico no tiene dataset guardado y no se puede editar aquí.');
      return;
    }
    loadedChartIdRef.current = initialChart.id;
    setBoundOriginalId(initialChart.originalChartId || null);
    setReuseExistingQuery(true);
    applyLoadedState(
      {
        title: initialChart.advancedConfig?.title || initialChart.label || '',
        baseTableId: spec.baseTableId,
        tableColumns: spec.tableColumns,
        rows: spec.rows,
        values: spec.values,
        filters: spec.filters,
        limit: spec.limit,
        orderBy: spec.orderBy,
        orderDirection: spec.orderDirection,
        orderColumnIndex: spec.orderColumnIndex,
        episodeAnalysis: spec.episodeAnalysis,
        spatialZoneAnalysis: spec.spatialZoneAnalysis,
        chartType: spec.chartType,
        imageDensityConfig:
          initialChart.imageDensityConfig || initialChart.advancedConfig?.imageDensityConfig || {},
        visualization: {
          ...(spec.visualization || {}),
          stacked: spec.visualization?.stacked ?? initialChart.stacked,
          horizontal: spec.visualization?.horizontal ?? initialChart.horizontal,
          barShowValueLabels:
            spec.visualization?.barShowValueLabels ??
            initialChart.advancedConfig?.barShowTotalLabel ??
            initialChart.advancedConfig?.barShowSegmentLabel,
          lineShowValueLabels:
            spec.visualization?.lineShowValueLabels ??
            initialChart.advancedConfig?.lineShowValueLabel,
          lineFillMissingAsZero:
            spec.visualization?.lineFillMissingAsZero ??
            initialChart.advancedConfig?.lineFillMissingAsZero,
          lineInterpolation:
            spec.visualization?.lineInterpolation ??
            initialChart.advancedConfig?.lineInterpolation ??
            'linear',
          fillTimeAxisGaps:
            spec.visualization?.fillTimeAxisGaps ??
            initialChart.advancedConfig?.fillTimeAxisGaps ??
            false,
          pieShowMode:
            spec.visualization?.pieShowMode ??
            pieShowModeFromLabels(
              spec.visualization?.pieShowValueLabel ?? initialChart.advancedConfig?.pieShowValueLabel,
              spec.visualization?.pieShowPercentLabel ?? initialChart.advancedConfig?.pieShowPercentLabel
            ),
          pieShowValueLabel:
            spec.visualization?.pieShowValueLabel ?? initialChart.advancedConfig?.pieShowValueLabel,
          pieShowPercentLabel:
            spec.visualization?.pieShowPercentLabel ?? initialChart.advancedConfig?.pieShowPercentLabel,
          pieLabelPosition:
            spec.visualization?.pieLabelPosition ??
            initialChart.advancedConfig?.pieLabelPosition ??
            DEFAULT_PIE_LABEL_POSITION,
          pieShowNameLabel:
            spec.visualization?.pieShowNameLabel ??
            initialChart.advancedConfig?.pieShowNameLabel ??
            false,
          legendPosition:
            spec.visualization?.legendPosition ??
            initialChart.advancedConfig?.legendPosition ??
            'left',
          pieLegendFontSize:
            spec.visualization?.pieLegendFontSize ??
            initialChart.advancedConfig?.pieLegendFontSize ??
            DEFAULT_PIE_LEGEND_FONT_SIZE,
          xAxisTitle:
            spec.visualization?.xAxisTitle ?? initialChart.advancedConfig?.xAxisTitle ?? '',
          yAxisTitle:
            spec.visualization?.yAxisTitle ?? initialChart.advancedConfig?.yAxisTitle ?? '',
          kpiValueFontSize:
            spec.visualization?.kpiValueFontSize ?? initialChart.advancedConfig?.kpiValueFontSize,
        },
        advancedConfig: initialChart.advancedConfig || {},
        stacked: spec.visualization?.stacked ?? initialChart.stacked,
        horizontal: spec.visualization?.horizontal ?? initialChart.horizontal,
        loadedExampleId: initialChart.originalChartId || null,
        loadedExampleTitle: initialChart.label || initialChart.advancedConfig?.title || '',
      },
      [],
      initialChart.label
    );
  }, [open, initialChart?.id, initialChart, context, applyLoadedState, showError]);

  const handleLoadExample = useCallback(
    (example) => {
      setReferencePreview(null);
      setLoadedExampleId(null);
      setLoadedExampleTitle('');
      setLoadWarnings([]);

      if (String(example?.id || '').startsWith('orig-')) {
        const loadPayload = originalToWizardLoad(example);
        if (loadPayload) {
          if (loadPayload.kind === 'dataset' || loadPayload.rows || loadPayload.values) {
            const { state, warnings } = applyWizardState(loadPayload);
            applyLoadedState(
              {
                ...state,
                ...loadPayload,
                loadedExampleId: example.id,
                loadedExampleTitle: example.title,
              },
              warnings,
              example.title
            );
            showSuccess(`Cargado: ${example.title}`);
            return;
          }
          if (loadPayload.chartType || loadPayload.visualization) {
            const { state, warnings } = applyWizardState(loadPayload);
            applyLoadedState(
              {
                ...state,
                loadedExampleId: example.id,
                loadedExampleTitle: example.title,
              },
              warnings,
              example.title
            );
            showSuccess(`Cargado: ${example.title}`);
            return;
          }
        }
      }

      if (example?.wizardState?.kind === 'dataset' || example?.wizardState?.rows || example?.wizardState?.values) {
        const { state, warnings } = applyWizardState(example);
        applyLoadedState(
          {
            ...state,
            ...example.wizardState,
            tableColumns: undefined,
            loadedExampleId: example.id,
            loadedExampleTitle: example.title,
            visualization: example.wizardState.visualization || state.visualization,
          },
          warnings,
          example.title
        );
        showSuccess(`Cargado: ${example.title}`);
        return;
      }
      if (example?.wizardState?.chartType) {
        const { state, warnings } = applyWizardState(example);
        applyLoadedState(state, warnings, example.title);
        showSuccess(`Cargado: ${example.title}`);
        return;
      }
      if (!context) {
        showError('Espera a que cargue el modelo');
        return;
      }
      const { state, warnings } = resolveExampleWizard(example, context);
      applyLoadedState(state, warnings, example.title);
      showSuccess(`Cargado: ${example.title}`);
    },
    [context, showError, showSuccess, applyLoadedState]
  );

  const handleViewChartItem = useCallback(
    (original, meta) => {
      if (!original) return;
      const entry = mergeLiveLibraryEntry(meta?.entry);
      const isReference = meta?.isReference === true || isLibraryReference(entry);
      if (isReference) {
        setReferencePreview({
          original,
          entry,
          dashboardId: meta.dashboardId,
          dashboardName: meta.dashboardName || '',
        });
        setLoadedExampleId(null);
        setLoadedExampleTitle('');
        setLoadWarnings([]);
        return;
      }
      setReferencePreview(null);
      handleLoadExample(original);
    },
    [handleLoadExample, mergeLiveLibraryEntry]
  );

  const handleEditOriginalFromReference = useCallback(() => {
    if (!referencePreview?.original) return;
    const { original } = referencePreview;
    setReferencePreview(null);
    handleLoadExample(original);
    showSuccess(`Editando original: ${original.title || 'gráfico'}`);
  }, [referencePreview, handleLoadExample, showSuccess]);

  const handleMakeInstanceIndependentInBuilder = useCallback(
    async ({ dashboardId, instanceId }) => {
      if (!dashboardId || !instanceId || !onMakeInstanceIndependent) return null;
      setMakingIndependent(true);
      try {
        const result = await onMakeInstanceIndependent({ dashboardId, instanceId });
        if (result?.original) {
          setReferencePreview(null);
          handleLoadExample(result.original);
        }
        return result;
      } finally {
        setMakingIndependent(false);
      }
    },
    [onMakeInstanceIndependent, handleLoadExample]
  );

  const handleMakeIndependentFromReference = useCallback(async () => {
    if (!referencePreview?.entry?.id || !referencePreview?.dashboardId) return;
    await handleMakeInstanceIndependentInBuilder({
      dashboardId: referencePreview.dashboardId,
      instanceId: referencePreview.entry.id,
    });
  }, [referencePreview, handleMakeInstanceIndependentInBuilder]);

  const resolveOriginalChartId = useCallback(
    ({ forkOriginal = false } = {}) => {
      if (forkOriginal) return undefined;
      if (boundOriginalId) return boundOriginalId;
      if (loadedExampleId && String(loadedExampleId).startsWith('orig-')) return loadedExampleId;
      return undefined;
    },
    [boundOriginalId, loadedExampleId]
  );

  const publishChartMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-builder/publish', body, { headers }),
  });

  const executePublish = async ({ propagateToAll = true, forkOriginal = false } = {}) => {
    // Tipos sin consulta (Boton/Clima/Calendario, y Texto narrativo cuando es texto fijo sin
    // {{marcadores}}): no hay nada que consultar, el chart se arma directo en el cliente, sin
    // pasar por el servidor ni depender de un modelo de datos activo.
    if (isQuerylessChartType(vizChartType, { narrativeTemplate: vizNarrativeTemplate })) {
      const chartConfig = {
        // queryId explícito en null: si se está editando un gráfico que antes sí tenía
        // consulta (ej. un narrativo con {{marcador}} al que le borraron el marcador), sin
        // esto el spread en App.js (...existing, ...chartConfig) dejaría colgado el queryId
        // viejo en vez de limpiarlo.
        queryId: null,
        ...(vizChartType === 'button'
          ? { type: 'button', label: title.trim() || 'Enlace', buttonUrl: vizButtonUrl.trim() }
          : vizChartType === 'weather'
            ? {
                type: 'weather',
                label: title.trim() || 'Tiempo',
                weatherConfig: { lat: parseFloat(vizWeatherLat), lon: parseFloat(vizWeatherLon) },
              }
            : vizChartType === 'calendar'
              ? { type: 'calendar', label: title.trim() || 'Calendario', calendarEvents: vizCalendarEvents }
              : {
                  // A diferencia de los otros tipos sin consulta, sí se conserva
                  // visualizationSpec: si más adelante se agrega un {{marcador}}, el editor
                  // recupera las columnas/filtros que ya tenía configurados en vez de partir
                  // de cero.
                  type: 'narrative',
                  label: title.trim() || 'Texto narrativo',
                  advancedConfig: { template: vizNarrativeTemplate },
                  visualizationSpec: buildSpec(),
                }),
      };
      onPublish?.({
        chartConfig,
        queryData: null,
        queryId: null,
        sql: null,
        database,
        connectionId,
        editingChartId: editing ? initialChart?.id : undefined,
        originalChartId: null,
        forkOriginal: false,
        dashboardsUpdated: 0,
      });
      showSuccess(editing ? 'Gráfico actualizado' : 'Gráfico agregado al dashboard');
      onClose?.();
      return;
    }
    const headers = getAuthHeaders?.();
    if (!headers) return;
    setPublishing(true);
    try {
      const spec = buildSpec();
      const originalChartId = resolveOriginalChartId({ forkOriginal });
      const res = await publishChartMutation.mutateAsync({
        body: {
          connectionId,
          database,
          spec,
          title: title.trim() || (vizChartType === 'table' ? 'Tabla' : vizChartType === 'tablePlus' ? 'Tabla+' : `Gráfico ${vizChartType}`),
          timezone,
          originalChartId,
          existingQueryId: forkOriginal || !reuseExistingQuery ? undefined : initialChart?.queryId,
          propagateToAll,
        },
        headers,
      });
      if (res.data.success) {
        const dashboardsUpdated = res.data.dashboardsUpdated || 0;
        showSuccess(
          editing
            ? forkOriginal
              ? 'Cambios guardados solo en este dashboard (nueva copia en colección)'
              : dashboardsUpdated > 0
                ? `Gráfico actualizado en ${dashboardsUpdated} dashboard(s)`
                : vizChartType === 'table'
                  ? 'Tabla actualizada'
                  : vizChartType === 'tablePlus'
                    ? 'Tabla+ actualizada'
                    : 'Gráfico actualizado'
            : vizChartType === 'table'
              ? 'Tabla agregada al dashboard'
              : vizChartType === 'tablePlus'
                ? 'Tabla+ agregada al dashboard'
                : 'Gráfico agregado al dashboard'
        );
        onPublish?.({
          chartConfig: res.data.chartConfig,
          queryData: res.data.queryData,
          queryId: res.data.queryId,
          sql: res.data.sql,
          database,
          connectionId,
          editingChartId: editing ? initialChart?.id : undefined,
          originalChartId: res.data.originalChartId,
          forkOriginal,
          dashboardsUpdated,
        });
        setPropagateDialogOpen(false);
        setPropagateUsage([]);
        onClose?.();
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'Error al publicar');
    } finally {
      setPublishing(false);
    }
  };

  const originalUsageMutation = useMutation({
    mutationFn: ({ originalId, headers }) =>
      axios.get(`/api/chart-originals/${encodeURIComponent(originalId)}/usage`, { headers }),
  });

  const closeBuilder = useCallback(() => {
    requestLeave(() => onClose?.());
  }, [requestLeave, onClose]);

  const handlePublish = async () => {
    requestLeave(async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;

    const originalId = resolveOriginalChartId({ forkOriginal: false });
    if (editing && originalId) {
      try {
        const res = await originalUsageMutation.mutateAsync({ originalId, headers });
        const usage = res.data.usage || [];
        const otherDashboards = usage.filter(
          (u) => u.dashboardId != null && String(u.dashboardId) !== String(currentDashboardId)
        );
        if (otherDashboards.length > 0) {
          setPropagateUsage(usage);
          setPropagateDialogOpen(true);
          return;
        }
      } catch (err) {
        showError(err.response?.data?.error || err.message || 'No se pudo comprobar el uso del gráfico');
        return;
      }
    }

    await executePublish({ propagateToAll: true, forkOriginal: false });
    });
  };

  const openSaveExampleDialog = () => {
    const base = title.trim() || 'Dataset';
    setSaveExampleTitle(`${base} (copia)`);
    setSaveExampleNote('');
    setSaveExampleOpen(true);
  };

  const saveExampleMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/chart-originals', body, { headers }),
  });

  const handleSaveExample = async () => {
    const headers = getAuthHeaders?.();
    if (!headers) return;
    const t = saveExampleTitle.trim();
    if (!t) {
      showError('Indica un título');
      return;
    }
    setSavingExample(true);
    try {
      const wizardState = {
        kind: 'dataset',
        title: t,
        baseTableId,
        rows: specRows,
        values: specValues,
        filters,
        rowLimit,
        orderByKind,
        orderDirection,
        orderColumnIndex,
        visualization: (() => {
          const spec = buildSpec();
          return spec.visualization;
        })(),
      };
      const spec = buildSpec();
      const res = await saveExampleMutation.mutateAsync({
        body: {
          title: t,
          businessQuestion: saveExampleNote.trim(),
          wizardState: { ...wizardState, ...spec, chartType: vizChartType },
          chartConfig: {
            type: vizChartType || 'table',
            label: t,
            visualizationSpec: spec,
          },
          connectionId,
          database,
          suggestedChartType: vizChartType || 'table',
        },
        headers,
      });
      if (res.data.success) {
        showSuccess('Duplicado como gráfico independiente');
        setSaveExampleOpen(false);
        setExamplesRefreshKey((k) => k + 1);
        setTitle(t);
        setLoadedExampleId(res.data.original?.id || null);
        setLoadedExampleTitle(t);
        setBoundOriginalId(res.data.original?.id || null);
        setReuseExistingQuery(false);
        setPublishAsNew(true);
      }
    } catch (err) {
      showError(err.response?.data?.error || err.message || 'No se pudo guardar');
    } finally {
      setSavingExample(false);
    }
  };

  // Boton/Clima/Calendario no usan ninguna tabla del modelo de datos: no deben
  // bloquearse solo porque la conexion actual no tiene tablas activas. Texto narrativo
  // tampoco: puede ser una nota de texto fijo sin ninguna dependencia de datos, y este
  // chequeo reemplaza todo el editor por un aviso, así que no puede depender de si tiene o
  // no un marcador en este momento (de ahí el chequeo estático por vizChartType, no por
  // chartTypeIsQueryless). Un narrativo con datos reales igual puede configurar Fila/Valor
  // apenas el modelo cargue; mientras tanto el selector de columnas solo queda vacío.
  const contextBlocked =
    !isStaticQuerylessType &&
    vizChartType !== 'narrative' &&
    context &&
    (context.status !== 'active' || context.tableCount === 0);

  const activeSeriesTimeRangeSetId = seriesTimeRangeSetIdFromField(vizSeriesField);
  const hasBarSeries = Boolean(vizSeriesField || activeSeriesTimeRangeSetId);

  const measureDecimalPlaces = useMemo(() => {
    const plainAliases = resolvePlainColumnAliases(columns);
    for (let i = 0; i < columns.length; i += 1) {
      const c = columns[i];
      if (!c?.tableId || !c?.column || c.role !== 'value') continue;
      const key = plainAliases[i];
      if (vizYField && key !== vizYField) continue;
      if (c.decimalPlaces == null || c.decimalPlaces === '') return undefined;
      const n = parseInt(c.decimalPlaces, 10);
      return Number.isFinite(n) && n >= 0 && n <= 10 ? n : undefined;
    }
    return undefined;
  }, [columns, vizYField]);

  const measureColorThresholds = useMemo(() => {
    const plainAliases = resolvePlainColumnAliases(columns);
    for (let i = 0; i < columns.length; i += 1) {
      const c = columns[i];
      if (!c?.tableId || !c?.column || c.role !== 'value') continue;
      const key = plainAliases[i];
      if (vizYField && key !== vizYField) continue;
      return isValidColorThresholds(c.colorThresholds) ? c.colorThresholds : undefined;
    }
    return undefined;
  }, [columns, vizYField]);

  const episodeDurationAdvancedConfig = useMemo(() => {
    if (!episodesChartActive) return null;
    return resolveEpisodeDurationPresentation(episodeAnalysis, columns, chartPreview?.mapping?.yAlias || vizYField)
      .yDuration;
  }, [episodesChartActive, episodeAnalysis, columns, chartPreview?.mapping?.yAlias, vizYField]);

  const barChartAdvancedConfig = useMemo(() => {
    if (vizChartType !== 'bar') return undefined;
    return {
      ...(measureDecimalPlaces != null ? { valueDecimalPlaces: measureDecimalPlaces } : {}),
      ...(episodeDurationAdvancedConfig || {}),
      ...(vizBarShowValueLabels
        ? {
            barShowTotalLabel: true,
            barShowSegmentLabel: Boolean(vizStacked && hasBarSeries),
          }
        : {}),
      ...(vizFillTimeAxisGaps ? { fillTimeAxisGaps: true } : {}),
      ...(vizShowMetricPercentLine ? { showMetricPercentLine: true } : {}),
      ...(vizXAxisTitle.trim() ? { xAxisTitle: vizXAxisTitle.trim() } : {}),
      ...(vizYAxisTitle.trim() ? { yAxisTitle: vizYAxisTitle.trim() } : {}),
    };
  }, [
    vizChartType,
    vizBarShowValueLabels,
    vizStacked,
    hasBarSeries,
    measureDecimalPlaces,
    episodeDurationAdvancedConfig,
    vizFillTimeAxisGaps,
    vizShowMetricPercentLine,
    vizXAxisTitle,
    vizYAxisTitle,
  ]);

  const lineChartAdvancedConfig = useMemo(() => {
    if (vizChartType !== 'line') return undefined;
    return {
      lineFillMissingAsZero: vizLineFillMissingAsZero,
      lineInterpolation: vizLineInterpolation,
      ...(measureDecimalPlaces != null ? { valueDecimalPlaces: measureDecimalPlaces } : {}),
      ...(episodeDurationAdvancedConfig || {}),
      ...(vizLineShowValueLabels ? { lineShowValueLabel: true } : {}),
      ...(vizFillTimeAxisGaps ? { fillTimeAxisGaps: true } : {}),
      ...(vizShowMetricPercentLine ? { showMetricPercentLine: true } : {}),
      ...(vizXAxisTitle.trim() ? { xAxisTitle: vizXAxisTitle.trim() } : {}),
      ...(vizYAxisTitle.trim() ? { yAxisTitle: vizYAxisTitle.trim() } : {}),
    };
  }, [
    vizChartType,
    vizLineShowValueLabels,
    vizLineFillMissingAsZero,
    vizFillTimeAxisGaps,
    vizShowMetricPercentLine,
    vizLineInterpolation,
    measureDecimalPlaces,
    episodeDurationAdvancedConfig,
    vizXAxisTitle,
    vizYAxisTitle,
  ]);

  const pieChartAdvancedConfig = useMemo(() => {
    if (vizChartType !== 'pie') return undefined;
    return {
      ...(measureDecimalPlaces != null ? { valueDecimalPlaces: measureDecimalPlaces } : {}),
      legendDisplay: vizLegendDisplay,
      legendPosition: vizLegendPosition,
      pieShowValueLabel: vizPieShowValueLabel,
      pieShowPercentLabel: vizPieShowPercentLabel,
      pieShowNameLabel: vizPieShowNameLabel,
      pieLabelPosition: vizPieLabelPosition,
      pieLegendFontSize: vizPieLegendFontSize,
    };
  }, [
    vizChartType,
    vizPieShowValueLabel,
    vizPieShowPercentLabel,
    vizPieShowNameLabel,
    vizPieLabelPosition,
    vizPieLegendFontSize,
    vizLegendDisplay,
    vizLegendPosition,
    measureDecimalPlaces,
  ]);

  const previewChartConfig = useMemo(() => {
    if (vizChartType === 'imageDensity') {
      const zoneFieldList = spatialZonesChartActive
        ? spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols)
        : [];
      const centroidX =
        zoneFieldList.find((f) => f.base === 'centroid_x')?.value || 'centroid_x';
      const centroidY =
        zoneFieldList.find((f) => f.base === 'centroid_y')?.value || 'centroid_y';
      const eventosField =
        zoneFieldList.find((f) => f.base === 'eventos')?.value || 'eventos';
      const xCol = spatialZonesChartActive
        ? centroidX
        : effectiveImageDensityAxis(vizXField, 'x', IMAGE_DENSITY_AXIS_AUTO_X);
      const yCol = spatialZonesChartActive
        ? centroidY
        : effectiveImageDensityAxis(vizYField, 'y', IMAGE_DENSITY_AXIS_AUTO_Y);
      return {
        type: 'imageDensity',
        xColumn: xCol,
        yColumn: yCol,
        imageDensityConfig: {
          ...buildImageDensityConfigFromViz(
          {
            backgroundImageUrl: vizBgImageUrl,
            xField: spatialZonesChartActive
              ? centroidX
              : imageDensityAxisForSpec(vizXField, IMAGE_DENSITY_AXIS_AUTO_X),
            yField: spatialZonesChartActive
              ? centroidY
              : imageDensityAxisForSpec(vizYField, IMAGE_DENSITY_AXIS_AUTO_Y),
            densityRenderMode: vizDensityRenderMode,
            densityHeatmapStyle: vizDensityHeatmapStyle,
            densityIntensity: vizDensityIntensity,
            densityOverlayOpacity: vizDensityOverlayOpacity,
            densityBlur: vizDensityBlur,
            densityPointSize: vizDensityPointSize,
            densitySeparation: vizDensitySeparation,
            densityBackgroundDim: vizDensityBackgroundDim,
            densityGroupByColumn: vizDensityGroupBy,
            densityWeightColumn: spatialZonesChartActive ? eventosField : vizDensityWeightCol,
            densityShowLegend: vizDensityShowLegend,
            densityPointRadius: vizDensityPointRadius,
            densitySelectedGroups: vizDensitySelectedGroups,
            densityClusterEnabled: spatialZonesChartActive ? false : vizDensityClusterEnabled,
            densityClusterRadiusPx: vizDensityClusterRadiusPx,
            densityClusterMerge: vizDensityClusterMerge,
            densityClusterShowCount: spatialZonesChartActive ? false : vizDensityClusterShowCount,
            densityClusterColorByCount: spatialZonesChartActive ? false : vizDensityClusterColorByCount,
            densityTooltipEnabled: vizDensityTooltipEnabled,
            densityTooltipColumns: vizDensityTooltipColumns,
            densityTooltipDetail: vizDensityTooltipDetail,
            densityPositionMode: vizDensityPositionMode,
            densityPositionKeyColumn: vizDensityPositionKeyColumn,
            densityManualPositions: vizDensityManualPositions,
            densityMetricColumns: vizDensityMetricColumns,
            densityShowMarkerLabels: vizDensityShowMarkerLabels,
            densityMarkerEnumColumn: vizDensityMarkerEnumColumn,
            densityMarkerShape: vizDensityMarkerShape,
            densityMarkerValueColumn: vizDensityMarkerValueColumn,
            densityMarkerValueAggregation: vizDensityMarkerValueAggregation,
            densityMarkerLabelColumn: vizDensityMarkerLabelColumn,
            densityMarkerFill: vizDensityMarkerFill,
            densityMarkerFit: vizDensityMarkerFit,
            densityMarkerSize: vizDensityMarkerSize,
            densityMarkerColor: vizDensityMarkerColor,
            densityImageWidth: vizDensityImageWidth,
            densityImageHeight: vizDensityImageHeight,
            densityPositionColumnsCommitted: vizDensityPositionColumnsCommitted,
          },
          { xColumn: xCol, yColumn: yCol }
          ),
          columnDateFormats: feedTemporalMaps.columnDateFormats,
          columnDateBuckets: feedTemporalMaps.columnDateBuckets,
        },
      };
    }
    if (vizChartType === 'geoHeatmap') {
      const latCol = effectiveImageDensityAxis(vizXField, 'x', GEO_HEATMAP_AUTO_LAT);
      const lngCol = effectiveImageDensityAxis(vizYField, 'y', GEO_HEATMAP_AUTO_LNG);
      return {
        type: 'geoHeatmap',
        xColumn: latCol,
        yColumn: lngCol,
        latColumn: latCol,
        lngColumn: lngCol,
        geoHeatmapConfig: buildGeoHeatmapConfigFromViz(
          {
            latField: geoHeatmapAxisForSpec(vizXField, GEO_HEATMAP_AUTO_LAT) || latCol,
            lngField: geoHeatmapAxisForSpec(vizYField, GEO_HEATMAP_AUTO_LNG) || lngCol,
            densityWeightColumn: vizDensityWeightCol,
            geoHeatmapRadius: vizGeoHeatmapRadius,
            geoHeatmapBlur: vizGeoHeatmapBlur,
            geoHeatmapUseValue: Boolean(vizDensityWeightCol),
            densityShowLegend: vizDensityShowLegend,
            densityTooltipEnabled: vizDensityTooltipEnabled,
            densityTooltipColumns: vizDensityTooltipColumns,
          },
          { latColumn: latCol, lngColumn: lngCol }
        ),
      };
    }
    if (vizChartType === 'intervals') {
      return {
        type: 'intervals',
        intervalConfig: {
          startColumn: vizIntervalStartField,
          endColumn: vizIntervalEndField,
          seriesColumn: vizIntervalSeriesField,
        },
        advancedConfig: { ...(episodeDurationAdvancedConfig || {}) },
      };
    }
    if (vizChartType === 'imageCarousel') {
      return {
        type: 'imageCarousel',
        imageCarouselConfig: {
          ...DEFAULT_IMAGE_CAROUSEL,
          sensorTypeColumn: vizCarouselSensorTypeField || DEFAULT_IMAGE_CAROUSEL.sensorTypeColumn,
          sensorIdColumn: vizCarouselSensorIdField || DEFAULT_IMAGE_CAROUSEL.sensorIdColumn,
          contentColumn: vizCarouselContentField || DEFAULT_IMAGE_CAROUSEL.contentColumn,
          inZoneColumn: vizCarouselInZoneField || '',
          singleImageMode: vizCarouselSingleImageMode === true,
          showOnlyInZone: vizCarouselShowOnlyInZone === true,
          cropMode: vizCarouselCropMode === true,
          cropClassFilter: vizCarouselCropClassFilter || '',
        },
      };
    }
    if (vizChartType === 'table' || vizChartType === 'tablePlus') return null;
    if (
      vizChartType === 'narrative' &&
      !/\{\{\s*[a-zA-Z0-9_]+\s*\}\}/.test(vizNarrativeTemplate || '') &&
      !chartWidgetPreview?.mapping
    ) {
      // Texto fijo sin marcadores {{...}}: no depende de ninguna columna de datos, se previsualiza
      // igual aunque no haya Fila/Valor configurado (a diferencia del resto de los tipos de gráfico).
      return { type: 'narrative', advancedConfig: { template: vizNarrativeTemplate } };
    }
    if (!chartWidgetPreview?.mapping) return null;
    const { xAlias, yAlias, labelAlias, chartType: ct } = chartWidgetPreview.mapping;
    const episodeLabelColumn =
      episodesChartActive && vizSeriesField && !activeSeriesTimeRangeSetId
        ? resolveEpisodeSeriesLabel(vizSeriesField, episodeAnalysis, episodeGroupCols)
        : undefined;
    if (vizChartType === 'kpi' || ct === 'kpi') {
      const kpiAdvancedConfig = {
        ...(chartWidgetPreview?.kpiComparison ? { kpiComparison: chartWidgetPreview.kpiComparison } : {}),
        ...(measureColorThresholds ? { kpiColorThresholds: measureColorThresholds } : {}),
        ...(vizKpiValueFontSize === 'small' || vizKpiValueFontSize === 'medium'
          ? { kpiValueFontSize: vizKpiValueFontSize }
          : vizKpiValueFontSize === 'large'
            ? { kpiValueFontSize: 'large' }
            : {}),
        ...(Array.isArray(tableCalculatedColumns) && tableCalculatedColumns.length
          ? { tableCalculatedColumns }
          : {}),
      };
      return {
        type: 'kpi',
        yColumn: yAlias,
        kpiLabel: title || yAlias,
        advancedConfig: kpiAdvancedConfig,
      };
    }
    if (vizChartType === 'narrative') {
      return {
        type: 'narrative',
        xColumn: xAlias,
        yColumn: yAlias,
        advancedConfig: { template: vizNarrativeTemplate },
      };
    }
    if (ct === 'table' || ct === 'imageDensity' || ct === 'geoHeatmap') return null;
    return {
      type: ct,
      xColumn: xAlias,
      yColumn: yAlias,
      labelColumn:
        episodeLabelColumn ||
        (activeSeriesTimeRangeSetId ? chartWidgetPreview.mapping?.labelAlias : null) ||
        labelAlias ||
        undefined,
      deltaColumn: chartWidgetPreview.mapping?.deltaAlias || undefined,
      percentColumn: chartWidgetPreview.mapping?.percentAlias || undefined,
      occupancyColumn: chartWidgetPreview.mapping?.occupancyAlias || undefined,
      cumulativeColumn: chartWidgetPreview.mapping?.cumulativeAlias || undefined,
      hourCycleStart: chartWidgetPreview.mapping.hourCycleStart,
      xDateBucket:
        chartWidgetPreview.mapping.xDateBucket ||
        (episodesChartActive
          ? episodeAnalysis?.xDateBucket
          : vizXColumnInfo?.column?.dateBucket !== 'raw'
            ? vizXColumnInfo?.column?.dateBucket
            : undefined),
      xDateDisplayFormat:
        chartWidgetPreview.mapping.xDateDisplayFormat ||
        (episodesChartActive
          ? episodeChartDateFormat
          : vizXColumnInfo?.column?.dateDisplayFormat &&
              vizXColumnInfo.column.dateDisplayFormat !== 'auto'
            ? vizXColumnInfo.column.dateDisplayFormat
            : undefined),
      // Sin esto, una serie agrupada por fecha (ej. "time · Por mes")
      // muestra el timestamp crudo en la leyenda en vez del valor formateado.
      // Se omite si labelColumn termina apuntando a un valor ya resuelto
      // (episodio o tramo horario), que no es la columna de fecha cruda.
      labelDateBucket:
        !episodeLabelColumn &&
        !activeSeriesTimeRangeSetId &&
        vizSeriesColumnInfo?.column?.dateBucket &&
        vizSeriesColumnInfo.column.dateBucket !== 'raw'
          ? vizSeriesColumnInfo.column.dateBucket
          : undefined,
      labelDateDisplayFormat:
        !episodeLabelColumn &&
        !activeSeriesTimeRangeSetId &&
        vizSeriesColumnInfo?.column?.dateDisplayFormat &&
        vizSeriesColumnInfo.column.dateDisplayFormat !== 'auto'
          ? vizSeriesColumnInfo.column.dateDisplayFormat
          : undefined,
      stacked: ct === 'bar' && hasBarSeries ? vizStacked : undefined,
      horizontal: ct === 'bar' ? vizHorizontal : undefined,
      advancedConfig: {
        ...(episodeDurationAdvancedConfig || {}),
        ...(ct === 'bar'
          ? barChartAdvancedConfig
          : ct === 'line'
            ? lineChartAdvancedConfig
            : ct === 'pie'
              ? pieChartAdvancedConfig
              : {}),
        ...(Array.isArray(tableCalculatedColumns) && tableCalculatedColumns.length
          ? { tableCalculatedColumns }
          : {}),
      },
    };
  }, [chartWidgetPreview, title, vizChartType, vizXField, vizYField, vizSeriesField, activeSeriesTimeRangeSetId, episodeAnalysis, episodeGroupCols, episodesChartActive, spatialZoneAnalysis, spatialZoneGroupCols, spatialZonesChartActive, episodeDurationAdvancedConfig, feedTemporalMaps, vizBgImageUrl, vizDensityRenderMode, vizDensityHeatmapStyle, vizDensityIntensity, vizDensityOverlayOpacity, vizDensityBlur, vizDensityPointSize, vizDensitySeparation, vizDensityBackgroundDim, vizDensityGroupBy, vizDensityWeightCol, vizDensityShowLegend, vizDensityPointRadius, vizDensitySelectedGroups, vizDensityClusterEnabled, vizDensityClusterRadiusPx, vizDensityClusterMerge, vizDensityClusterShowCount, vizDensityClusterColorByCount, vizDensityTooltipEnabled, vizDensityTooltipColumns, vizDensityTooltipDetail, vizIntervalStartField, vizIntervalEndField, vizIntervalSeriesField, vizCarouselSensorTypeField, vizCarouselSensorIdField, vizCarouselContentField, vizCarouselInZoneField, vizCarouselSingleImageMode, vizCarouselShowOnlyInZone, vizCarouselCropMode, vizCarouselCropClassFilter, vizDensityPositionMode, vizDensityPositionKeyColumn, vizDensityManualPositions, vizDensityMetricColumns, vizDensityShowMarkerLabels, vizDensityMarkerEnumColumn, vizDensityMarkerShape, vizDensityMarkerValueColumn, vizDensityMarkerValueAggregation, vizDensityMarkerLabelColumn, vizDensityMarkerFill, vizDensityMarkerFit, vizDensityMarkerSize, vizDensityMarkerColor, vizDensityImageWidth, vizDensityImageHeight, vizDensityPositionColumnsCommitted, vizGeoHeatmapRadius, vizGeoHeatmapBlur, vizStacked, vizHorizontal, hasBarSeries, barChartAdvancedConfig, lineChartAdvancedConfig, pieChartAdvancedConfig, vizXColumnInfo, vizSeriesColumnInfo, episodeChartDateFormat, measureColorThresholds, vizKpiValueFontSize, tableCalculatedColumns, vizNarrativeTemplate]);

  const datetimeFieldOptions = useMemo(
    () =>
      buildChartFeedDatetimeFieldOptions({
        chartFeed,
        episodeAnalysis,
        episodeGroupCols,
        spatialZoneAnalysis,
        spatialZoneGroupCols,
        columns,
        context,
        resolvePlainColumnAliases,
        headerLabel,
        getColumnMeta,
      }),
    [
      chartFeed,
      episodeAnalysis,
      episodeGroupCols,
      spatialZoneAnalysis,
      spatialZoneGroupCols,
      columns,
      context,
    ]
  );

  useEffect(() => {
    if (!activeSeriesTimeRangeSetId) return;
    const allowed = new Set(datetimeFieldOptions.map((o) => o.value));
    if (vizSeriesTimeRangeColumn && allowed.has(vizSeriesTimeRangeColumn)) return;
    const guessed = guessSeriesTimeRangeSourceField({
      chartFeed,
      episodeAnalysis,
      episodeGroupCols,
      spatialZoneAnalysis,
      spatialZoneGroupCols,
      columns,
      filters,
      setId: activeSeriesTimeRangeSetId,
      context,
      vizXField,
      fieldAlias,
      getColumnMeta,
      guessSqlTimeRangeSourceField: guessTimeRangeSourceField,
    });
    if (guessed && guessed !== vizSeriesTimeRangeColumn) {
      setVizSeriesTimeRangeColumn(guessed);
    }
  }, [
    activeSeriesTimeRangeSetId,
    chartFeed,
    datetimeFieldOptions,
    episodeAnalysis,
    episodeGroupCols,
    spatialZoneAnalysis,
    spatialZoneGroupCols,
    columns,
    filters,
    context,
    vizXField,
    vizSeriesTimeRangeColumn,
  ]);

  const handleImageDensityConfigChange = useCallback((cfg) => {
    setVizBgImageUrl(cfg.backgroundImageUrl || '');
    const vizFields = imageDensityVizFieldsFromConfig(cfg);
    setVizDensityRenderMode(vizFields.densityRenderMode);
    setVizDensityHeatmapStyle(vizFields.densityHeatmapStyle);
    setVizDensityIntensity(vizFields.densityIntensity);
    setVizDensityOverlayOpacity(vizFields.densityOverlayOpacity);
    setVizDensityBlur(vizFields.densityBlur);
    setVizDensityPointSize(vizFields.densityPointSize);
    setVizDensitySeparation(vizFields.densitySeparation);
    setVizDensityBackgroundDim(vizFields.densityBackgroundDim);
    setVizDensityGroupBy(vizFields.densityGroupByColumn);
    setVizDensityWeightCol(vizFields.densityWeightColumn);
    setVizDensityShowLegend(vizFields.densityShowLegend);
    setVizDensityPointRadius(vizFields.densityPointRadius);
    setVizDensitySelectedGroups(vizFields.densitySelectedGroups);
    setVizDensityClusterEnabled(vizFields.densityClusterEnabled === true);
    setVizDensityClusterRadiusPx(
      vizFields.densityClusterRadiusPx != null ? vizFields.densityClusterRadiusPx : 25
    );
    setVizDensityClusterMerge(vizFields.densityClusterMerge || 'count');
    setVizDensityClusterShowCount(vizFields.densityClusterShowCount === true);
    setVizDensityClusterColorByCount(vizFields.densityClusterColorByCount === true);
    setVizDensityTooltipEnabled(vizFields.densityTooltipEnabled === true);
    setVizDensityTooltipColumns(
      Array.isArray(vizFields.densityTooltipColumns) ? vizFields.densityTooltipColumns : []
    );
    setVizDensityTooltipDetail(vizFields.densityTooltipDetail || null);
    setVizDensityPositionMode(vizFields.densityPositionMode === 'manual' ? 'manual' : 'columns');
    setVizDensityPositionKeyColumn(vizFields.densityPositionKeyColumn || '');
    setVizDensityManualPositions(
      vizFields.densityManualPositions && typeof vizFields.densityManualPositions === 'object'
        ? vizFields.densityManualPositions
        : {}
    );
    setVizDensityMetricColumns(
      Array.isArray(vizFields.densityMetricColumns) ? vizFields.densityMetricColumns : []
    );
    setVizDensityShowMarkerLabels(vizFields.densityShowMarkerLabels === true);
    setVizDensityMarkerEnumColumn(vizFields.densityMarkerEnumColumn || '');
    setVizDensityMarkerShape(vizFields.densityMarkerShape || 'circle');
    setVizDensityMarkerValueColumn(vizFields.densityMarkerValueColumn || '');
    setVizDensityMarkerValueAggregation(vizFields.densityMarkerValueAggregation || 'first');
    setVizDensityMarkerLabelColumn(vizFields.densityMarkerLabelColumn || '');
    setVizDensityMarkerFill(vizFields.densityMarkerFill === 'none' ? 'none' : 'value');
    setVizDensityMarkerFit(vizFields.densityMarkerFit === 'marker' ? 'marker' : 'text');
    setVizDensityMarkerSize(
      ['small', 'medium', 'large'].includes(vizFields.densityMarkerSize) ? vizFields.densityMarkerSize : 'medium'
    );
    setVizDensityMarkerColor(vizFields.densityMarkerColor || '#ffffff');
    setVizDensityImageWidth(vizFields.densityImageWidth);
    setVizDensityImageHeight(vizFields.densityImageHeight);
    setVizDensityPositionColumnsCommitted(
      vizFields.densityPositionColumnsCommitted === true ||
        isNonEmptyManualPositions(vizFields.densityManualPositions)
    );
  }, []);

  const axesImageDensityConfig = useMemo(
    () =>
      buildImageDensityConfigFromViz(
        {
          backgroundImageUrl: vizBgImageUrl,
          xField: imageDensityAxisForSpec(vizXField, IMAGE_DENSITY_AXIS_AUTO_X),
          yField: imageDensityAxisForSpec(vizYField, IMAGE_DENSITY_AXIS_AUTO_Y),
          densityRenderMode: vizDensityRenderMode,
          densityHeatmapStyle: vizDensityHeatmapStyle,
          densityIntensity: vizDensityIntensity,
          densityOverlayOpacity: vizDensityOverlayOpacity,
          densityBlur: vizDensityBlur,
          densityPointSize: vizDensityPointSize,
          densitySeparation: vizDensitySeparation,
          densityBackgroundDim: vizDensityBackgroundDim,
          densityGroupByColumn: vizDensityGroupBy,
          densityWeightColumn: vizDensityWeightCol,
          densityShowLegend: vizDensityShowLegend,
          densityPointRadius: vizDensityPointRadius,
          densitySelectedGroups: vizDensitySelectedGroups,
          densityClusterEnabled: vizDensityClusterEnabled,
          densityClusterRadiusPx: vizDensityClusterRadiusPx,
          densityClusterMerge: vizDensityClusterMerge,
          densityClusterShowCount: vizDensityClusterShowCount,
          densityClusterColorByCount: vizDensityClusterColorByCount,
          densityTooltipEnabled: vizDensityTooltipEnabled,
          densityTooltipColumns: vizDensityTooltipColumns,
          densityTooltipDetail: vizDensityTooltipDetail,
          densityPositionMode: vizDensityPositionMode,
          densityPositionKeyColumn: vizDensityPositionKeyColumn,
          densityManualPositions: vizDensityManualPositions,
          densityMetricColumns: vizDensityMetricColumns,
          densityShowMarkerLabels: vizDensityShowMarkerLabels,
          densityMarkerEnumColumn: vizDensityMarkerEnumColumn,
          densityMarkerShape: vizDensityMarkerShape,
          densityMarkerValueColumn: vizDensityMarkerValueColumn,
          densityMarkerValueAggregation: vizDensityMarkerValueAggregation,
          densityMarkerLabelColumn: vizDensityMarkerLabelColumn,
          densityMarkerFill: vizDensityMarkerFill,
          densityMarkerFit: vizDensityMarkerFit,
          densityMarkerSize: vizDensityMarkerSize,
          densityMarkerColor: vizDensityMarkerColor,
          densityImageWidth: vizDensityImageWidth,
          densityImageHeight: vizDensityImageHeight,
          densityPositionColumnsCommitted: vizDensityPositionColumnsCommitted,
        },
        {
          xColumn: effectiveImageDensityAxis(vizXField, 'x', IMAGE_DENSITY_AXIS_AUTO_X),
          yColumn: effectiveImageDensityAxis(vizYField, 'y', IMAGE_DENSITY_AXIS_AUTO_Y),
        }
      ),
    [
      vizBgImageUrl,
      vizXField,
      vizYField,
      vizDensityRenderMode,
      vizDensityHeatmapStyle,
      vizDensityIntensity,
      vizDensityOverlayOpacity,
      vizDensityBlur,
      vizDensityPointSize,
      vizDensitySeparation,
      vizDensityBackgroundDim,
      vizDensityGroupBy,
      vizDensityWeightCol,
      vizDensityShowLegend,
      vizDensityPointRadius,
      vizDensitySelectedGroups,
      vizDensityClusterEnabled,
      vizDensityClusterRadiusPx,
      vizDensityClusterMerge,
      vizDensityClusterShowCount,
      vizDensityClusterColorByCount,
      vizDensityTooltipEnabled,
      vizDensityTooltipColumns,
      vizDensityTooltipDetail,
      vizDensityPositionMode,
      vizDensityPositionKeyColumn,
      vizDensityManualPositions,
      vizDensityMetricColumns,
      vizDensityShowMarkerLabels,
      vizDensityMarkerEnumColumn,
      vizDensityMarkerShape,
      vizDensityMarkerValueColumn,
    vizDensityMarkerValueAggregation,
      vizDensityMarkerLabelColumn,
      vizDensityMarkerFill, vizDensityMarkerFit, vizDensityMarkerSize, vizDensityMarkerColor,
      vizDensityImageWidth,
      vizDensityImageHeight,
      vizDensityPositionColumnsCommitted,
    ]
  );

  useEffect(() => {
    if (vizChartType !== 'imageDensity' || vizDensityPositionMode !== 'manual') {
      setHeatmapPositionDraft(null);
    }
  }, [vizChartType, vizDensityPositionMode]);

  useEffect(() => {
    if (!isNonEmptyManualPositions(vizDensityManualPositions)) return;
    if (!vizDensityPositionColumnsCommitted) {
      setVizDensityPositionColumnsCommitted(true);
    }
    const inferred = inferPositionKeyColumn(
      preview?.data || [],
      vizDensityManualPositions,
      vizDensityPositionKeyColumn
    );
    if (inferred && inferred !== vizDensityPositionKeyColumn) {
      setVizDensityPositionKeyColumn(inferred);
    }
  }, [
    preview?.data,
    vizDensityManualPositions,
    vizDensityPositionKeyColumn,
    vizDensityPositionColumnsCommitted,
  ]);

  const heatmapTableOverlay = useMemo(() => {
    const baseData = preview?.data || [];
    const editing = Boolean(heatmapPositionDraft?.editing);
    const committed =
      vizDensityPositionColumnsCommitted || isNonEmptyManualPositions(vizDensityManualPositions);
    if (!editing && !committed) {
      return { data: baseData, columns: [] };
    }
    const positions = editing
      ? heatmapPositionDraft?.positions || {}
      : vizDensityManualPositions;
    const positionKeyColumn = inferPositionKeyColumn(
      baseData,
      positions,
      heatmapPositionDraft?.positionKeyColumn || vizDensityPositionKeyColumn
    );
    if (!positionKeyColumn) {
      return { data: baseData, columns: [] };
    }
    return {
      data: attachHeatmapPositionColumns(baseData, {
        positionKeyColumn,
        positions,
      }),
      columns: heatmapOverlayColumns({ temporary: editing }),
    };
  }, [
    preview?.data,
    heatmapPositionDraft,
    vizDensityPositionColumnsCommitted,
    vizDensityManualPositions,
    vizDensityPositionKeyColumn,
  ]);

  const chartWidgetData = useMemo(() => {
    let base = applyCalculatedColumns(chartWidgetPreview?.data || [], tableCalculatedColumns);
    if (vizChartType === 'kpi' && isCalculatedColumnKey(vizYField)) {
      const def = normalizeCalculatedColumns(tableCalculatedColumns).find((item) => item.key === vizYField);
      if (def && def.op !== 'concat') base = sumCalculatedColumn(base, vizYField);
    }
    const editing = Boolean(heatmapPositionDraft?.editing);
    const committed =
      vizDensityPositionColumnsCommitted || isNonEmptyManualPositions(vizDensityManualPositions);
    if (!editing && !committed) return base;
    const positions = editing ? heatmapPositionDraft?.positions || {} : vizDensityManualPositions;
    const positionKeyColumn = inferPositionKeyColumn(
      base,
      positions,
      heatmapPositionDraft?.positionKeyColumn || vizDensityPositionKeyColumn
    );
    if (!positionKeyColumn) return base;
    return attachHeatmapPositionColumns(base, {
      positionKeyColumn,
      positions,
    });
  }, [
    chartWidgetPreview?.data,
    tableCalculatedColumns,
    vizChartType,
    vizYField,
    heatmapPositionDraft,
    vizDensityPositionColumnsCommitted,
    vizDensityManualPositions,
    vizDensityPositionKeyColumn,
  ]);

  const tablePlusColumnKeys = useMemo(() => {
    if (chartFeedTableData?.[0]) return Object.keys(chartFeedTableData[0]);
    if (chartFeed === 'episodes' && episodesTableActive) {
      return episodeOutputFields(episodeAnalysis, episodeGroupCols).map((f) => f.value);
    }
    if (chartFeed === 'zones' && zonesTableActive) {
      return spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols).map((f) => f.value);
    }
    if (preview?.columns?.length) return preview.columns;
    if (preview?.data?.[0]) return Object.keys(preview.data[0]);
    return [];
  }, [
    chartFeedTableData,
    chartFeed,
    episodesTableActive,
    zonesTableActive,
    episodeAnalysis,
    episodeGroupCols,
    spatialZoneAnalysis,
    spatialZoneGroupCols,
    preview,
  ]);
  const tablePlusColumnLabels = useMemo(() => {
    const labels = {};
    if (chartFeed === 'episodes' && episodesTableActive) {
      episodeOutputFields(episodeAnalysis, episodeGroupCols).forEach((f) => {
        labels[f.value] = String(f.value).replace(/_/g, ' ');
      });
    } else if (chartFeed === 'zones' && zonesTableActive) {
      spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols).forEach((f) => {
        labels[f.value] = String(f.value).replace(/_/g, ' ');
      });
    } else {
      resolvePlainColumnAliases(columns).forEach((alias, i) => {
        if (alias) labels[alias] = headerLabel(columns[i], context) || alias;
      });
    }
    tablePlusColumnKeys.forEach((key) => {
      if (!labels[key]) labels[key] = String(key).replace(/_/g, ' ');
    });
    return labels;
  }, [
    chartFeed,
    episodesTableActive,
    zonesTableActive,
    episodeAnalysis,
    episodeGroupCols,
    spatialZoneAnalysis,
    spatialZoneGroupCols,
    columns,
    context,
    tablePlusColumnKeys,
  ]);
  const tablePlusInstantColumns = useMemo(
    () => (chartFeed === 'base' ? resolveTablePlusInstantColumns(columns) : []),
    [chartFeed, columns]
  );
  const tablePlusOptionsPanel = vizChartType === 'tablePlus' ? (
    <Paper
      variant="outlined"
      sx={{
        width: '20%',
        minWidth: 180,
        flexShrink: 0,
        alignSelf: 'stretch',
        p: 1,
        bgcolor: 'grey.50',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        Tabla+
      </Typography>
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={tablePlusApplyTimezone && tablePlusInstantColumns.length > 0}
            disabled={!tablePlusInstantColumns.length}
            onChange={(e) => setTablePlusApplyTimezone(e.target.checked)}
          />
        }
        label="Zona horaria del dashboard"
      />
      <Typography variant="caption" color="text.secondary">
        {tablePlusInstantColumns.length
          ? `Convierte a ${timezone || 'la zona del dashboard'} las fechas calculadas con mínimo o máximo (${tablePlusInstantColumns
              .map((key) => tablePlusColumnLabels[key] || key)
              .join(', ')}). El resto de las fechas ya viene en esa zona.`
          : chartFeed === 'base'
            ? `Todas las fechas de esta tabla ya vienen en ${timezone || 'la zona del dashboard'}.`
            : `${chartFeedTableLabel} ya viene en ${timezone || 'la zona del dashboard'}.`}
      </Typography>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mt: 0.5 }}>
        Columnas
      </Typography>
      {!tablePlusColumnKeys.length && (
        <Typography variant="caption" color="text.secondary">
          {chartFeed === 'base'
            ? 'La vista previa de la tabla llena esta lista.'
            : `La vista previa de ${chartFeedTableLabel.toLowerCase()} llena esta lista.`}
        </Typography>
      )}
      {tablePlusColumnKeys.map((key) => {
        const visible = tablePlusVisibleColumns == null || tablePlusVisibleColumns.includes(key);
        return (
          <Box key={key}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={visible}
                  onChange={() => {
                    const current = tablePlusVisibleColumns == null
                      ? [...tablePlusColumnKeys]
                      : tablePlusVisibleColumns.filter((col) => tablePlusColumnKeys.includes(col));
                    const next = current.includes(key)
                      ? current.filter((col) => col !== key)
                      : [...current, key];
                    setTablePlusVisibleColumns(next.length === tablePlusColumnKeys.length ? null : next);
                  }}
                />
              }
              label={<Typography variant="caption">Ver {tablePlusColumnLabels[key] || key}</Typography>}
            />
            <FormControlLabel
              sx={{ ml: 2 }}
              control={
                <Checkbox
                  size="small"
                  checked={tablePlusGroupColumns.includes(key)}
                  onChange={() => {
                    setTablePlusGroupColumns((prev) =>
                      prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]
                    );
                  }}
                />
              }
              label={<Typography variant="caption">Agrupar en tarjeta</Typography>}
            />
          </Box>
        );
      })}
    </Paper>
  ) : null;

  const narrativeHasPlaceholder = hasNarrativePlaceholder(vizNarrativeTemplate);
  const narrativeBlockedByInertFilter =
    vizChartType === 'narrative' &&
    Boolean(vizNarrativeTemplate?.trim()) &&
    !narrativeHasPlaceholder &&
    filters.some(isFilterActive);

  const narrativeOptionsPanel = vizChartType === 'narrative' ? (
    <Paper
      variant="outlined"
      sx={{
        width: '20%',
        minWidth: 220,
        flexShrink: 0,
        alignSelf: 'stretch',
        p: 1,
        bgcolor: 'grey.50',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        Texto narrativo
      </Typography>
      <TextField
        size="small"
        multiline
        minRows={4}
        placeholder="Ej: El activo registra {{total}} eventos: {{seguridad}} de seguridad ({{seguridad_pct}}%) y {{no_seguridad}} de no seguridad ({{no_seguridad_pct}}%)."
        value={vizNarrativeTemplate}
        onChange={(e) => setVizNarrativeTemplate(e.target.value)}
      />
      {narrativeBlockedByInertFilter && (
        <Alert severity="warning" sx={{ py: 0 }}>
          Este texto no tiene marcadores {'{{...}}'}, así que no depende de ningún dato: los
          filtros configurados no cambiarían el resultado. Quita los filtros o agrega un
          marcador para poder publicar.
        </Alert>
      )}
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mt: 0.5 }}>
        Marcadores disponibles
      </Typography>
      {!narrativePlaceholders.length && (
        <Typography variant="caption" color="text.secondary">
          La vista previa de datos agrupados llena esta lista.
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {narrativePlaceholders.map((p) => (
          <Chip
            key={p.key}
            size="small"
            label={`{{${p.key}}} = ${p.valueText}`}
            onClick={() => setVizNarrativeTemplate((prev) => `${prev}{{${p.key}}}`)}
          />
        ))}
      </Box>
      {vizNarrativeTemplate.trim() && (
        <>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mt: 0.5 }}>
            Vista previa
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {narrativePreviewSegments.map((seg, i) =>
              seg.type === 'value' ? (
                <Box key={i} component="strong" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {seg.value}
                </Box>
              ) : (
                <React.Fragment key={i}>{seg.value}</React.Fragment>
              )
            )}
          </Typography>
        </>
      )}
    </Paper>
  ) : null;

  const axesSeriesPanel =
    vizChartType === 'tablePlus'
      ? tablePlusOptionsPanel
      : vizChartType === 'narrative'
        ? narrativeOptionsPanel
        : vizChartType !== 'table' ? (
      <Paper
        variant="outlined"
        sx={{
          width: '20%',
          minWidth: 140,
          flexShrink: 0,
          alignSelf: 'stretch',
          p: 1,
          bgcolor: 'grey.50',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
        }}
      >
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          Ejes y series del gráfico
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {vizChartType === 'intervals' && (
            <>
              {[
                { label: 'Inicio', value: vizIntervalStartField, set: setVizIntervalStartField, auto: 'Automático («inicio»)' },
                { label: 'Fin', value: vizIntervalEndField, set: setVizIntervalEndField, auto: 'Automático («fin»)' },
                { label: 'Serie / color', value: vizIntervalSeriesField, set: setVizIntervalSeriesField, auto: 'Ninguna' },
              ].map((f) => (
                <FormControl key={f.label} size="small" fullWidth>
                  <InputLabel>{f.label}</InputLabel>
                  <Select label={f.label} value={f.value} onChange={(e) => f.set(e.target.value)}>
                    <MenuItem value="">{f.auto}</MenuItem>
                    {densityAxisFieldOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
            </>
          )}
          {vizChartType === 'imageCarousel' && (
            <>
              {[
                {
                  label: 'Tipo de sensor',
                  value: vizCarouselSensorTypeField,
                  set: setVizCarouselSensorTypeField,
                  defaultLogical: DEFAULT_IMAGE_CAROUSEL.sensorTypeColumn,
                  auto: `Automático («${DEFAULT_IMAGE_CAROUSEL.sensorTypeColumn}»)`,
                },
                {
                  label: 'Sensor',
                  value: vizCarouselSensorIdField,
                  set: setVizCarouselSensorIdField,
                  defaultLogical: DEFAULT_IMAGE_CAROUSEL.sensorIdColumn,
                  auto: `Automático («${DEFAULT_IMAGE_CAROUSEL.sensorIdColumn}»)`,
                },
                {
                  label: 'Contenido de la detección',
                  value: vizCarouselContentField,
                  set: setVizCarouselContentField,
                  defaultLogical: DEFAULT_IMAGE_CAROUSEL.contentColumn,
                  auto: `Automático («${DEFAULT_IMAGE_CAROUSEL.contentColumn}»)`,
                },
                {
                  label: 'Dentro de zona',
                  value: vizCarouselInZoneField,
                  set: setVizCarouselInZoneField,
                  defaultLogical: '',
                  auto: 'Ninguna',
                },
              ].map((f) => (
                <FormControl key={f.label} size="small" fullWidth>
                  <InputLabel>{f.label}</InputLabel>
                  <Select
                    label={f.label}
                    value={carouselColumnSelectValue(f.value, f.defaultLogical, densityAxisFieldOptionValues)}
                    onChange={(e) => f.set(e.target.value)}
                  >
                    <MenuItem value="">{f.auto}</MenuItem>
                    {densityAxisFieldOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
              <Tooltip title="Filtra por el valor de class en cada detección" placement="right">
                <Box sx={{ width: '100%', alignSelf: 'stretch' }}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Filtrar por clase (opcional)"
                    value={vizCarouselCropClassFilter}
                    onChange={(e) => setVizCarouselCropClassFilter(e.target.value)}
                  />
                </Box>
              </Tooltip>
              <Box
                sx={{
                  pl: 1.25,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.25,
                  '& .MuiFormControlLabel-label': { fontSize: '0.75rem', lineHeight: 1.35 },
                }}
              >
                <FormControlLabel
                  sx={{ ml: 0, alignItems: 'flex-start' }}
                  control={
                    <Switch
                      size="small"
                      checked={vizCarouselSingleImageMode}
                      onChange={(e) => setVizCarouselSingleImageMode(e.target.checked)}
                    />
                  }
                  label="Una imagen a la vez (avance automático cada 5 segundos)"
                />
                <Tooltip
                  title="Muestra solo el recorte de cada detección (una miniatura por caja) en vez de la foto completa con las cajas superpuestas."
                  placement="right"
                >
                  <FormControlLabel
                    sx={{ ml: 0, alignItems: 'flex-start', width: 'fit-content' }}
                    control={
                      <Switch
                        size="small"
                        checked={vizCarouselCropMode}
                        onChange={(e) => setVizCarouselCropMode(e.target.checked)}
                      />
                    }
                    label="Mostrar solo el recorte de cada detección"
                  />
                </Tooltip>
                <Tooltip
                  title="No aplica a sensores de cruce de línea: la zona configurada ahí es una línea de 2 puntos, no un área. Solo filtra sensores con un polígono real (3 puntos o más)."
                  placement="right"
                >
                  <FormControlLabel
                    sx={{ ml: 0, alignItems: 'flex-start', width: 'fit-content' }}
                    control={
                      <Switch
                        size="small"
                        checked={vizCarouselShowOnlyInZone}
                        onChange={(e) => setVizCarouselShowOnlyInZone(e.target.checked)}
                      />
                    }
                    label="Mostrar solo dentro de zona"
                  />
                </Tooltip>
                {vizCarouselShowOnlyInZone && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
                    {vizCarouselInZoneField
                      ? `Usa la columna "${vizCarouselInZoneField}" para decidir qué filas quedan dentro de la zona.`
                      : 'Sin columna de zona, se calcula con el polígono real configurado en el sensor. No aplica a sensores de cruce de línea.'}
                  </Typography>
                )}
              </Box>
            </>
          )}
          {vizChartType !== 'kpi' &&
            vizChartType !== 'pie' &&
            vizChartType !== 'imageDensity' &&
            vizChartType !== 'intervals' &&
            vizChartType !== 'imageCarousel' && (
            <FormControl size="small" fullWidth>
              <InputLabel>
                {vizChartType === 'imageDensity'
                  ? 'Columna X'
                  : vizChartType === 'geoHeatmap'
                    ? 'Latitud'
                    : 'Eje X'}
              </InputLabel>
              <Select
                label={
                  vizChartType === 'imageDensity'
                    ? 'Columna X'
                    : vizChartType === 'geoHeatmap'
                      ? 'Latitud'
                      : 'Eje X'
                }
                value={
                  isPointDatasetChartType(vizChartType)
                    ? normalizeImageDensityAxisSelectValue(
                        vizXField,
                        'x',
                        vizChartType === 'geoHeatmap'
                          ? densityAxisFieldOptionValues
                          : densityAxisFieldOptionValues
                      )
                    : vizXField
                }
                renderValue={
                  isPointDatasetChartType(vizChartType)
                    ? (v) =>
                        vizChartType === 'geoHeatmap'
                          ? v === GEO_HEATMAP_AUTO_LAT
                            ? 'Automático (columna «lat»)'
                            : imageDensityAxisSelectLabel(v, 'x', densityAxisOptionLabelByValue)
                          : imageDensityAxisSelectLabel(v, 'x', densityAxisOptionLabelByValue)
                    : undefined
                }
                onChange={(e) => setVizXField(e.target.value)}
              >
                {isPointDatasetChartType(vizChartType) ? (
                  <MenuItem
                    value={vizChartType === 'geoHeatmap' ? GEO_HEATMAP_AUTO_LAT : IMAGE_DENSITY_AXIS_AUTO_X}
                  >
                    {vizChartType === 'geoHeatmap' ? 'Automático (columna «lat»)' : 'Automático (columna «x»)'}
                  </MenuItem>
                ) : (
                  <MenuItem value="">(auto)</MenuItem>
                )}
                {(isPointDatasetChartType(vizChartType) ? densityAxisFieldOptions : resultFieldOptions).map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {vizChartType === 'pie' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Leyenda</InputLabel>
              <Select label="Leyenda" value={vizXField} onChange={(e) => setVizXField(e.target.value)}>
                <MenuItem value="">(auto)</MenuItem>
                {pieLegendFieldOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {vizChartType !== 'table' &&
            vizChartType !== 'kpi' &&
            !isRawRowsChartType(vizChartType) &&
            (episodesChartActive || vizXColumnInfo?.isDate) && (
              <>
                {!episodesChartActive && (
                  <FormControl size="small" fullWidth>
                    <InputLabel>{vizChartType === 'pie' ? 'Agrupar leyenda' : 'Agrupar eje X'}</InputLabel>
                    <Select
                      label={vizChartType === 'pie' ? 'Agrupar leyenda' : 'Agrupar eje X'}
                      value={vizXColumnInfo.column.dateBucket || 'raw'}
                      onChange={(e) => {
                        const nextBucket = e.target.value;
                        patchColumnAt(vizXColumnInfo.index, { dateBucket: nextBucket });
                        // "Calcular acumulado" solo tiene sentido con un eje X de fecha real;
                        // si se cambia a un bucket numerico (Hora del dia, etc.), el checkbox
                        // se esconde en la UI pero el estado seguia activo por debajo y se
                        // guardaba igual. Se limpia aca para que no quede como flag fantasma.
                        if (PIVOTABLE_DATE_BUCKETS.includes(nextBucket)) {
                          setVizSeriesMetricCumulative(false);
                        }
                      }}
                    >
                      {(context?.dateBuckets || []).map((b) => (
                        <MenuItem key={b.value} value={b.value}>
                          {b.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <DateDisplayFormatControl
                  format={
                    episodesChartActive ? episodeChartDateFormat : vizXColumnInfo?.column?.dateDisplayFormat
                  }
                  dateBucket={
                    episodesChartActive
                      ? chartPreview?.mapping?.xDateBucket || 'day'
                      : vizXColumnInfo?.column?.dateBucket
                  }
                  onChange={(dateDisplayFormat) => {
                    if (episodesChartActive) {
                      patchEpisodeDateFormat(dateDisplayFormat);
                      return;
                    }
                    patchColumnAt(vizXColumnInfo.index, {
                      dateDisplayFormat:
                        !dateDisplayFormat || dateDisplayFormat === 'auto' ? undefined : dateDisplayFormat,
                    });
                  }}
                  selectLabel={vizChartType === 'pie' ? 'Formato leyenda' : 'Formato eje X'}
                  showHelper={false}
                />
              </>
            )}
          {vizChartType !== 'imageDensity' && vizChartType !== 'intervals' && vizChartType !== 'imageCarousel' && (
          <FormControl size="small" fullWidth>
            <InputLabel>
              {vizChartType === 'kpi'
                ? 'Medida'
                : vizChartType === 'imageDensity'
                  ? 'Columna Y'
                  : vizChartType === 'geoHeatmap'
                    ? 'Longitud'
                    : vizChartType === 'pie'
                      ? 'Valor'
                      : 'Eje Y'}
            </InputLabel>
              <Select
                label={
                  vizChartType === 'kpi'
                    ? 'Medida'
                    : vizChartType === 'imageDensity'
                      ? 'Columna Y'
                      : vizChartType === 'geoHeatmap'
                        ? 'Longitud'
                        : vizChartType === 'pie'
                          ? 'Valor'
                          : 'Eje Y'
                }
                value={
                  isPointDatasetChartType(vizChartType)
                    ? normalizeImageDensityAxisSelectValue(vizYField, 'y', densityAxisFieldOptionValues)
                    : vizYField
                }
                renderValue={
                  isPointDatasetChartType(vizChartType)
                    ? (v) =>
                        vizChartType === 'geoHeatmap'
                          ? v === GEO_HEATMAP_AUTO_LNG
                            ? 'Automático (columna «lng»)'
                            : imageDensityAxisSelectLabel(v, 'y', densityAxisOptionLabelByValue)
                          : imageDensityAxisSelectLabel(v, 'y', densityAxisOptionLabelByValue)
                    : undefined
                }
                onChange={(e) => setVizYField(e.target.value)}
              >
                {isPointDatasetChartType(vizChartType) ? (
                  <MenuItem
                    value={vizChartType === 'geoHeatmap' ? GEO_HEATMAP_AUTO_LNG : IMAGE_DENSITY_AXIS_AUTO_Y}
                  >
                    {vizChartType === 'geoHeatmap' ? 'Automático (columna «lng»)' : 'Automático (columna «y»)'}
                  </MenuItem>
                ) : (
                  <MenuItem value="">(auto)</MenuItem>
                )}
                {(isPointDatasetChartType(vizChartType)
                  ? densityAxisFieldOptions
                  : vizChartType === 'pie'
                    ? pieValueFieldOptions
                    : resultFieldOptions
                ).map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {vizChartType === 'kpi' && !postProcessChartActive && (
            <FormControl size="small" fullWidth>
              <InputLabel>Agregación</InputLabel>
              <Select
                label="Agregación"
                value={vizKpiAggregation || vizYColumnInfo?.column?.aggregation || 'sum'}
                onChange={(e) => setVizKpiAggregation(e.target.value)}
              >
                {kpiAggregationOptions.map((a) => (
                  <MenuItem key={a.value} value={a.value}>
                    {a.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {vizChartType === 'kpi' && !postProcessChartActive && (
            <FormControl size="small" fullWidth>
              <InputLabel>Tamaño de fuente del valor</InputLabel>
              <Select
                label="Tamaño de fuente del valor"
                value={vizKpiValueFontSize}
                onChange={(e) => setVizKpiValueFontSize(e.target.value)}
              >
                <MenuItem value="large">Grande (por defecto)</MenuItem>
                <MenuItem value="medium">Mediano</MenuItem>
                <MenuItem value="small">Pequeño</MenuItem>
              </Select>
            </FormControl>
          )}
          {vizChartType === 'kpi' && !postProcessChartActive && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={vizKpiComparePriorYear}
                  onChange={(e) => setVizKpiComparePriorYear(e.target.checked)}
                />
              }
              label="Comparar con: Año anterior"
            />
          )}
          {vizChartType === 'kpi' && vizKpiComparePriorYear && (
            <Alert severity="info" sx={{ py: 0.25 }}>
              Se compara contra el mismo filtro de fecha, un año atrás. Requiere que el gráfico tenga
              un filtro sobre una columna de fecha (fijo o dinámico); si no hay ninguno, no se muestra
              comparación.
            </Alert>
          )}
          {(vizChartType === 'bar' || vizChartType === 'line') && !postProcessChartActive && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Comparar con periodo anterior</InputLabel>
                <Select
                  label="Comparar con periodo anterior"
                  value={vizComparePeriod}
                  onChange={(e) => {
                    const next = e.target.value;
                    setVizComparePeriod(next);
                    // Mutuamente excluyente con "Comparar serie"/"Contra serie" y con
                    // "Comparar con fecha": si no se limpian, quedan guardadas en el spec y
                    // pueden activarse solas si esta comparacion de periodo llega a fallar en
                    // algun request puntual.
                    if (next) {
                      setVizSeriesMetricA('');
                      setVizSeriesMetricB('');
                      setVizSeriesMetricDivideBy('');
                      setVizSeriesMetricCumulative(false);
                      setVizCustomDateCompareBaseStart('');
                      setVizCustomDateCompareParam('');
                    }
                  }}
                >
                  <MenuItem value="">Ninguna</MenuItem>
                  <MenuItem value="week">Semana anterior</MenuItem>
                  <MenuItem value="month">Mes anterior</MenuItem>
                  <MenuItem value="year">Año anterior</MenuItem>
                </Select>
              </FormControl>
              <Tooltip
                title={
                  'Empareja cada día del filtro de fecha actual con el día del periodo anterior ' +
                  'elegido que cae en el mismo día de la semana, ajustando hasta 3 días si el ' +
                  'desplazamiento exacto (una semana, un mes o un año) no cae en el mismo día. ' +
                  'Requiere un filtro de rango de fechas (fijo o dinámico) y que el Eje X sea una ' +
                  'fecha real agrupada por día (no "Día del mes"); reemplaza el resultado por la ' +
                  'comparación día con día, ignorando "Serie/color". Un día sin datos en cualquiera ' +
                  'de los dos periodos queda sin su par.'
                }
              >
                <IconButton size="small">
                  <HelpOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
          {(vizChartType === 'bar' || vizChartType === 'line') && vizComparePeriod && (
            <Alert severity="info" sx={{ py: 0.25 }}>
              Compara el periodo actual con{' '}
              {COMPARE_PERIOD_ALERT_LABEL[vizComparePeriod] || COMPARE_PERIOD_ALERT_LABEL.year}, alineando
              los mismos días de la semana.
            </Alert>
          )}
          {(vizChartType === 'bar' || vizChartType === 'line') &&
            !postProcessChartActive &&
            !vizComparePeriod &&
            !vizSeriesMetricA &&
            customDateCompareBaseOptions.length > 0 &&
            customDateCompareParamOptions.length > 0 && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Fecha base</InputLabel>
                  <Select
                    label="Fecha base"
                    value={vizCustomDateCompareBaseStart}
                    onChange={(e) => {
                      const next = e.target.value;
                      setVizCustomDateCompareBaseStart(next);
                      if (next) {
                        setVizComparePeriod('');
                        setVizSeriesMetricA('');
                        setVizSeriesMetricB('');
                        setVizSeriesMetricDivideBy('');
                        setVizSeriesMetricCumulative(false);
                      }
                    }}
                  >
                    <MenuItem value="">Ninguna</MenuItem>
                    {customDateCompareBaseOptions.map((o) => (
                      <MenuItem key={o.key} value={o.paramNameStart}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FormControl size="small" fullWidth disabled={!vizCustomDateCompareBaseStart}>
                    <InputLabel>Comparar con fecha</InputLabel>
                    <Select
                      label="Comparar con fecha"
                      value={vizCustomDateCompareParam}
                      onChange={(e) => {
                        const next = e.target.value;
                        setVizCustomDateCompareParam(next);
                        if (next) {
                          setVizComparePeriod('');
                          setVizSeriesMetricA('');
                          setVizSeriesMetricB('');
                          setVizSeriesMetricDivideBy('');
                          setVizSeriesMetricCumulative(false);
                        }
                      }}
                    >
                      <MenuItem value="">Ninguna</MenuItem>
                      {customDateCompareParamOptions.map((f) => (
                        <MenuItem key={f.paramName} value={f.paramName}>
                          {`@${f.paramName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Tooltip
                    title={
                      'Vuelve a ejecutar la consulta sustituyendo el rango de fecha base por un período ' +
                      'que empieza en este valor, con la misma duración. Empareja por eje X y, si el ' +
                      'gráfico usa Series/color, por valor de serie. Diferencia y % de variación en el ' +
                      'tooltip de Actual. Sin valor, no hay segunda ejecución.'
                    }
                  >
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
          {(vizChartType === 'bar' || vizChartType === 'line') &&
            vizCustomDateCompareBaseStart &&
            vizCustomDateCompareParam && (
              <Alert severity="info" sx={{ py: 0.25 }}>
                Compara el período actual con el que empieza en «Comparar con fecha», del mismo largo
                que el rango base. Con Series/color, cada valor arma su propio par Actual/Comparación;
                sin ella, el gráfico queda con esas dos líneas.
              </Alert>
            )}
          {(vizChartType === 'bar' || vizChartType === 'line') &&
            !postProcessChartActive &&
            !vizComparePeriod &&
            !vizCustomDateCompareParam &&
            vizSeriesField && (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Comparar serie</InputLabel>
                  <Select
                    label="Comparar serie"
                    value={vizSeriesMetricA}
                    onChange={(e) => {
                      const next = e.target.value;
                      setVizSeriesMetricA(next);
                      // Mutuamente excluyente con "Comparar con fecha", mismo motivo que la
                      // exclusion de comparePeriod de arriba.
                      if (next) {
                        setVizCustomDateCompareBaseStart('');
                        setVizCustomDateCompareParam('');
                      }
                    }}
                  >
                    <MenuItem value="">Ninguna</MenuItem>
                    {distinctSeriesLabelValues.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <FormControl size="small" fullWidth disabled={!vizSeriesMetricA}>
                    <InputLabel>Contra serie</InputLabel>
                    <Select
                      label="Contra serie"
                      value={vizSeriesMetricB}
                      onChange={(e) => setVizSeriesMetricB(e.target.value)}
                    >
                      <MenuItem value="">Ninguna</MenuItem>
                      {distinctSeriesLabelValues
                        .filter((v) => v !== vizSeriesMetricA)
                        .map((v) => (
                          <MenuItem key={v} value={v}>
                            {v}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Tooltip
                    title={
                      'Compara dos valores del campo "Series/color" en la misma categoría del eje X ' +
                      '("Comparar serie" = A, "Contra serie" = B, la referencia). Calcula la diferencia ' +
                      '(A − B) y el % de variación respecto a B; se muestran al pasar el mouse sobre los ' +
                      'puntos de la serie A, o como línea aparte con el checkbox "Mostrar % de variación ' +
                      'como línea". Si no hay fila de B en ese mismo punto, no hay cálculo.'
                    }
                  >
                    <IconButton size="small">
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {vizSeriesMetricA && vizSeriesMetricB && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Dividir por</InputLabel>
                      <Select
                        label="Dividir por"
                        value={vizSeriesMetricDivideBy}
                        onChange={(e) => setVizSeriesMetricDivideBy(e.target.value)}
                      >
                        <MenuItem value="">Ninguna</MenuItem>
                        {valueColumnFieldOptions.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip
                      title={
                        'Calcula además (A − B) dividido por esta columna, como porcentaje sobre ese ' +
                        'divisor (ej. una columna numérica traída con "Seguir referencia" desde una ' +
                        'tabla relacionada). Se muestra junto a la diferencia y el % de variación en ' +
                        'el tooltip de la serie A.'
                      }
                    >
                      <IconButton size="small">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                {vizSeriesMetricA &&
                  vizSeriesMetricB &&
                  vizXColumnInfo?.column?.dateBucket &&
                  !PIVOTABLE_DATE_BUCKETS.includes(vizXColumnInfo.column.dateBucket) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={vizSeriesMetricCumulative}
                            onChange={(e) => setVizSeriesMetricCumulative(e.target.checked)}
                          />
                        }
                        label={<Typography variant="body2">Calcular acumulado</Typography>}
                      />
                      <Tooltip
                        title={
                          'Suma la diferencia (A − B) de cada punto sobre la del punto anterior en el ' +
                          'orden del eje X, reiniciando en cada día calendario. Se muestra en el tooltip ' +
                          'de la serie A (ej. un total corrido a lo largo del día, como entradas menos ' +
                          'salidas acumuladas hora a hora).'
                        }
                      >
                        <IconButton size="small">
                          <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
              </>
            )}
          {(vizChartType === 'bar' || vizChartType === 'line') &&
            (vizComparePeriod ||
              (vizSeriesMetricA && vizSeriesMetricB) ||
              (vizCustomDateCompareBaseStart && vizCustomDateCompareParam)) && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={vizShowMetricPercentLine}
                    onChange={(e) => setVizShowMetricPercentLine(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" title="Agrega el % de variación como una línea más en el gráfico, con su propio eje a la derecha (no comparte escala con el valor principal). La diferencia sigue mostrándose solo al pasar el mouse.">
                    Mostrar % de variación como línea
                  </Typography>
                }
              />
            )}
          {(vizChartType === 'bar' || vizChartType === 'line' || vizChartType === 'radar') && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Series / color</InputLabel>
                <Select
                  label="Series / color"
                  value={vizSeriesField}
                  onChange={(e) => {
                    const next = e.target.value;
                    setVizSeriesField(next);
                    const setId = seriesTimeRangeSetIdFromField(next);
                    if (setId) {
                      setVizSeriesTimeRangeColumn(
                        guessSeriesTimeRangeSourceField({
                          chartFeed,
                          episodeAnalysis,
                          episodeGroupCols,
                          spatialZoneAnalysis,
                          spatialZoneGroupCols,
                          columns,
                          filters,
                          setId,
                          context,
                          vizXField,
                          fieldAlias,
                          getColumnMeta,
                          guessSqlTimeRangeSourceField: guessTimeRangeSourceField,
                        })
                      );
                    }
                  }}
                >
                  <MenuItem value="">Ninguna</MenuItem>
                  {seriesFieldOptions.length > 0 && <ListSubheader>Columnas</ListSubheader>}
                  {seriesFieldOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                  {(context?.timeRangeSets || []).length > 0 && (
                    <ListSubheader>Rangos horarios (turnos)</ListSubheader>
                  )}
                  {(context?.timeRangeSets || []).map((s) => (
                    <MenuItem key={`tr-${s.id}`} value={`${SERIES_TIME_RANGE_PREFIX}${s.id}`}>
                      {s.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {activeSeriesTimeRangeSetId && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Hora desde columna</InputLabel>
                  <Select
                    label="Hora desde columna"
                    value={
                      vizSeriesTimeRangeColumn ||
                      datetimeFieldOptions[0]?.value ||
                      ''
                    }
                    onChange={(e) => setVizSeriesTimeRangeColumn(e.target.value)}
                  >
                    {datetimeFieldOptions.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}
          {vizChartType === 'geoHeatmap' && (
            <>
              <Divider sx={{ my: 0.25 }} />
              <GeoHeatmapSettingsFields
                config={buildGeoHeatmapConfigFromViz(
                  {
                    latField: effectiveImageDensityAxis(vizXField, 'x', GEO_HEATMAP_AUTO_LAT),
                    lngField: effectiveImageDensityAxis(vizYField, 'y', GEO_HEATMAP_AUTO_LNG),
                    densityWeightColumn: vizDensityWeightCol,
                    geoHeatmapRadius: vizGeoHeatmapRadius,
                    geoHeatmapBlur: vizGeoHeatmapBlur,
                    densityShowLegend: vizDensityShowLegend,
                  },
                  {
                    latColumn: effectiveImageDensityAxis(vizXField, 'x', GEO_HEATMAP_AUTO_LAT),
                    lngColumn: effectiveImageDensityAxis(vizYField, 'y', GEO_HEATMAP_AUTO_LNG),
                  }
                )}
                onConfigChange={(cfg) => {
                  const vizFields = geoHeatmapVizFieldsFromConfig(cfg);
                  setVizDensityWeightCol(vizFields.densityWeightColumn || '');
                  setVizGeoHeatmapRadius(vizFields.geoHeatmapRadius);
                  setVizGeoHeatmapBlur(vizFields.geoHeatmapBlur);
                  setVizDensityShowLegend(vizFields.densityShowLegend);
                }}
                fieldOptions={densityAxisFieldOptions}
              />
            </>
          )}
          {vizChartType === 'imageDensity' && (
            <>
              <Divider sx={{ my: 0.25 }} />
              <ImageDensitySettingsFields
                config={axesImageDensityConfig}
                onConfigChange={handleImageDensityConfigChange}
                data={chartFeedTableData}
                feedLabel={chartFeedTableLabel}
                xColumn={
                  spatialZonesChartActive
                    ? spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols).find(
                        (f) => f.base === 'centroid_x'
                      )?.value || 'centroid_x'
                    : effectiveImageDensityAxis(vizXField, 'x', IMAGE_DENSITY_AXIS_AUTO_X)
                }
                yColumn={
                  spatialZonesChartActive
                    ? spatialZoneOutputFields(spatialZoneAnalysis, spatialZoneGroupCols).find(
                        (f) => f.base === 'centroid_y'
                      )?.value || 'centroid_y'
                    : effectiveImageDensityAxis(vizYField, 'y', IMAGE_DENSITY_AXIS_AUTO_Y)
                }
                getAuthHeaders={getAuthHeaders}
                showImageControls
                xAxisValue={normalizeImageDensityAxisSelectValue(
                  vizXField,
                  'x',
                  densityAxisFieldOptionValues
                )}
                yAxisValue={normalizeImageDensityAxisSelectValue(
                  vizYField,
                  'y',
                  densityAxisFieldOptionValues
                )}
                onXAxisChange={setVizXField}
                onYAxisChange={setVizYField}
                axisOptions={densityAxisFieldOptions}
                xAutoValue={IMAGE_DENSITY_AXIS_AUTO_X}
                yAutoValue={IMAGE_DENSITY_AXIS_AUTO_Y}
                xAxisLabel={(v) => imageDensityAxisSelectLabel(v, 'x', densityAxisOptionLabelByValue)}
                yAxisLabel={(v) => imageDensityAxisSelectLabel(v, 'y', densityAxisOptionLabelByValue)}
                columnDateFormats={feedTemporalMaps.columnDateFormats}
                columnDateBuckets={feedTemporalMaps.columnDateBuckets}
              />
            </>
          )}
          {vizChartType === 'pie' && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel>Ubicación etiquetas</InputLabel>
                <Select
                  label="Ubicación etiquetas"
                  value={vizPieLabelPosition}
                  onChange={(e) => setVizPieLabelPosition(normalizePieLabelPosition(e.target.value))}
                >
                  {PIE_LABEL_POSITION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                {isPieLegendLabelMode(vizPieLabelPosition)
                  ? vizPieLabelPosition === 'legendAndChart'
                    ? 'Datos completos en leyenda; en el gráfico solo segmentos ≥7%'
                    : 'Datos en leyenda (junto al nombre)'
                  : 'Datos en el gráfico'}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pl: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={vizPieShowValueLabel}
                      onChange={(e) => setVizPieShowValueLabel(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Valor</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={vizPieShowPercentLabel}
                      onChange={(e) => setVizPieShowPercentLabel(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Porcentaje</Typography>}
                />
                {vizPieLabelPosition === 'outside' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={vizPieShowNameLabel}
                        onChange={(e) => setVizPieShowNameLabel(e.target.checked)}
                      />
                    }
                    label={<Typography variant="body2">Nombre en callout</Typography>}
                  />
                )}
              </Box>
              {isPieLegendLabelMode(vizPieLabelPosition) && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Tamaño leyenda</InputLabel>
                  <Select
                    label="Tamaño leyenda"
                    value={vizPieLegendFontSize}
                    onChange={(e) => setVizPieLegendFontSize(normalizePieLegendFontSize(e.target.value))}
                  >
                    {PIE_LEGEND_FONT_SIZE_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" fullWidth>
                <InputLabel>Ubicación leyenda</InputLabel>
                <Select
                  label="Ubicación leyenda"
                  value={vizLegendDisplay ? vizLegendPosition : 'hidden'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'hidden') {
                      setVizLegendDisplay(false);
                    } else {
                      setVizLegendDisplay(true);
                      setVizLegendPosition(v);
                    }
                  }}
                >
                  <MenuItem value="hidden">Oculta</MenuItem>
                  <MenuItem value="left">Izquierda</MenuItem>
                  <MenuItem value="top">Arriba</MenuItem>
                  <MenuItem value="bottom">Abajo</MenuItem>
                  <MenuItem value="right">Derecha</MenuItem>
                </Select>
              </FormControl>
            </>
          )}
          {(vizChartType === 'bar' || vizChartType === 'line') && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.25 }}>
              <TextField
                size="small"
                fullWidth
                label="Título eje X"
                value={vizXAxisTitle}
                onChange={(e) => setVizXAxisTitle(e.target.value)}
                placeholder="Opcional"
              />
              <TextField
                size="small"
                fullWidth
                label="Título eje Y"
                value={vizYAxisTitle}
                onChange={(e) => setVizYAxisTitle(e.target.value)}
                placeholder="Opcional"
              />
            </Box>
          )}
          {vizSupportsFillTimeAxisGaps && (vizChartType === 'line' || vizChartType === 'bar') && (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={vizFillTimeAxisGaps}
                  onChange={(e) => setVizFillTimeAxisGaps(e.target.checked)}
                />
              }
              label={
                <Typography
                  variant="body2"
                  title="Inserta en el eje X todas las horas o fechas entre el primer y último dato, para que los huecos sin registros ocupen su espacio temporal real."
                >
                  Incluir horas/fechas sin datos en el eje X
                </Typography>
              }
            />
          )}
          {vizChartType === 'bar' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25 }}>
              {hasBarSeries && (
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={vizStacked}
                      onChange={(e) => setVizStacked(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Barras apiladas</Typography>}
                />
              )}
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={vizHorizontal}
                    onChange={(e) => setVizHorizontal(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Orientación horizontal</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={vizBarShowValueLabels}
                    onChange={(e) => setVizBarShowValueLabels(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Etiquetas de valores</Typography>}
              />
            </Box>
          )}
          {vizChartType === 'line' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 0.25 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Interpolación</InputLabel>
                <Select
                  label="Interpolación"
                  value={vizLineInterpolation}
                  onChange={(e) => setVizLineInterpolation(e.target.value)}
                >
                  {LINE_INTERPOLATION_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={vizLineFillMissingAsZero}
                    onChange={(e) => setVizLineFillMissingAsZero(e.target.checked)}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    title="Rellena con 0 las horas/fechas sin dato (con «Incluir horas/fechas sin datos» activo) y los instantes donde una serie no tiene valor pero otras sí."
                  >
                    Rellenar huecos con 0
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={vizLineShowValueLabels}
                    onChange={(e) => setVizLineShowValueLabels(e.target.checked)}
                  />
                }
                label={<Typography variant="body2">Etiquetas de valores</Typography>}
              />
            </Box>
          )}
        </Box>
      </Paper>
    ) : null;

  const editingFilter = filterEditIndex != null ? filters[filterEditIndex] : null;
  const filterPopoverAnchorEl = isValidPopoverAnchor(filterAnchor)
    ? filterAnchor
    : isValidPopoverAnchor(filterBarRef.current)
      ? filterBarRef.current
      : null;
  const filterPopoverOpen =
    filterEditIndex != null && Boolean(editingFilter) && Boolean(filterPopoverAnchorEl);

  return (
    <Dialog
      open={open}
      onClose={closeBuilder}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { maxHeight: '94vh', height: '90vh' } }}
    >
      <DialogTitle sx={{ py: 1.25 }}>{editing ? 'Editar gráfico' : 'Editor de gráficos'}</DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0 }}>
          <ChartBuilderCollectionSidebar
            getAuthHeaders={getAuthHeaders}
            refreshKey={`${collectionRefreshKey}-${examplesRefreshKey}`}
            activeExampleId={loadedExampleId}
            activeReferenceInstanceId={referencePreview?.entry?.id ?? null}
            onSelect={handleLoadExample}
            onViewChartItem={handleViewChartItem}
            disabled={loading || Boolean(contextBlocked)}
            onDeleteError={(msg) => showError(msg)}
            onRestoreToGrid={onRestoreToGrid}
            onAddOriginalToDashboard={onAddOriginalToDashboard}
            onMakeInstanceIndependent={handleMakeInstanceIndependentInBuilder}
            currentDashboardId={currentDashboardId}
            currentDashboardName={currentDashboardName}
            chartLibrary={chartLibrary}
          />

          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: referencePreview ? 0 : 1.5, gap: 1 }}>
            {referencePreview ? (
              <ReferencePreviewPanel
                original={referencePreview.original}
                entry={referencePreview.entry}
                dashboardName={referencePreview.dashboardName}
                canMakeIndependent={
                  String(referencePreview.dashboardId) === String(currentDashboardId) &&
                  Boolean(onMakeInstanceIndependent) &&
                  isLibraryReference(referencePreview.entry)
                }
                makingIndependent={makingIndependent}
                canRestoreToGrid={
                  referencePreview.entry?.visibleOnGrid === false &&
                  String(referencePreview.dashboardId) === String(currentDashboardId)
                }
                onEditOriginal={handleEditOriginalFromReference}
                onMakeIndependent={handleMakeIndependentFromReference}
                onRestoreToGrid={() => onRestoreToGrid?.(referencePreview.entry?.id)}
                onClose={() => setReferencePreview(null)}
              />
            ) : (
              <>
            {loadedExampleId && (
              <Alert
                severity="success"
                sx={{ py: 0.25 }}
                onClose={() => {
                  setLoadedExampleId(null);
                  setLoadedExampleTitle('');
                  setLoadWarnings([]);
                }}
              >
                Cargado: <strong>{loadedExampleTitle}</strong>
                {loadWarnings.slice(0, 2).map((w) => (
                  <Typography key={w} variant="caption" display="block">
                    {w}
                  </Typography>
                ))}
              </Alert>
            )}

            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  minHeight: 280,
                }}
              >
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Cargando modelo de datos…
                </Typography>
              </Box>
            ) : contextBlocked ? (
              <Alert severity="warning">
                Activa el modelo y selecciona tablas en <strong>Configuración → Modelo de datos</strong>.
              </Alert>
            ) : (
              <>
                {previewLoading && (
                  <LinearProgress sx={{ flexShrink: 0, borderRadius: 1 }} aria-label="Cargando vista previa" />
                )}

                {/* Barra superior: título, tipo y filtros (ejes junto al preview) */}
                <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 1, mb: 0.15 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      label="Título"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      sx={{ minWidth: 180, flex: 1 }}
                    />
                    {!isStaticQuerylessType && (
                    <Tooltip
                      title="Vacío o 0 = sin tope de filas en el SQL. 1–5000 = límite explícito después de filtros y agrupación. Solo corta si hay más grupos/filas que ese máximo; no limita datos crudos antes de agrupar."
                      arrow
                      placement="top"
                    >
                      <TextField
                        size="small"
                        type="number"
                        label="Máx. filas"
                        value={rowLimit}
                        onChange={(e) => setRowLimit(e.target.value)}
                        inputProps={{ min: 0, max: 5000 }}
                        placeholder="Sin límite"
                        sx={{ width: 110 }}
                      />
                    </Tooltip>
                    )}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Visualizar</InputLabel>
                      <Select
                        label="Visualizar"
                        value={vizChartType}
                        onChange={(e) => {
                          const v = e.target.value;
                          setVizChartType(v);
                          if (v === 'tablePlus') setPreviewTab('chart');
                        }}
                      >
                        <MenuItem value="table">Tabla</MenuItem>
                        <MenuItem value="tablePlus">Tabla+</MenuItem>
                        <MenuItem value="bar">Barras</MenuItem>
                        <MenuItem value="line">Líneas</MenuItem>
                        <MenuItem value="pie">Torta</MenuItem>
                        <MenuItem value="radar">Radar (spider)</MenuItem>
                        <MenuItem value="intervals">Intervalos</MenuItem>
                        <MenuItem value="kpi">KPI</MenuItem>
                        <MenuItem value="narrative">Texto narrativo</MenuItem>
                        <MenuItem value="imageDensity">Heatmap imagen</MenuItem>
                        <MenuItem value="geoHeatmap">Mapa de calor (geo)</MenuItem>
                        <MenuItem value="imageCarousel">Carrusel de imágenes</MenuItem>
                        <MenuItem value="button">Botón (enlace externo)</MenuItem>
                        <MenuItem value="weather">Clima</MenuItem>
                        <MenuItem value="calendar">Calendario</MenuItem>
                      </Select>
                    </FormControl>
                    {!isStaticQuerylessType && (
                    <>
                    <Button size="small" onClick={() => setShowSql((s) => !s)}>
                      {showSql ? 'Ocultar SQL' : 'SQL'}
                    </Button>
                    <Tooltip title="Medir tiempo en estado (ej: minutos sin stock) con limpieza de ruido">
                      <Button
                        size="small"
                        color={episodeAnalysis?.enabled ? 'secondary' : 'primary'}
                        variant={episodeAnalysis?.enabled ? 'contained' : 'text'}
                        onClick={() => setEpisodeDialogOpen(true)}
                      >
                        Episodios
                      </Button>
                    </Tooltip>
                    <Tooltip title="Agrupar detecciones cercanas en zonas (ranking Top N por recurrencia)">
                      <span>
                        <Button
                          size="small"
                          color={spatialZoneAnalysis?.enabled ? 'secondary' : 'primary'}
                          variant={spatialZoneAnalysis?.enabled ? 'contained' : 'text'}
                          disabled={!hasUnnestColumn}
                          onClick={() => setSpatialZoneDialogOpen(true)}
                        >
                          Zonas
                        </Button>
                      </span>
                    </Tooltip>
                    <Button
                      size="small"
                      startIcon={<RefreshIcon fontSize="small" />}
                      onClick={refreshPreviewData}
                      disabled={!chartSpecReady || previewLoading || chartPreviewLoading}
                    >
                      Actualizar datos
                    </Button>
                    </>
                    )}
                  </Box>

                  {isStaticQuerylessType && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      {vizChartType === 'button' && (
                        <TextField
                          size="small"
                          fullWidth
                          label="URL"
                          placeholder="https://admin.vsaas.ai/..."
                          value={vizButtonUrl}
                          onChange={(e) => setVizButtonUrl(e.target.value)}
                          error={Boolean(vizButtonUrl) && !isSafeButtonUrl(vizButtonUrl)}
                          helperText={
                            Boolean(vizButtonUrl) && !isSafeButtonUrl(vizButtonUrl)
                              ? 'Debe ser una URL válida que empiece con http:// o https://'
                              : 'El botón abre esta URL en una pestaña nueva.'
                          }
                        />
                      )}
                      {vizChartType === 'weather' && (
                        <WeatherLocationPicker
                          label={title}
                          lat={vizWeatherLat}
                          lon={vizWeatherLon}
                          getAuthHeaders={getAuthHeaders}
                          onChange={({ label: nextLabel, lat, lon }) => {
                            if (nextLabel !== title) setTitle(nextLabel);
                            setVizWeatherLat(lat);
                            setVizWeatherLon(lon);
                          }}
                        />
                      )}
                      {vizChartType === 'calendar' && (
                        <CalendarEventsEditor
                          events={vizCalendarEvents}
                          onChange={setVizCalendarEvents}
                          getAuthHeaders={getAuthHeaders}
                        />
                      )}
                    </Paper>
                  )}

                  {!isStaticQuerylessType && (
                  <>
                  <Paper variant="outlined" sx={{ p: 1, overflow: 'visible' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Filtros activos
                        </Typography>
                        {filters.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            arrastra para ordenar ·{' '}
                            <Box component="span" sx={{ color: 'secondary.main' }}>●</Box> dato
                            {' · '}
                            <Box component="span" sx={{ color: 'info.main' }}>●</Box> modelo
                          </Typography>
                        )}
                        {filters.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            ninguno — menú del encabezado → Filtrar
                          </Typography>
                        )}
                      </Box>
                      {filters.length > 0 && (
                        <DragDropContext onDragEnd={handleFilterDragEnd}>
                          <Droppable droppableId="chart-builder-active-filters" direction="horizontal">
                            {(provided) => (
                              <Box
                                ref={(node) => {
                                  filterBarRef.current = node;
                                  provided.innerRef(node);
                                }}
                                {...provided.droppableProps}
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'nowrap',
                                  gap: 0.75,
                                  alignItems: 'center',
                                  overflowX: 'auto',
                                  overflowY: 'visible',
                                  pb: 0.25,
                                }}
                              >
                                {filters.map((f, idx) => {
                                  const chipProps = filterChipProps(f);
                                  const clientId = f.clientId || `filter-fallback-${idx}`;
                                  return (
                                    <Draggable key={clientId} draggableId={clientId} index={idx}>
                                      {(dragProvided, snapshot) => (
                                        <Box
                                          ref={dragProvided.innerRef}
                                          {...dragProvided.draggableProps}
                                          {...dragProvided.dragHandleProps}
                                          sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            flexShrink: 0,
                                            cursor: snapshot.isDragging ? 'grabbing' : 'grab',
                                            opacity: snapshot.isDragging ? 0.9 : 1,
                                            touchAction: 'none',
                                          }}
                                        >
                                          <DragIndicatorIcon
                                            sx={{ fontSize: 16, color: 'text.disabled', mr: 0.25, flexShrink: 0 }}
                                            aria-hidden
                                          />
                                          <Chip
                                            size="small"
                                            color={chipProps.color}
                                            variant={chipProps.variant}
                                            label={filterChipLabel(f, context?.timeRangeSets, context)}
                                            onClick={(e) => {
                                              if (snapshot.isDragging) return;
                                              setFilterEditIndex(idx);
                                              setFilterAnchor(e.currentTarget);
                                            }}
                                            onDelete={() => {
                                              setFilters((p) => p.filter((_, i) => i !== idx));
                                              setFilterEditIndex((prev) => {
                                                if (prev == null) return prev;
                                                if (prev === idx) return null;
                                                if (prev > idx) return prev - 1;
                                                return prev;
                                              });
                                            }}
                                            deleteIcon={
                                              <DeleteOutlineIcon
                                                onMouseDown={(e) => e.stopPropagation()}
                                              />
                                            }
                                          />
                                        </Box>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                              </Box>
                            )}
                          </Droppable>
                        </DragDropContext>
                      )}
                    </Box>
                  </Paper>

                  {(preview?.warnings || []).slice(0, 2).map((w) => (
                    <Alert key={w} severity="warning" sx={{ py: 0 }}>
                      {w}
                    </Alert>
                  ))}

                  <ChartFeedBar
                    postProcessOrder={postProcessOrder}
                    episodeAnalysis={episodeAnalysis}
                    spatialZoneAnalysis={spatialZoneAnalysis}
                    onPostProcessOrderChange={handlePostProcessOrderChange}
                  />

                  {episodesTableActive && pipelinePreviews.episodes?.error && (
                    <Alert severity="warning" sx={{ py: 0, mt: 0.5 }}>
                      Episodios: {pipelinePreviews.episodes.error}
                    </Alert>
                  )}
                  {zonesTableActive && pipelinePreviews.zones?.error && (
                    <Alert severity="warning" sx={{ py: 0, mt: 0.5 }}>
                      Zonas: {pipelinePreviews.zones.error}
                    </Alert>
                  )}

                  <Collapse in={showSql && Boolean(preview?.sql || chartPreview?.sql)}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1, pr: 8, position: 'relative', maxHeight: 120, overflow: 'auto', bgcolor: 'grey.50' }}
                    >
                      <Box sx={{ position: 'absolute', top: 4, right: 4, display: 'flex', gap: 0.5 }}>
                        <Tooltip title={sqlCopied ? 'Copiado' : 'Copiar SQL'}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              const sqlText =
                                (vizChartType === 'table' || vizChartType === 'tablePlus' ? preview?.sql : chartPreview?.sql) ||
                                preview?.sql ||
                                chartPreview?.sql ||
                                '';
                              handleCopySql(sqlText);
                            }}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Ver completo">
                          <IconButton size="small" onClick={() => setSqlFullscreen(true)}>
                            <OpenInFullIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Typography variant="caption" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                        {(vizChartType === 'table' || vizChartType === 'tablePlus' ? preview?.sql : chartPreview?.sql) ||
                          preview?.sql ||
                          chartPreview?.sql}
                      </Typography>
                    </Paper>
                  </Collapse>
                  </>
                  )}
                </Box>

                <Dialog open={sqlFullscreen} onClose={() => setSqlFullscreen(false)} maxWidth="md" fullWidth>
                  <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    SQL
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title={sqlCopied ? 'Copiado' : 'Copiar SQL'}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            const sqlText =
                              (vizChartType === 'table' || vizChartType === 'tablePlus' ? preview?.sql : chartPreview?.sql) ||
                              preview?.sql ||
                              chartPreview?.sql ||
                              '';
                            handleCopySql(sqlText);
                          }}
                        >
                          <ContentCopyIcon fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => setSqlFullscreen(false)}>
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  </DialogTitle>
                  <DialogContent dividers>
                    <Typography variant="body2" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap' }}>
                      {(vizChartType === 'table' || vizChartType === 'tablePlus' ? preview?.sql : chartPreview?.sql) ||
                        preview?.sql ||
                        chartPreview?.sql}
                    </Typography>
                  </DialogContent>
                </Dialog>

                {/* Tabla | Gráfico */}
                {!isStaticQuerylessType && (
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  {(vizChartType !== 'table' || postProcessChartActive) && (
                    <Tabs
                      value={
                        spatialZonesChartActive && previewTab === 'chart' && vizChartType === 'table'
                          ? 'zones'
                          : episodesChartActive && previewTab === 'chart' && vizChartType === 'table'
                            ? 'episodes'
                            : previewTab
                      }
                      onChange={(_, v) => setPreviewTab(v)}
                      sx={{
                        flexShrink: 0,
                        minHeight: 36,
                        borderBottom: 1,
                        borderColor: 'divider',
                        mb: 0.5,
                        '& .MuiTab-root': { minHeight: 36, py: 0.5, textTransform: 'none' },
                      }}
                    >
                      <Tab
                        label={postProcessChartActive ? 'Serie SQL (filtros)' : 'Tabla'}
                        value="table"
                      />
                      {pipelineTabSteps.map((step) =>
                        step === 'episodes' ? (
                          <Tab
                            key="episodes"
                            label={
                              chartFeed === 'episodes'
                                ? 'Episodios → Gráfico'
                                : `${episodeInputSource.shortLabel} → Episodios`
                            }
                            value="episodes"
                          />
                        ) : (
                          <Tab
                            key="zones"
                            label={
                              chartFeed === 'zones' ? 'Zonas → Gráfico' : 'Serie SQL → Zonas'
                            }
                            value="zones"
                          />
                        )
                      )}
                      {vizChartType !== 'table' && (
                        <Tab label="Gráfico" value="chart" />
                      )}
                    </Tabs>
                  )}

                  {vizChartType === 'table' &&
                    !postProcessChartActive &&
                    columns.some((c) => PIVOTABLE_DATE_BUCKETS.includes(c.dateBucket) && c.pivotHour === true) &&
                    customDateCompareBaseOptions.length > 0 &&
                    customDateCompareParamOptions.length > 0 && (
                      <Paper variant="outlined" sx={{ p: 1, mb: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary">
                          Comparación por segunda fecha
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>Fecha base</InputLabel>
                            <Select
                              label="Fecha base"
                              value={vizCustomDateCompareBaseStart}
                              onChange={(e) => setVizCustomDateCompareBaseStart(e.target.value)}
                            >
                              <MenuItem value="">Ninguna</MenuItem>
                              {customDateCompareBaseOptions.map((o) => (
                                <MenuItem key={o.key} value={o.paramNameStart}>
                                  {o.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 220 }} disabled={!vizCustomDateCompareBaseStart}>
                            <InputLabel>Comparar con fecha</InputLabel>
                            <Select
                              label="Comparar con fecha"
                              value={vizCustomDateCompareParam}
                              onChange={(e) => setVizCustomDateCompareParam(e.target.value)}
                            >
                              <MenuItem value="">Ninguna</MenuItem>
                              {customDateCompareParamOptions.map((f) => (
                                <MenuItem key={f.paramName} value={f.paramName}>
                                  {`@${f.paramName}`}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                        {vizCustomDateCompareBaseStart && vizCustomDateCompareParam && (
                          <Alert severity="info" sx={{ py: 0.25 }}>
                            Compara el período actual con el que empieza en «Comparar con fecha», del mismo largo
                            que el rango base. Con «Pivote en tabla», cada fila pasa a ser Actual o Comparación
                            (la columna de agrupación original no aparece en la tabla mientras esto está activo).
                          </Alert>
                        )}
                      </Paper>
                    )}
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {(previewTab === 'table' ||
                      (vizChartType === 'table' && !postProcessChartActive)) && (
                      <>
                      <LiveDatasetTable
                        columns={columns}
                        calculatedColumns={tableCalculatedColumns}
                        onChangeCalculatedColumns={setTableCalculatedColumns}
                        previewData={heatmapTableOverlay.data}
                        overlayColumns={heatmapTableOverlay.columns}
                        previewLoading={previewLoading}
                        context={context}
                        connectionId={connectionId}
                        database={database}
                        getAuthHeaders={getAuthHeaders}
                        orderColumnIndex={orderColumnIndex}
                        orderDirection={orderDirection}
                        onChangeColumns={handleColumnsChange}
                        onSort={handleSort}
                        onFilterColumn={(idx, el, opts) => handleFilterColumn(idx, el, opts)}
                        filters={filters}
                      />
                      </>
                    )}

                    {zonesTableActive && previewTab === 'zones' && (
                      <SpatialZoneResultsTable
                        data={pipelinePreviews.zones?.data ?? []}
                        loading={chartPreviewLoading && !pipelinePreviews.zones}
                        error={pipelinePreviews.zones?.error || chartPreviewError}
                        spatialZoneAnalysis={spatialZoneAnalysis}
                        groupColumns={spatialZoneGroupCols}
                        onChangeSpatialZoneAnalysis={patchSpatialZoneAnalysis}
                      />
                    )}

                    {episodesTableActive && previewTab === 'episodes' && (
                      <EpisodeResultsTable
                        data={pipelinePreviews.episodes?.data ?? []}
                        loading={chartPreviewLoading && !pipelinePreviews.episodes}
                        error={pipelinePreviews.episodes?.error || chartPreviewError}
                        episodeAnalysis={episodeAnalysis}
                        groupColumns={episodeGroupCols}
                        onChangeEpisodeAnalysis={patchEpisodeAnalysis}
                      />
                    )}

                    {vizChartType !== 'table' && previewTab === 'chart' && (
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          display: 'flex',
                          flexDirection: 'row',
                          gap: 1,
                          overflow: 'hidden',
                        }}
                      >
                        {axesSeriesPanel}
                        <Paper
                          variant="outlined"
                          sx={{
                            width: '80%',
                            flex: '1 1 80%',
                            minWidth: 0,
                            p: 1,
                            pt: 1.15,
                            minHeight: 280,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'auto',
                            position: 'relative',
                          }}
                        >
                        <Chip
                          size="small"
                          label={`Datos: ${chartFeed === 'base' ? 'Serie SQL' : chartFeed === 'episodes' ? 'Episodios' : 'Zonas'}`}
                          color={chartFeed === 'base' ? 'default' : 'primary'}
                          sx={{ alignSelf: 'flex-start', mb: 0.5, fontWeight: 600 }}
                        />
                        {chartPreviewLoading &&
                          !chartFeedTableData?.length &&
                          !(isRawRowsChartType(vizChartType) && chartFeed === 'base' && preview?.data?.length) &&
                          !(vizChartType === 'tablePlus' && chartFeed === 'base' && preview?.data?.length) && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                          </Box>
                        )}
                        {chartPreviewLoading && (
                          <LinearProgress
                            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}
                          />
                        )}
                        {chartPreviewError && (
                          <Alert severity="warning" sx={{ mb: 1, py: 0.25 }}>
                            {chartPreviewError}
                          </Alert>
                        )}
                        {vizChartType === 'tablePlus' && (
                          <Box sx={{ flex: 1, minHeight: 240 }}>
                            {chartFeedTableData?.length ? (
                              <TablePlusView
                                data={chartFeedTableData}
                                specColumns={chartFeed === 'base' ? columns : undefined}
                                visibleColumns={tablePlusVisibleColumns}
                                groupColumns={tablePlusGroupColumns}
                                applyTimezone={tablePlusApplyTimezone}
                                timeZone={timezone}
                                columnLabels={tablePlusColumnLabels}
                                columnDateFormats={feedTemporalMaps.columnDateFormats}
                                columnDateBuckets={feedTemporalMaps.columnDateBuckets}
                                columnDurationFormats={chartFeedDurationFormats}
                                calculatedColumns={tableCalculatedColumns}
                              />
                            ) : (
                              !chartPreviewLoading && (
                                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                  {chartFeed === 'base'
                                    ? 'Sin filas en la tabla fuente.'
                                    : `Sin filas en ${chartFeedTableLabel.toLowerCase()}. Revisa la configuración o recarga la vista previa.`}
                                </Typography>
                              )
                            )}
                          </Box>
                        )}
                        {previewChartConfig && previewChartConfig.type === 'narrative' && chartTypeIsQueryless && (
                          // Narrativo de texto fijo: nunca corre ninguna consulta (no llena
                          // chartWidgetPreview), así que se muestra el texto directo acá en vez
                          // de montar <ChartWidget> (que corre sus useMemo de datos/opciones de
                          // Chart.js en cada render aunque el resultado no se use para este
                          // tipo) — evita que tipear en el textarea de al lado se sienta trabado.
                          <Box sx={{ flex: 1, minHeight: 240, p: 1.5, overflow: 'auto' }}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {narrativePreviewSegments.map((seg, i) =>
                                seg.type === 'value' ? (
                                  <Box key={i} component="strong" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                    {seg.value}
                                  </Box>
                                ) : (
                                  <React.Fragment key={i}>{seg.value}</React.Fragment>
                                )
                              )}
                            </Typography>
                          </Box>
                        )}
                        {previewChartConfig &&
                          previewChartConfig.type !== 'table' &&
                          !(previewChartConfig.type === 'narrative' && chartTypeIsQueryless) &&
                          chartWidgetPreview &&
                          !chartPreviewLoading &&
                          // Heatmap imagen/geo sobre la serie base usa `preview` (tabla), no
                          // runChartPreview: el data key de chartPreview nunca se setea.
                          (!needsChartSqlPreview || chartPreviewDataKeyRef.current === previewDataKey) && (
                          <Box sx={{ flex: 1, minHeight: 240 }}>
                            <ChartWidget
                              key={
                                previewChartConfig.type === 'kpi'
                                  ? `kpi-font-${vizKpiValueFontSize}`
                                  : previewChartConfig.type
                              }
                              type={previewChartConfig.type}
                              data={chartWidgetData}
                              xColumn={previewChartConfig.xColumn}
                              yColumn={previewChartConfig.yColumn}
                              groupByColumn={previewChartConfig.groupByColumn}
                              labelColumn={previewChartConfig.labelColumn}
                              deltaColumn={previewChartConfig.deltaColumn}
                              percentColumn={previewChartConfig.percentColumn}
                              occupancyColumn={previewChartConfig.occupancyColumn}
                              cumulativeColumn={previewChartConfig.cumulativeColumn}
                              hourCycleStart={previewChartConfig.hourCycleStart}
                              xDateBucket={previewChartConfig.xDateBucket}
                              xDateDisplayFormat={previewChartConfig.xDateDisplayFormat}
                              labelDateBucket={previewChartConfig.labelDateBucket}
                              labelDateDisplayFormat={previewChartConfig.labelDateDisplayFormat}
                              stacked={previewChartConfig.stacked}
                              horizontal={previewChartConfig.horizontal}
                              filterValues={chartWidgetPreviewFilterValues}
                              advancedConfig={previewChartConfig.advancedConfig}
                              kpiValueFontSize={
                                previewChartConfig.type === 'kpi' ? vizKpiValueFontSize : undefined
                              }
                              label={title || previewChartConfig.kpiLabel}
                              getAuthHeaders={getAuthHeaders}
                              imageDensityConfig={previewChartConfig.imageDensityConfig}
                              geoHeatmapConfig={previewChartConfig.geoHeatmapConfig}
                              intervalConfig={previewChartConfig.intervalConfig}
                              imageCarouselConfig={previewChartConfig.imageCarouselConfig}
                              columnDurationFormats={chartFeedDurationFormats}
                              imageDensityAllowPositionEdit
                              onImageDensityConfigChange={handleImageDensityConfigChange}
                              onHeatmapPositionDraftChange={setHeatmapPositionDraft}
                              unsavedSessionId="chart-builder-preview"
                            />
                          </Box>
                        )}
                        {!chartPreviewLoading &&
                          !chartPreviewError &&
                          !previewChartConfig &&
                          !isRawRowsChartType(vizChartType) && (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                              Configura al menos una Fila (eje) y un Valor (medida) para previsualizar el gráfico.
                            </Typography>
                          )}
                        </Paper>
                      </Box>
                    )}
                  </Box>
                </Box>
                )}
              </>
            )}
              </>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
        <Button onClick={closeBuilder}>Cancelar</Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          onClick={openSaveExampleDialog}
          disabled={!chartSpecReady || contextBlocked || Boolean(referencePreview) || chartTypeIsQueryless}
        >
          Duplicar
        </Button>
        <Button
          variant="contained"
          onClick={handlePublish}
          disabled={publishing || !chartSpecReady || contextBlocked || Boolean(referencePreview)}
        >
          {publishing
            ? 'Publicando…'
            : editing
              ? 'Guardar cambios'
              : vizChartType === 'table'
                ? 'Publicar tabla'
                : vizChartType === 'tablePlus'
                  ? 'Publicar Tabla+'
                  : 'Publicar gráfico'}
        </Button>
      </DialogActions>

      <FilterEditorPopover
        open={filterPopoverOpen}
        anchorEl={filterPopoverAnchorEl}
        onClose={() => {
          setFilterEditIndex(null);
          setFilterAnchor(null);
          flushPreview();
        }}
        filter={editingFilter}
        context={context}
        connectionId={connectionId}
        database={database}
        getAuthHeaders={getAuthHeaders}
        onChange={(next) => {
          if (filterEditIndex == null) return;
          setFilters((p) =>
            p.map((f, i) =>
              i === filterEditIndex ? { ...next, clientId: f.clientId || next.clientId } : f
            )
          );
        }}
        onRemove={() => {
          if (filterEditIndex == null) return;
          setFilters((p) => p.filter((_, i) => i !== filterEditIndex));
          setFilterEditIndex(null);
          setFilterAnchor(null);
          flushPreview();
        }}
      />

      <Dialog open={saveExampleOpen} onClose={() => !savingExample && setSaveExampleOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Duplicar gráfico</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crea una copia en la colección. El gráfico del dashboard queda igual. Después, Publicar agrega esa copia como otro gráfico en este dashboard.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Título"
            value={saveExampleTitle}
            onChange={(e) => setSaveExampleTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            label="Nota (opcional)"
            value={saveExampleNote}
            onChange={(e) => setSaveExampleNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveExampleOpen(false)} disabled={savingExample}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={handleSaveExample} disabled={savingExample}>
            {savingExample ? 'Duplicando…' : 'Duplicar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: propagar cambios a otros dashboards */}
      <Dialog open={propagateDialogOpen} onClose={() => !publishing && setPropagateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gráfico usado en varios dashboards</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Este gráfico también aparece en otros dashboards. ¿Cómo quieres aplicar los cambios?
          </Typography>
          {propagateUsage.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {[...new Set(propagateUsage.map((u) => u.dashboardName).filter(Boolean))].map((name) => (
                <Chip key={name} size="small" label={name} variant="outlined" />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setPropagateDialogOpen(false)} disabled={publishing}>
            Cancelar
          </Button>
          <Button
            variant="outlined"
            disabled={publishing}
            onClick={() => executePublish({ propagateToAll: false, forkOriginal: true })}
          >
            Duplicar solo aquí
          </Button>
          <Button
            variant="contained"
            disabled={publishing}
            onClick={() => executePublish({ propagateToAll: true, forkOriginal: false })}
          >
            {publishing ? 'Guardando…' : 'Propagar a todos'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: análisis de episodios (tiempo en estado) */}
      <EpisodeAnalysisDialog
        open={episodeDialogOpen}
        onClose={() => setEpisodeDialogOpen(false)}
        value={episodeAnalysis || DEFAULT_EPISODE_CONFIG}
        onChange={(next) => {
          setEpisodeAnalysis(next);
          if (next?.enabled) {
            const feed = resolveChartFeedFromPipeline(
              postProcessOrder,
              true,
              Boolean(spatialZoneAnalysis?.enabled)
            );
            if (feed === 'episodes') setPreviewTab('episodes');
          } else {
            setPreviewTab((tab) => (tab === 'episodes' ? 'table' : tab));
          }
        }}
        seriesReady={episodeSeriesReady}
        groupColumns={episodeGroupCols}
        columns={columns}
        context={context}
        episodeInputSource={episodeInputSource}
        episodeInputFields={episodeInputFields}
        fetchSeries={fetchEpisodeSeries}
      />

      <SpatialZoneAnalysisDialog
        open={spatialZoneDialogOpen}
        onClose={() => setSpatialZoneDialogOpen(false)}
        value={spatialZoneAnalysis || DEFAULT_SPATIAL_ZONE_CONFIG}
        onChange={handleSpatialZoneAnalysisChange}
        columns={columns}
        context={context}
      />
    </Dialog>
  );
};

export default ChartBuilderDialog;
